"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from "react"
import { toast } from "sonner"
import * as Y from "yjs"
import type { Awareness } from "y-protocols/awareness"
import { CompilerApiClient } from "@/lib/compiler/api-client"
import { getCompilerRuntimeConfig } from "@/lib/compiler/config"
import { redactWsUrl, toSameOriginCompilerWsUrl } from "@/lib/compiler/same-origin-ws"
import { toCompilerError } from "@/lib/compiler/errors"
import { basename, normalizePath, parentPath, isAssetByExtension, isImageFile } from "@/lib/compiler/path-utils"
import {
  buildProjectTree,
  buildIdMapFromTree,
  extractFilePathsFromTree,
  extractFolderPathsFromTree,
  type ProjectNode,
} from "@/lib/project-tree"
import { buildAssetUrlCandidates, resolvePdfUrl } from "@/lib/compiler/url-resolvers"
import { CompilerWsClient } from "@/lib/compiler/ws-client"
import {
  compilePdfTsLog,
  extractBackendTimeFields,
  parsePdfCacheBust,
  resolvePdfCacheBustMs,
} from "@/lib/compiler/compile-pdf-ts-log"
import { CrdtWsSyncManager } from "@/lib/crdt/crdt-ws-sync-manager"
import { clearClientCrdtHistory } from "@/lib/crdt/client-history-buffer"
import { fetchReconstructSeedFiles } from "@/lib/crdt/crdt-history"
import { crdtHistoryBffUrl, rewriteSessionApiToBff } from "@/lib/crdt/session-url"
import { resolveAuthenticatedCrdtWsUrl } from "@/lib/crdt-auth"
import {
  buildAwarenessUserFallback,
  clearAwarenessUserProfileCache,
  fetchAwarenessUserFromProfile,
} from "@/lib/crdt/awareness-user"
import { bffFetch } from "@/lib/api-client"
import { api } from "@/lib/api"
import { hasAuthSession, readAuthUserLabel } from "@/lib/auth-client"
import {
  consumeAuthCompilerSession,
  hasAuthCompilerSessionPending,
} from "@/lib/auth-compiler-session"
import {
  createAuthCompilerSessionForProject,
  fetchActiveAuthCompilerSession,
  probeCompilerSession,
  loadAuthProjectSessionId,
  saveAuthProjectSessionId,
  clearAuthProjectSessionId,
  getCollaborationSessionIdFromUrl,
  syncCollaborationSessionIdToUrl,
  tryClaimAutoCompileLeader,
} from "@/lib/auth-project-session"
import { ApiRequestError } from "@/lib/api-client"
import {
  SESSION_MAX_ATTEMPTS,
  SessionInitError,
  acquireSessionInitLock,
  classifyHttpStatus,
  releaseSessionInitLock,
  runBoundedSessionAttempts,
} from "@/lib/compiler/session-lifecycle"
import { resolveAuthenticatedWsUrl } from "@/lib/websocket"
import {
  CRDT_RUNTIME_DEBUG,
  clearYTextDebugRegistry,
  createCrdtDebugMetrics,
  createInstrumentedWebSocketClass,
  logAwarenessUpdate,
  logDocUpdate,
  logFilesMapStructure,
  logReactFlushFromYjs,
  logReactYFileMutation,
  logTransact,
  logYTextDelta,
  logYwsSync,
  primeYTextDebugRegistry,
} from "@/lib/crdt-runtime-debug"
import type {
  CompilerTemplateFile,
  CrdtInfo,
  EditorEvent,
  EditorFileRecord,
  IncomingMessage,
  LocalCrdtOrigin,
  LogLevel,
  OutgoingEnvelope,
  RequestPhase,
  SeedCrdtOrigin,
  CreateSessionResponse, CrdtFilesResponse,
} from "@/lib/compiler/types"
import {
  createAndSaveSessionAction,
  setAutoCompileAction,
  clearSessionAction,
} from "@/app/actions/session"
import { postTabSyncMessage } from "@/lib/detached-tab-sync"
import type { editor as MonacoEditor } from "monaco-editor"

const EDIT_DEBOUNCE_MS = 300
/** Idle delay before auto-compile runs after the last local edit (when auto-compile is on). */
const AUTO_COMPILE_DEBOUNCE_MS = 3000
/**
 * After remote CRDT goes idle, wait this long before soft-fetching PDF.
 * Must exceed peer auto-compile debounce + typical compile time, otherwise we
 * fetch mid-compile (stale PDF) and may block a later correct ?t=.
 */
const PEER_PDF_SOFT_REFRESH_MS = AUTO_COMPILE_DEBOUNCE_MS + 5000
/** If compiler WS has not received any message for this long, treat it as half-open and reconnect before compile. */
const COMPILER_WS_RECV_STALE_MS = 15_000
/** After local compile_trigger, reconnect if no compile_* / pdf_ready arrives. */
const LOCAL_COMPILE_ACK_TIMEOUT_MS = 8_000
/** Soft-fetch PDF if local compile is still pending (peer often already got pdf_ready). */
const LOCAL_COMPILE_SOFT_RECOVERY_MS = 12_000
const STORE_KEY = "rudrise-compiler-state-v1"
const SESSION_STORAGE_KEY = "rudrise-session-id-v1"
const CRDT_DEBUG = process.env.NODE_ENV === "development"

function saveSessionIdToStorage(sessionId: string): void {
  if (typeof window === "undefined") return
  try { window.localStorage.setItem(SESSION_STORAGE_KEY, sessionId) } catch {}
}

function loadSessionIdFromStorage(): string | null {
  if (typeof window === "undefined") return null
  try { return window.localStorage.getItem(SESSION_STORAGE_KEY) } catch { return null }
}

function clearSessionIdFromStorage(): void {
  if (typeof window === "undefined") return
  try { window.localStorage.removeItem(SESSION_STORAGE_KEY) } catch {}
}

function crdtLog(level: "log" | "warn" | "error", ...args: unknown[]) {
  if (!CRDT_DEBUG) return
  console[level]("[CRDT]", ...args)
}

/** True when a Yjs update originated from this tab's editor (not CRDT remote/seed). */
function isLocalDocumentEdit(origin: unknown): boolean {
  if (!origin || typeof origin !== "object") return false
  const kind = (origin as { kind?: unknown }).kind
  if (kind === "local") return true
  if (kind === "remote" || kind === "seed") return false
  const ctorName = (origin as { constructor?: { name?: string } }).constructor?.name
  return ctorName === "MonacoBinding"
}

function isRemoteDocumentEdit(origin: unknown): boolean {
  if (!origin || typeof origin !== "object") return false
  return (origin as { kind?: unknown }).kind === "remote"
}

/** file_id → name + parent. Path lookups stay on folderIdMapRef (path → file_id). */
type FsNodeMeta = {
  name: string
  parent: string | null
  kind: "file" | "folder"
}

type FsStructureEvent =
  | { type: "FILE_CREATED"; file_id: string; name: string; parent_id: string | null; path?: string; isAsset?: boolean }
  | { type: "FOLDER_CREATED"; file_id: string; name: string; parent_id: string | null; path?: string }
  | { type: "FILE_RENAMED"; file_id: string; name: string }
  | { type: "FILE_MOVED"; file_id: string; parent_id: string | null }
  | { type: "FILE_DELETED"; file_id: string; path?: string }
  | { type: "PATH_RELOCATED"; source: string; target: string; file_id?: string }

function readStructureString(data: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = data[key]
    if (typeof value === "string" && value.trim()) return value.trim()
  }
  return ""
}

function readParentId(data: Record<string, unknown>): string | null | undefined {
  if (!("parent_id" in data) && !("parent" in data)) return undefined
  const value = "parent_id" in data ? data.parent_id : data.parent
  if (typeof value === "string" && value.trim()) return value.trim()
  return null
}

function structureFileLabel(name: string, extension: string): string {
  const ext = extension.startsWith(".") ? extension.slice(1) : extension
  if (!name) return ""
  if (!ext || name.endsWith(`.${ext}`)) return name
  return `${name}.${ext}`
}

function structureEventFromMessage(type: string, data: unknown): FsStructureEvent | null {
  const record = data && typeof data === "object" ? (data as Record<string, unknown>) : {}
  const normalizedType = type.trim().toLowerCase()
  const fileID = readStructureString(record, ["file_id", "id", "folder_id"])
  const extension = readStructureString(record, ["extension"])
  const name = structureFileLabel(readStructureString(record, ["name"]), extension)
  const path = normalizePath(readStructureString(record, ["path"]))
  const to = normalizePath(readStructureString(record, ["to", "new_path", "target"]))
  const parentId = readParentId(record)

  if (normalizedType === "file_created" || normalizedType === "folder_created") {
    const label = name || (path ? basename(path) : "")
    if (!fileID || !label) return null
    if (normalizedType === "folder_created") {
      return { type: "FOLDER_CREATED", file_id: fileID, name: label, parent_id: parentId ?? null, path: path || undefined }
    }
    return {
      type: "FILE_CREATED",
      file_id: fileID,
      name: label,
      parent_id: parentId ?? null,
      path: path || undefined,
      isAsset: record.is_asset === true || isAssetByExtension(path || label),
    }
  }

  if (normalizedType === "file_renamed" || normalizedType === "file_rename") {
    if (path && to) return { type: "PATH_RELOCATED", source: path, target: to, file_id: fileID || undefined }
    if (fileID && name) return { type: "FILE_RENAMED", file_id: fileID, name }
    return null
  }

  if (normalizedType === "file_moved") {
    if (path && to) return { type: "PATH_RELOCATED", source: path, target: to, file_id: fileID || undefined }
    if (!fileID || parentId === undefined) return null
    return { type: "FILE_MOVED", file_id: fileID, parent_id: parentId }
  }

  if (normalizedType === "file_deleted") {
    if (!fileID && !path) return null
    return { type: "FILE_DELETED", file_id: fileID, path: path || undefined }
  }

  return null
}

function structureEventFromOp(entry: Record<string, unknown>): FsStructureEvent | null {
  const type = typeof entry.type === "string" ? entry.type : ""
  const fileID = typeof entry.file_id === "string" ? entry.file_id.trim() : ""
  const path = normalizePath(typeof entry.path === "string" ? entry.path : "")
  const to = normalizePath(typeof entry.to === "string" ? entry.to : "")
  if ((type === "create" || type === "asset") && path) {
    return {
      type: "FILE_CREATED",
      file_id: fileID,
      name: basename(path),
      parent_id: null,
      path,
      isAsset: type === "asset" || isAssetByExtension(path),
    }
  }
  if (type === "rename" && path && to) {
    return { type: "PATH_RELOCATED", source: path, target: to, file_id: fileID || undefined }
  }
  if (type === "delete" && (path || fileID)) {
    return { type: "FILE_DELETED", file_id: fileID, path: path || undefined }
  }
  return null
}

function indexProjectTree(root: ProjectNode, parentId: string | null, into: Map<string, FsNodeMeta>) {
  for (const child of root.children ?? []) {
    if (!child.id || child.id === "project-root") {
      if (child.type === "folder") indexProjectTree(child, parentId, into)
      continue
    }
    into.set(child.id, {
      name: child.name,
      parent: parentId,
      kind: child.type === "folder" ? "folder" : "file",
    })
    if (child.type === "folder") indexProjectTree(child, child.id, into)
  }
}


function stableColorFromSeed(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  const hue = h % 360
  return `hsl(${hue} 65% 48%)`
}

const LEGACY_FILE_ID_MAP: Record<string, string> = {
  "1": "sample_file1.tex",
  "2": "sample_file2.tex",
  "3": "sample_file3.tex",
  "4": "sample_file4.tex",
}

const INITIAL_FILES: Record<string, EditorFileRecord> = {}

type WsStatus = "disconnected" | "connecting" | "connected" | "reconnecting"
type CompileStatus = "idle" | "compiling" | "error"
type OpenFileHandler = (fileId: string, fileName: string) => void

export type PathEvent =
  | { type: "rename"; source: string; target: string }
  | { type: "delete"; path: string }

export type PathEventHandler = (event: PathEvent) => void

/** PDF user-space rectangle from synctex forward (page is 1-based). */
export type SynctexForwardBox = {
  page: number
  x: number
  y: number
  width: number
  height: number
  // Source location that produced this box — passed through to the Flutter iframe
  // so it can identify the exact occurrence to highlight.
  sourceLine?: number
  sourceColumn?: number
  occurrenceIndex?: number
}

interface CompilerEditorState {
  configReady: boolean
  configError?: string
  templatePhase: RequestPhase
  sessionPhase: RequestPhase
  /** True after automatic session retries are exhausted; drives the reconnect dialog. */
  sessionDisconnected: boolean
  templateId: string
  templateName?: string
  templateDescription?: string
  mainFile: string
  sessionId?: string
  wsUrl?: string
  /** BFF URL for CRDT history replay (set when session includes crdt info). */
  crdtHistoryUrl?: string
  /** Files used to seed Y.Doc at session connect (baseline for history replay). */
  crdtReplaySeedFiles?: { path: string; content: string }[]
  wsStatus: WsStatus
  reconnectAttempt: number
  autoCompile: boolean
  compileStatus: CompileStatus
  compileError?: string
  compileErrorDetails?: string
  pdfUrl?: string
  files: Record<string, EditorFileRecord>
  virtualFolders: string[]
  events: EditorEvent[]
  /** Bumped when CRDT websocket completes initial sync (incl. reconnect); drives MonacoBinding re-attach. */
  crdtProviderEpoch: number
  /** Bumped when the Y.Map("files") key set changes; new Y.Text entries need a fresh binding. */
  crdtMapRevision: number
  /** Currently open/active file path in the editor. */
  activeFile?: string
  /** File path that triggered the last successful compilation (PDF is for this file). */
  compiledFile?: string
  /** Per-file PDF URLs: compiledFile → BFF pdf url. Preserved across compiles so each PDF tab keeps its own URL. */
  pdfByFile: Record<string, string>
  /**
   * Cached page-thumbnail data URLs, keyed by pdfUrl (not compiledFile — the
   * Flutter PDF viewer only knows the URL it was given via SET_PDF_URL, not
   * our internal file path). Pushed proactively by the Flutter iframe via a
   * PDF_THUMBNAIL postMessage whenever its own preview thumbnail changes, so
   * a PDF tab's drag-ghost can show it instantly (no async render possible
   * at dragstart) — see buildPdfDragCard in dockable-layout.tsx.
   */
  pdfThumbnailByUrl: Record<string, string>
  /** Auth projects: bumped after /api/files/tree + preview hydration (gates initial auto-compile). */
  authProjectHydrationEpoch: number
  /** Project filesystem version for optimistic concurrency. */
  filesystemVersion?: number
}

interface EditorStoreContextValue {
  state: CompilerEditorState
  projectName: string
  projectId: string
  getContent: (fileId: string) => string
  registerFile: (fileId: string, name: string, content: string) => void
  updateContent: (fileId: string, content: string) => void
  openFile: (fileId: string, fileName: string) => void
  openFileAtLine: (fileId: string, fileName: string, line: number) => void
  registerOpenFileHandler: (handler: OpenFileHandler) => void
  registerRevealLineHandler: (handler: (line: number) => void) => void
  registerPathEventHandler: (handler: PathEventHandler | null) => void
  registerSynctexPdfHandler: (
      handler: ((box: SynctexForwardBox) => void) | null
  ) => (() => void) | void
  applySynctexForwardResult: (box: SynctexForwardBox) => void
  registerFramePostHandler: (
      handler: ((msg: object) => void) | null
  ) => (() => void) | void
  postToFlutterFrame: (msg: object) => void
  registerInverseSyncHandler: (handler: ((line: number, column: number, word?: string) => void) | null) => void
  applyInverseSyncResult: (line: number, column: number, word?: string) => void
  setPdfThumbnail: (pdfUrl: string, dataUrl: string) => void
  /** Register the active Monaco code editor instance for ribbon / global commands. */
  registerActiveMonacoEditor: (ed: MonacoEditor.IStandaloneCodeEditor | null) => void
  getActiveMonacoEditor: () => MonacoEditor.IStandaloneCodeEditor | null
  runOnActiveEditor: <T>(fn: (ed: MonacoEditor.IStandaloneCodeEditor) => T) => T | undefined
  /**
   * Register undo/redo handlers for the active editor (e.g. Y.UndoManager while
   * MonacoBinding is attached). Pass null to clear.
   */
  registerEditorHistoryHandlers: (
    handlers: { undo: () => void; redo: () => void; canUndo?: () => boolean; canRedo?: () => boolean } | null,
  ) => void
  /** Undo in the active editor (Yjs history when bound, else Monaco). */
  undoActiveEditor: () => void
  /** Redo in the active editor (Yjs history when bound, else Monaco). */
  redoActiveEditor: () => void
  loadTemplate: () => Promise<void>
  createSession: () => Promise<void>
  /** Reset retry count and re-run session initialization (single-flight). */
  retryCompilerSession: () => void
  manualCompile: () => void
  setAutoCompile: (enabled: boolean) => void
  createFolder: (folderPath: string) => boolean
  createFileAtPath: (
    path: string,
    content: string,
    isAsset: boolean,
    onInserted?: () => void,
  ) => boolean
  deletePath: (path: string) => void
  renamePath: (sourcePath: string, targetPath: string) => boolean
  /** Clipboard cut → paste. Uses the same versioned move as drag/move when the id is in this tree. */
  moveClipboardFiles: (fileIds: string[], targetFolderPath: string | null) => Promise<boolean>
  uploadAsset: (file: File, folderPath?: string) => Promise<void>
  resolveAssetCandidates: (assetPath: string) => string[]
  /** Backend id for a folder already known to the tree, by its store path — undefined for the
   *  project root or an unknown path. */
  getFolderId: (path: string) => string | undefined
  /** Re-fetch the file tree from the backend (for changes made through some other API, e.g. a
   *  completed upload-session, rather than this module's own create/rename/delete/move calls).
   *  Files newly revealed by the refresh are registered with the compiler session. */
  refreshFileTree: () => Promise<void>
  getYjsBinding: () => {
    doc: Y.Doc
    awareness: Awareness
    filesMap: Y.Map<Y.Text>
    getFileId: (path: string) => string
    getYText: (path: string) => Y.Text | null
  } | null
  /** Update the active file without triggering tab management (use when switching tabs). */
  setActiveFile: (fileIdOrPath: string) => void
}

type Action =
  | { type: "CONFIG_READY" }
  | { type: "CONFIG_ERROR"; message: string }
  | { type: "LOG"; event: EditorEvent }
  | { type: "TEMPLATE_LOADING" }
  | {
      type: "TEMPLATE_SUCCESS"
      templateId: string
      templateName: string
      templateDescription?: string
      mainFile: string
      files: Record<string, EditorFileRecord>
      virtualFolders: string[]
    }
  | { type: "TEMPLATE_ERROR" }
  | { type: "SESSION_LOADING" }
  | { type: "SESSION_SUCCESS"; sessionId: string; wsUrl: string; pdfUrl?: string; crdtHistoryUrl?: string }
  | { type: "SESSION_ERROR"; disconnected?: boolean }
  | { type: "WS_STATUS"; status: WsStatus; attempt?: number }
  | { type: "AUTO_COMPILE"; enabled: boolean }
  | { type: "COMPILE_START" }
  | { type: "COMPILE_SUCCESS"; pdfUrl?: string; compiledFile?: string }
  | { type: "COMPILE_ERROR"; message: string; details?: string }
  | { type: "SET_ACTIVE_FILE"; path: string }
  | { type: "SET_PDF_THUMBNAIL"; pdfUrl: string; dataUrl: string }
  | { type: "UPSERT_FILE"; file: EditorFileRecord }
  | { type: "DELETE_PATH"; path: string }
  | { type: "ADD_FOLDER"; path: string }
  | { type: "RENAME_PATH"; source: string; target: string }
  | { type: "CRDT_PROVIDER_SYNCED" }
  | { type: "CRDT_MAP_STRUCTURE" }
  | { type: "CRDT_REPLAY_SEED"; seedFiles: { path: string; content: string }[] }
  | { type: "CLEAR_FILES" }
  | { type: "AUTH_PROJECT_HYDRATED" }
  | { type: "SET_FILESYSTEM_VERSION"; version?: number }

function nowIso(): string {
  return new Date().toISOString()
}

function toEvent(level: LogLevel, message: string, details?: string): EditorEvent {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    level,
    message,
    details,
    at: nowIso(),
  }
}

function resolvePathFromFileId(fileId: string): string {
  const normalized = normalizePath(fileId)
  if (normalized) return normalized
  return normalizePath(LEGACY_FILE_ID_MAP[fileId] || fileId)
}

function isPlaceholderFileContent(path: string, content: string): boolean {
  if (!content) return true
  const name = basename(path)
  return content === `% ${name}\n`
}

function newFilesystemOperationId(): string {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `op-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function listVirtualFoldersFromFiles(files: Record<string, EditorFileRecord>): string[] {
  const folders = new Set<string>()
  Object.keys(files).forEach((path) => {
    const parts = normalizePath(path).split("/").filter(Boolean)
    for (let i = 1; i < parts.length; i += 1) {
      folders.add(parts.slice(0, i).join("/"))
    }
  })
  return Array.from(folders).sort((a, b) => a.localeCompare(b))
}

function dedupeFolders(paths: string[]): string[] {
  return Array.from(new Set(paths.map((p) => normalizePath(p)).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b),
  )
}

function reducer(state: CompilerEditorState, action: Action): CompilerEditorState {
  switch (action.type) {
    case "CONFIG_READY":
      return { ...state, configReady: true }
    case "CONFIG_ERROR":
      return { ...state, configReady: false, configError: action.message }
    case "LOG":
      return { ...state, events: [action.event, ...state.events].slice(0, 120) }
    case "TEMPLATE_LOADING":
      return { ...state, templatePhase: "loading" }
    case "TEMPLATE_SUCCESS": {
      const alreadyHasSession = Boolean(state.sessionId || state.sessionPhase === "loading" || state.sessionPhase === "success")
      if (alreadyHasSession) {
        return {
          ...state,
          templatePhase: "success",
          templateId: action.templateId,
          templateName: action.templateName,
          templateDescription: action.templateDescription,
          mainFile: action.mainFile || state.mainFile,
        }
      }
      return {
        ...state,
        templatePhase: "success",
        templateId: action.templateId,
        templateName: action.templateName,
        templateDescription: action.templateDescription,
        mainFile: action.mainFile,
        files: action.files,
        virtualFolders: action.virtualFolders,
      }
    }
    case "TEMPLATE_ERROR":
      return { ...state, templatePhase: "error" }
    case "SESSION_LOADING": {
      if (state.sessionId) clearClientCrdtHistory(state.sessionId)
      return {
        ...state,
        sessionPhase: "loading",
        sessionDisconnected: false,
        crdtProviderEpoch: 0,
        crdtMapRevision: 0,
        crdtHistoryUrl: undefined,
        crdtReplaySeedFiles: undefined,
      }
    }
    case "SESSION_SUCCESS":
      return {
        ...state,
        sessionPhase: "success",
        sessionDisconnected: false,
        sessionId: action.sessionId,
        wsUrl: action.wsUrl,
        pdfUrl: action.pdfUrl,
        crdtHistoryUrl: action.crdtHistoryUrl,
        authProjectHydrationEpoch: 0,
      }
    case "SESSION_ERROR":
      return {
        ...state,
        sessionPhase: "error",
        sessionDisconnected: Boolean(action.disconnected),
      }
    case "WS_STATUS":
      return { ...state, wsStatus: action.status, reconnectAttempt: action.attempt ?? state.reconnectAttempt }
    case "AUTO_COMPILE":
      return { ...state, autoCompile: action.enabled }
    case "COMPILE_START":
      return { ...state, compileStatus: "compiling", compileError: undefined, compileErrorDetails: undefined }
    case "COMPILE_SUCCESS": {
      const resolvedPdfUrl = action.pdfUrl ?? state.pdfUrl
      const resolvedCompiledFile = action.compiledFile ?? state.compiledFile
      return {
        ...state,
        compileStatus: "idle",
        compileError: undefined,
        compileErrorDetails: undefined,
        pdfUrl: resolvedPdfUrl,
        compiledFile: resolvedCompiledFile,
        pdfByFile:
          resolvedCompiledFile && resolvedPdfUrl
            ? { ...state.pdfByFile, [resolvedCompiledFile]: resolvedPdfUrl }
            : state.pdfByFile,
      }
    }
    case "SET_ACTIVE_FILE":
      return { ...state, activeFile: action.path }
    case "SET_PDF_THUMBNAIL":
      return {
        ...state,
        pdfThumbnailByUrl: { ...state.pdfThumbnailByUrl, [action.pdfUrl]: action.dataUrl },
      }
    case "COMPILE_ERROR":
      return { ...state, compileStatus: "error", compileError: action.message, compileErrorDetails: action.details }
    case "UPSERT_FILE": {
      const files = { ...state.files, [action.file.path]: action.file }
      console.log("[editor-store] REDUCER: UPSERT_FILE path:", action.file.path, "existing:", Object.keys(state.files))
      return {
        ...state,
        files,
        virtualFolders: dedupeFolders([...state.virtualFolders, ...listVirtualFoldersFromFiles(files)]),
      }
    }
    case "DELETE_PATH": {
      const target = normalizePath(action.path)
      console.log("[editor-store] REDUCER: DELETE_PATH target:", target, "existing files:", Object.keys(state.files))
      if (!target) return state
      const files = Object.fromEntries(
        Object.entries(state.files).filter(([path]) => path !== target && !path.startsWith(`${target}/`)),
      )
      const virtualFolders = state.virtualFolders.filter(
        (folder) => folder !== target && !folder.startsWith(`${target}/`),
      )
      return { ...state, files, virtualFolders }
    }
    case "CLEAR_FILES":
      return {
        ...state,
        files: {},
        virtualFolders: [],
      }
    case "ADD_FOLDER":
      return { ...state, virtualFolders: dedupeFolders([...state.virtualFolders, action.path]) }
    case "CRDT_PROVIDER_SYNCED":
      return { ...state, crdtProviderEpoch: state.crdtProviderEpoch + 1 }
    case "CRDT_MAP_STRUCTURE":
      return { ...state, crdtMapRevision: state.crdtMapRevision + 1 }
    case "CRDT_REPLAY_SEED":
      return { ...state, crdtReplaySeedFiles: action.seedFiles }
    case "AUTH_PROJECT_HYDRATED":
      return { ...state, authProjectHydrationEpoch: state.authProjectHydrationEpoch + 1 }
    case "SET_FILESYSTEM_VERSION":
      return { ...state, filesystemVersion: action.version }
    case "RENAME_PATH": {
      const source = normalizePath(action.source)
      const target = normalizePath(action.target)
      if (!source || !target || source === target) return state
      const nextFiles: Record<string, EditorFileRecord> = {}
      for (const [path, record] of Object.entries(state.files)) {
        if (path === source || path.startsWith(`${source}/`)) {
          const suffix = path === source ? "" : path.slice(source.length)
          const updatedPath = `${target}${suffix}`
          nextFiles[updatedPath] = { ...record, path: updatedPath }
        } else {
          nextFiles[path] = record
        }
      }
      const mappedFolders = state.virtualFolders.map((folder) => {
        if (folder === source || folder.startsWith(`${source}/`)) {
          const suffix = folder === source ? "" : folder.slice(source.length)
          return `${target}${suffix}`
        }
        return folder
      })

      let nextActiveFile = state.activeFile
      if (state.activeFile === source) {
        nextActiveFile = target
      } else if (state.activeFile?.startsWith(`${source}/`)) {
        const suffix = state.activeFile.slice(source.length)
        nextActiveFile = `${target}${suffix}`
      }

      let nextCompiledFile = state.compiledFile
      if (state.compiledFile === source) {
        nextCompiledFile = target
      } else if (state.compiledFile?.startsWith(`${source}/`)) {
        const suffix = state.compiledFile.slice(source.length)
        nextCompiledFile = `${target}${suffix}`
      }

      const nextPdfByFile = { ...state.pdfByFile }
      Object.entries(state.pdfByFile).forEach(([file, url]) => {
        if (file === source) {
          delete nextPdfByFile[file]
          nextPdfByFile[target] = url
        } else if (file.startsWith(`${source}/`)) {
          delete nextPdfByFile[file]
          const suffix = file.slice(source.length)
          nextPdfByFile[`${target}${suffix}`] = url
        }
      })

      return {
        ...state,
        files: nextFiles,
        virtualFolders: dedupeFolders([...mappedFolders, ...listVirtualFoldersFromFiles(nextFiles)]),
        activeFile: nextActiveFile,
        compiledFile: nextCompiledFile,
        pdfByFile: nextPdfByFile,
      }
    }
    default:
      return state
  }
}

function initialState(initialSession?: CreateSessionResponse | null): CompilerEditorState {
  type PersistedSnapshot = {
    templateId?: string
    autoCompile?: boolean
    mainFile?: string
  }

  let persisted: PersistedSnapshot | undefined

  if (typeof window !== "undefined") {
    try {
      const raw = window.localStorage.getItem(STORE_KEY)
      persisted = raw ? (JSON.parse(raw) as PersistedSnapshot) : undefined
    } catch {
      persisted = undefined
    }
  }

  return {
    configReady: false,
    templatePhase: "idle",
    sessionPhase: initialSession ? "success" : "idle",
    sessionDisconnected: false,
    templateId: persisted?.templateId || process.env.NEXT_PUBLIC_TEMPLATE_ID?.trim() || "",
    mainFile: persisted?.mainFile || "",
    wsStatus: "disconnected",
    reconnectAttempt: 0,
    autoCompile: initialSession ? initialSession.auto_compile : (persisted?.autoCompile ?? true),
    compileStatus: "idle",
    files: INITIAL_FILES,
    virtualFolders: listVirtualFoldersFromFiles(INITIAL_FILES),
    events: [],
    crdtProviderEpoch: 0,
    crdtMapRevision: 0,
    sessionId: initialSession?.session_id || undefined,
    activeFile: undefined,
    compiledFile: undefined,
    pdfByFile: {},
    pdfThumbnailByUrl: {},
    authProjectHydrationEpoch: 0,
  }
}

const EditorStoreContext = createContext<EditorStoreContextValue | null>(null)
export { EditorStoreContext }

function getMimeType(ext: string): string {
  const map: Record<string, string> = {
    tex: "application/x-tex", bib: "text/plain", txt: "text/plain",
    md: "text/markdown", html: "text/html", htm: "text/html",
    css: "text/css", js: "text/javascript", ts: "text/typescript",
    json: "application/json", xml: "application/xml", svg: "image/svg+xml",
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif",
    pdf: "application/pdf", zip: "application/zip",
  }
  return map[ext.toLowerCase()] ?? "application/octet-stream"
}

export function EditorStoreProvider({
  children,
  initialSession,
  projectName: initialProjectName,
  projectId: initialProjectId,
}: {
  children: ReactNode
  initialSession?: CreateSessionResponse | null
  projectName?: string | null
  projectId?: string | null
}) {
  // Checked once at mount — auth users skip all session/template/compile init.
  const isAuthUserRef = useRef(typeof window !== "undefined" && hasAuthSession())
  const projectNameRef = useRef<string>(initialProjectName ?? "")
  // Auth users must never fall back to the shared "rudrise-project-id" key — it is
  // overwritten by whichever tab/project was opened last and causes the wrong
  // project's session/files to load. Auth users always have projectId in the URL.
  const projectIdRef = useRef<string>(
    initialProjectId ??
    (!isAuthUserRef.current && typeof window !== "undefined"
      ? (localStorage.getItem("rudrise-project-id") ?? "")
      : "")
  )
  const fileTreeFetchedRef = useRef(false)
  const sessionInitInFlightRef = useRef(false)
  const sessionInitAbortRef = useRef<AbortController | null>(null)
  const sessionInitOwnerRef = useRef("")
  const sessionInitLockKeyRef = useRef("")
  const startAnonymousCompilerSessionRef = useRef<(source: "auto" | "manual") => void>(() => {})
  const folderIdMapRef = useRef<Map<string, string>>(new Map())
  /** file_id → { name, parent }. Rebuilt from the path index and from filesystem events. */
  const fileNodeByIdRef = useRef<Map<string, FsNodeMeta>>(new Map())
  /** file ids whose Y.Text must not be bound to a path again after delete. */
  const retiredFileIdsRef = useRef<Set<string>>(new Set())
  const appliedStructureOpIdsRef = useRef<Set<string>>(new Set())
  const structureOpsLiveRef = useRef(false)
  const applyFsEventRef = useRef<(event: FsStructureEvent) => boolean>(() => false)
  const settlePendingStructureOpsRef = useRef<() => void>(() => {})
  const authPreviewLoadsRef = useRef<Set<string>>(new Set())
  const pendingAuthPreviewPathsRef = useRef<Set<string>>(new Set())
  /** Auth text creates waiting on POST /api/files. The path is not in state.files yet. */
  const pendingAuthFileCreatesRef = useRef<Set<string>>(new Set())
  const applyFileTreeRef = useRef<(pid: string) => Promise<string[]>>(async () => [])
  const compilerWsUrlRef = useRef<string>("")
  const crdtWsPathRef = useRef<string>("")

  // Auth users never resume a compiler session — pass null so sessionId stays undefined
  // and sessionPhase stays "idle", preventing any session-related side-effects.
  const [state, dispatch] = useReducer(
    reducer,
    isAuthUserRef.current ? null : initialSession,
    initialState
  )
  const openFileHandlerRef = useRef<OpenFileHandler | null>(null)
  const revealLineHandlerRef = useRef<((line: number) => void) | null>(null)
  const pathEventHandlerRef = useRef<PathEventHandler | null>(null)
  const synctexPdfHandlerRef = useRef<((box: SynctexForwardBox) => void) | null>(null)
  const framePostHandlerRef = useRef<((msg: object) => void) | null>(null)
  const inverseSyncHandlerRef = useRef<((line: number, column: number, word?: string) => void) | null>(null)
  const activeMonacoEditorRef = useRef<MonacoEditor.IStandaloneCodeEditor | null>(null)
  const editorHistoryHandlersRef = useRef<{
    undo: () => void
    redo: () => void
    canUndo?: () => boolean
    canRedo?: () => boolean
  } | null>(null)
  const wsClientRef = useRef<CompilerWsClient | null>(null)
  const apiClientRef = useRef<CompilerApiClient | null>(null)
  const configRef = useRef<{ templateId: string } | null>(null)
  const latestStateRef = useRef<CompilerEditorState>(state)
  const renderedStateRef = useRef<CompilerEditorState>(state)
  const localEditTimersRef = useRef<Record<string, number>>({})
  // Publish paths write the ref before React commits. A nested render that still
  // has the previous state must not copy that stale state back over the ref.
  if (renderedStateRef.current !== state) {
    renderedStateRef.current = state
    latestStateRef.current = state
  }

  // ── CRDT refs ──────────────────────────────────────────────────────────────────
  const sessionGenerationRef = useRef(0)
  const yDocRef = useRef<Y.Doc | null>(null)
  const yFilesMapRef = useRef<Y.Map<Y.Text> | null>(null)
  /** Ordered ops journal — structural ops (create/delete/rename/asset) appended here sync via CRDT. */
  const yOpsRef = useRef<Y.Array<Record<string, unknown>> | null>(null)
  /** Tracks which Y.Text instance is bound per path so map conflicts force Monaco rebind. */
  const lastBoundYTextByPathRef = useRef<Map<string, Y.Text>>(new Map())
  const crdtSyncRef = useRef<CrdtWsSyncManager | null>(null)
  const crdtReactFlushTimerRef = useRef<number | null>(null)
  const crdtDocUpdateHandlerRef = useRef<((update: Uint8Array, origin: unknown) => void) | null>(null)
  const crdtDocDebugUpdateHandlerRef = useRef<
    ((u: Uint8Array, o: unknown, d: Y.Doc, tr: Y.Transaction) => void) | null
  >(null)
  const crdtProviderUnsubsRef = useRef<(() => void)[]>([])
  const crdtDebugMetricsRef = useRef(createCrdtDebugMetrics())
  const crdtYTextDebugRegistryRef = useRef<Map<string, () => void>>(new Map())
  const crdtMetricsIntervalRef = useRef<number | null>(null)
  const crdtWsConnectedAtRef = useRef<number | null>(null)
  /** Tracks which tex file was sent in the most recent compile_trigger, for accurate compile_success attribution. */
  const lastTriggeredCompileFileRef = useRef<string | null>(null)
  /** True only when this tab sent compile_trigger — used for error toasts / local UI ownership. */
  const localCompilePendingRef = useRef(false)
  /** Newest PDF cache-bust we have applied — ignore stale/out-of-order WS pdf events. */
  const lastAppliedPdfCacheBustRef = useRef(0)
  /** Wall clock when we last applied a PDF (dedupe soft refresh after awareness). */
  const lastAppliedPdfAtRef = useRef(0)
  /** True when resumed session already had a compiled PDF — skip duplicate initial auto-compile. */
  const sessionHadExistingPdfRef = useRef(false)
  /** pdf_url / file hints from the latest compile_success while waiting for pdf_ready. */
  const pendingWsPdfRef = useRef<{
    pdfUrlFromBackend?: string
    compiledFile?: string
    backendTimes: Record<string, unknown>
    messageData: Record<string, unknown> | null
  } | null>(null)
  const pdfReadyFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoCompileEditTimerRef = useRef<number | null>(null)
  const triggerCompileRef = useRef<(reason?: string) => Promise<void>>(async () => {})
  const scheduleAutoCompileOnEditRef = useRef<() => void>(() => {})
  const schedulePeerPdfSoftRefreshRef = useRef<() => void>(() => {})
  /** Stable handle for awareness → PDF apply (setupYjs runs before applySessionPdf exists). */
  const applySessionPdfRef = useRef<(
    sessionId: string,
    options: {
      reason: string
      origin: "local" | "remote"
      pdfUrlFromBackend?: string | null
      compiledFile?: string | null
      backendTimes?: Record<string, unknown>
      messageData?: Record<string, unknown> | null
    },
  ) => void>(() => {})
  const tabHiddenAtRef = useRef<number | null>(null)
  /** Timestamp diagnostics for Compile:PDF:TS (file_change → compile_trigger → pdf). */
  const lastFileChangeTsRef = useRef<{ iso: string; epochMs: number } | null>(null)
  const lastCompileTriggerTsRef = useRef<{ iso: string; epochMs: number } | null>(null)
  const compileCycleStartedAtRef = useRef<number | null>(null)
  /** Ignore local auto-compile / file_change echoes briefly after remote CRDT applies. */
  const remoteEditGuardUntilRef = useRef(0)
  const peerPdfSoftRefreshTimerRef = useRef<number | null>(null)
  /** Last time compiler WS delivered any message (or opened). Detects half-open sockets that still accept sends. */
  const lastCompilerWsRecvAtRef = useRef(0)
  /** True once this tab sees compile_start/success/error/pdf_ready for the current local compile. */
  const localCompileResponseSeenRef = useRef(false)
  const localCompileAckTimerRef = useRef<number | null>(null)
  const localCompileSoftRecoveryTimerRef = useRef<number | null>(null)
  /** Peer pdfReady seen while this tab was mid-compile — apply after local cycle ends. */
  const pendingAwarenessPdfRef = useRef<{
    t: number
    sessionId: string
    compiledFile?: string | null
  } | null>(null)

  // Set of deleted paths to prevent race-condition resurrections from late-arriving sync updates
  const deletedPathsRef = useRef<Set<string>>(new Set())

  const logEvent = useCallback((level: LogLevel, message: string, details?: string) => {
    dispatch({ type: "LOG", event: toEvent(level, message, details) })
  }, [])

  // ── CRDT helpers ─────────────────────────────────────────────────────────────────

  const deleteYFile = useCallback((path: string): boolean => {
    const filesMap = yFilesMapRef.current
    const doc = yDocRef.current
    if (!filesMap || !doc) return false
    const fileID = folderIdMapRef.current.get(path) ?? path
    doc.transact(() => {
      filesMap.delete(fileID)
    }, { kind: "local", fileID } satisfies LocalCrdtOrigin)
    return true
  }, [])

  /** Append one structure operation to the CRDT ops journal so the compiler
   *  applies it in order (deduped via op_id). Lives inside the synced doc, so
   *  late joiners and restarted pods replay it automatically. */
  const appendStructureOp = useCallback((op: { type: string; path: string; to?: string; file_id?: string }) => {
    const doc = yDocRef.current
    const ops = yOpsRef.current
    if (!doc || !ops || !op.path) {
      console.warn("[CRDT:Ops] structure op DROPPED — compiler will never see it", {
        type: op.type,
        path: op.path,
        to: op.to,
        hasDoc: Boolean(doc),
        hasOps: Boolean(ops),
        sessionId: latestStateRef.current.sessionId,
        wsStatus: latestStateRef.current.wsStatus,
      })
      return
    }
    const entry: Record<string, unknown> = {
      op_id:
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: op.type,
      path: op.path,
      user_name: readAuthUserLabel() ?? "",
      ts: Date.now(),
    }
    if (op.to) entry.to = op.to
    if (op.file_id) entry.file_id = op.file_id
    const fileID = typeof op.file_id === "string" ? op.file_id.trim() : ""
    doc.transact(() => {
      ops.push([entry])
    }, {
      kind: "local",
      path: op.path,
      ...(fileID ? { fileID } : {}),
    } satisfies LocalCrdtOrigin)
  }, [])

  /** Record a rename/move source and its current descendants as exact tombstones.
   *  A later file at a new path under that prefix is not one of these keys. */
  const tombstoneMovedAwayPaths = useCallback((source: string) => {
    const mark = (path: string) => {
      if (path === source || path.startsWith(`${source}/`)) deletedPathsRef.current.add(path)
    }
    mark(source)
    Object.keys(latestStateRef.current.files).forEach(mark)
    latestStateRef.current.virtualFolders.forEach(mark)
    folderIdMapRef.current.forEach((_id, path) => mark(path))
  }, [])

  const isPathDeleted = useCallback((path: string): boolean => {
    const normalized = normalizePath(path)
    if (!normalized) return false
    const mappedId = folderIdMapRef.current.get(normalized)
    // A mapped file_id is the current node at this path. A parent folder that was
    // renamed away (chapters/ → sections/) must not hide chapters/new.tex.
    if (mappedId) return retiredFileIdsRef.current.has(mappedId)
    if (deletedPathsRef.current.has(normalized)) return true
    const underTombstone = Array.from(deletedPathsRef.current).some((del) =>
      normalized.startsWith(`${del}/`),
    )
    if (!underTombstone) return false
    if (latestStateRef.current.files[normalized]) return false
    if (latestStateRef.current.virtualFolders.includes(normalized)) return false
    return true
  }, [])

  const rebuildFileNodeIndex = useCallback(() => {
    const next = new Map<string, FsNodeMeta>()
    folderIdMapRef.current.forEach((id, path) => {
      const previous = fileNodeByIdRef.current.get(id)
      const parent = parentPath(path)
      const kind: FsNodeMeta["kind"] = latestStateRef.current.files[path]
        ? "file"
        : (previous?.kind ?? "folder")
      next.set(id, {
        name: basename(path),
        parent: parent ? (folderIdMapRef.current.get(parent) ?? null) : null,
        kind,
      })
    })
    fileNodeByIdRef.current = next
  }, [])

  const findPathByFileId = useCallback((fileID: string): string | undefined => {
    let found: string | undefined
    folderIdMapRef.current.forEach((id, path) => {
      if (id === fileID) found = path
    })
    return found
  }, [])

  /** Creator-only. Inserts one Y.Text and broadcasts it (local origin).
   *  Remote FILE_CREATED must not call this. A second Y.Text for the same file_id
   *  is a Y.Map conflict and can drop the creator's text. Peers use the object
   *  that arrives in the content map. A repeat call does not replace an existing key.
   *  Callers skip binary/asset files. */
  const ensureContentYText = useCallback((fileID: string, mode: "local" | "remote"): boolean => {
    if (mode !== "local") return false
    if (!fileID || retiredFileIdsRef.current.has(fileID)) return false
    const contentMap = yFilesMapRef.current
    const doc = yDocRef.current
    if (!contentMap || !doc || contentMap.has(fileID)) return false
    let inserted = false
    doc.transact(() => {
      if (contentMap.has(fileID)) return
      contentMap.set(fileID, new Y.Text())
      inserted = true
    }, { kind: "local", fileID } satisfies LocalCrdtOrigin)
    return inserted
  }, [])

  /** Drop a file_id locally without broadcasting. The deleter already published
   *  the content delete via deleteYFile (local origin). Seed origin is not echoed
   *  by the sync manager, so a receiver applying the same delete cannot loop. */
  const dropContentYText = useCallback((fileID: string): boolean => {
    const contentMap = yFilesMapRef.current
    const doc = yDocRef.current
    if (!fileID || !contentMap || !doc || !contentMap.has(fileID)) return false
    doc.transact(() => {
      contentMap.delete(fileID)
    }, { kind: "seed" } satisfies SeedCrdtOrigin)
    return true
  }, [])

  const relocateTrackedPath = useCallback((sourcePath: string, targetPath: string, fileID?: string): boolean => {
    const source = normalizePath(sourcePath)
    const target = normalizePath(targetPath)
    if (!source || !target || source === target) return true

    const sourceId = folderIdMapRef.current.get(source) ?? ""
    const resolvedId = fileID || sourceId
    if (fileID) {
      const currentPath = findPathByFileId(fileID)
      if (currentPath === target) return true
      // Already at a later path. Replaying this op would rewind the file.
      if (currentPath && currentPath !== source) return true
      if (!currentPath && sourceId !== fileID) {
        // Source is empty, or it belongs to a different id. Never move that
        // other file. An empty source can be retried once this id is indexed.
        return sourceId ? true : false
      }
    } else if (resolvedId) {
      const currentPath = findPathByFileId(resolvedId)
      if (currentPath === target) return true
      if (currentPath && currentPath !== source) return true
    }

    const current = latestStateRef.current
    const inFiles = Object.keys(current.files).some((path) => path === source || path.startsWith(`${source}/`))
    const inFolders = current.virtualFolders.some((path) => path === source || path.startsWith(`${source}/`))
    const inMap = folderIdMapRef.current.has(source)
    if (!inFiles && !inFolders && !inMap) {
      return !(fileID && !findPathByFileId(fileID))
    }

    const targetId = folderIdMapRef.current.get(target)
    if ((current.files[target] || current.virtualFolders.includes(target) || targetId) && targetId !== sourceId) {
      console.warn("[CRDT:Fs] relocate skipped — target already exists", { source, target })
      return true
    }

    deletedPathsRef.current.delete(target)
    Array.from(deletedPathsRef.current).forEach((deleted) => {
      if (deleted.startsWith(`${target}/`)) deletedPathsRef.current.delete(deleted)
    })
    tombstoneMovedAwayPaths(source)

    if (sourceId) {
      const childMoves: Array<[string, string]> = []
      folderIdMapRef.current.forEach((id, key) => {
        if (key.startsWith(`${source}/`)) childMoves.push([key, id])
      })
      folderIdMapRef.current.delete(source)
      folderIdMapRef.current.set(target, sourceId)
      for (const [key, id] of childMoves) {
        folderIdMapRef.current.delete(key)
        folderIdMapRef.current.set(`${target}${key.slice(source.length)}`, id)
      }
    } else if (resolvedId) {
      folderIdMapRef.current.set(target, resolvedId)
    }

    const boundMoves: Array<[string, Y.Text]> = []
    lastBoundYTextByPathRef.current.forEach((ytext, key) => {
      if (key === source || key.startsWith(`${source}/`)) boundMoves.push([key, ytext])
    })
    for (const [key, ytext] of boundMoves) {
      lastBoundYTextByPathRef.current.delete(key)
      const suffix = key === source ? "" : key.slice(source.length)
      lastBoundYTextByPathRef.current.set(`${target}${suffix}`, ytext)
    }

    const nextFiles: Record<string, EditorFileRecord> = {}
    for (const [path, record] of Object.entries(current.files)) {
      if (path === source || path.startsWith(`${source}/`)) {
        const suffix = path === source ? "" : path.slice(source.length)
        const updatedPath = `${target}${suffix}`
        nextFiles[updatedPath] = { ...record, path: updatedPath }
      } else {
        nextFiles[path] = record
      }
    }
    const mappedFolders = current.virtualFolders.map((folder) => {
      if (folder === source || folder.startsWith(`${source}/`)) {
        const suffix = folder === source ? "" : folder.slice(source.length)
        return `${target}${suffix}`
      }
      return folder
    })
    let nextActiveFile = current.activeFile
    if (current.activeFile === source) nextActiveFile = target
    else if (current.activeFile?.startsWith(`${source}/`)) {
      nextActiveFile = `${target}${current.activeFile.slice(source.length)}`
    }
    let nextCompiledFile = current.compiledFile
    if (current.compiledFile === source) nextCompiledFile = target
    else if (current.compiledFile?.startsWith(`${source}/`)) {
      nextCompiledFile = `${target}${current.compiledFile.slice(source.length)}`
    }
    const nextPdfByFile = { ...current.pdfByFile }
    Object.entries(current.pdfByFile).forEach(([file, url]) => {
      if (file === source || file.startsWith(`${source}/`)) {
        delete nextPdfByFile[file]
        const suffix = file === source ? "" : file.slice(source.length)
        nextPdfByFile[`${target}${suffix}`] = url
      }
    })
    latestStateRef.current = {
      ...current,
      files: nextFiles,
      virtualFolders: dedupeFolders([...mappedFolders, ...listVirtualFoldersFromFiles(nextFiles)]),
      activeFile: nextActiveFile,
      compiledFile: nextCompiledFile,
      pdfByFile: nextPdfByFile,
    }
    dispatch({ type: "RENAME_PATH", source, target })
    pathEventHandlerRef.current?.({ type: "rename", source, target })
    rebuildFileNodeIndex()
    console.log("[CRDT:Fs] path updated; Y.Text kept for file_id", {
      source,
      target,
      fileID: resolvedId || null,
    })
    return true
  }, [findPathByFileId, rebuildFileNodeIndex, tombstoneMovedAwayPaths])

  const applyFsEvent = useCallback((event: FsStructureEvent): boolean => {
    const resolveCreatePath = (name: string, parentId: string | null, explicitPath?: string): string => {
      if (explicitPath) return normalizePath(explicitPath)
      if (!parentId) return normalizePath(name)
      const parent = findPathByFileId(parentId)
      if (!parent) return ""
      return normalizePath(`${parent}/${name}`)
    }

    if (event.type === "FILE_CREATED" || event.type === "FOLDER_CREATED") {
      const fileID = event.file_id
      if (fileID && retiredFileIdsRef.current.has(fileID)) return true
      if (fileID) {
        const existing = findPathByFileId(fileID)
        if (existing) {
          // Already indexed. getYText reads contentMap.get(fileID); do not allocate another.
          return true
        }
      }
      const resolved = resolveCreatePath(event.name, event.parent_id, event.path)
      if (!resolved) {
        console.warn("[CRDT:Fs] create skipped — could not resolve path", event)
        return false
      }
      const occupant = folderIdMapRef.current.get(resolved)
      if (occupant && occupant !== fileID) {
        console.warn("[CRDT:Fs] create skipped — path already mapped", { path: resolved, occupant, fileID })
        return true
      }
      deletedPathsRef.current.delete(resolved)
      if (fileID) folderIdMapRef.current.set(resolved, fileID)

      if (event.type === "FOLDER_CREATED") {
        const parts = resolved.split("/")
        const additions: string[] = []
        for (let i = 1; i <= parts.length; i += 1) additions.push(parts.slice(0, i).join("/"))
        for (const folder of additions) {
          if (!latestStateRef.current.virtualFolders.includes(folder)) {
            dispatch({ type: "ADD_FOLDER", path: folder })
          }
        }
        latestStateRef.current = {
          ...latestStateRef.current,
          virtualFolders: dedupeFolders([...latestStateRef.current.virtualFolders, ...additions]),
        }
        rebuildFileNodeIndex()
        return true
      }

      const isAsset = event.isAsset || isAssetByExtension(resolved)
      if (!latestStateRef.current.files[resolved]) {
        const file: EditorFileRecord = {
          path: resolved,
          content: "",
          isAsset,
          localVersion: 1,
          pendingSync: false,
        }
        dispatch({ type: "UPSERT_FILE", file })
        latestStateRef.current = {
          ...latestStateRef.current,
          files: { ...latestStateRef.current.files, [resolved]: file },
        }
      }
      rebuildFileNodeIndex()
      // Path metadata only. The creator's Y.Text arrives through CRDT sync and
      // the content-map observer binds Monaco to that object.
      return true
    }

    if (event.type === "FILE_RENAMED") {
      const source = findPathByFileId(event.file_id)
      if (!source) {
        console.warn("[CRDT:Fs] rename skipped — unknown file_id", event.file_id)
        return false
      }
      if (basename(source) === event.name) return true
      const parent = parentPath(source)
      const target = normalizePath(parent ? `${parent}/${event.name}` : event.name)
      if (!target) return false
      return relocateTrackedPath(source, target, event.file_id)
    }

    if (event.type === "FILE_MOVED") {
      const source = findPathByFileId(event.file_id)
      if (!source) {
        console.warn("[CRDT:Fs] move skipped — unknown file_id", event.file_id)
        return false
      }
      const name = basename(source)
      let target = ""
      if (!event.parent_id) {
        target = normalizePath(name)
      } else {
        const parent = findPathByFileId(event.parent_id)
        if (!parent) {
          console.warn("[CRDT:Fs] move skipped — parent_id not in map", event)
          return false
        }
        target = normalizePath(`${parent}/${name}`)
      }
      if (!target || target === source) {
        const node = fileNodeByIdRef.current.get(event.file_id)
        if (node) fileNodeByIdRef.current.set(event.file_id, { ...node, parent: event.parent_id })
        return true
      }
      return relocateTrackedPath(source, target, event.file_id)
    }

    if (event.type === "PATH_RELOCATED") {
      return relocateTrackedPath(event.source, event.target, event.file_id)
    }

    const mapped = event.file_id ? findPathByFileId(event.file_id) : undefined
    if (event.file_id && retiredFileIdsRef.current.has(event.file_id) && !mapped) return true

    let path = mapped ? normalizePath(mapped) : ""
    if (!path && event.path) {
      const hinted = normalizePath(event.path)
      const occupant = hinted ? folderIdMapRef.current.get(hinted) : undefined
      // A historical delete must not remove a different file that now owns this path.
      if (event.file_id && occupant && occupant !== event.file_id) {
        retiredFileIdsRef.current.add(event.file_id)
        fileNodeByIdRef.current.delete(event.file_id)
        if (!dropContentYText(event.file_id)) dispatch({ type: "CRDT_MAP_STRUCTURE" })
        return true
      }
      path = hinted
    }
    if (!path && !event.file_id) return true

    if (!path) {
      retiredFileIdsRef.current.add(event.file_id)
      fileNodeByIdRef.current.delete(event.file_id)
      if (!dropContentYText(event.file_id)) dispatch({ type: "CRDT_MAP_STRUCTURE" })
      return true
    }

    const current = latestStateRef.current
    const tracked =
      folderIdMapRef.current.has(path) ||
      Boolean(current.files[path]) ||
      current.virtualFolders.includes(path) ||
      Object.keys(current.files).some((key) => key.startsWith(`${path}/`))
    if (!tracked) {
      if (event.file_id) {
        retiredFileIdsRef.current.add(event.file_id)
        fileNodeByIdRef.current.delete(event.file_id)
        dropContentYText(event.file_id)
      }
      return true
    }

    deletedPathsRef.current.add(path)
    const keys: string[] = []
    const ids: string[] = []
    folderIdMapRef.current.forEach((id, key) => {
      if (key === path || key.startsWith(`${path}/`)) {
        keys.push(key)
        ids.push(id)
      }
    })
    if (event.file_id && !ids.includes(event.file_id)) ids.push(event.file_id)

    let removedText = false
    for (const id of ids) {
      retiredFileIdsRef.current.add(id)
      fileNodeByIdRef.current.delete(id)
      if (dropContentYText(id)) removedText = true
    }
    for (const key of keys) {
      folderIdMapRef.current.delete(key)
      lastBoundYTextByPathRef.current.delete(key)
      deletedPathsRef.current.add(key)
    }

    const nextFiles = Object.fromEntries(
      Object.entries(current.files).filter(([filePath]) => filePath !== path && !filePath.startsWith(`${path}/`)),
    )
    latestStateRef.current = {
      ...current,
      files: nextFiles,
      virtualFolders: current.virtualFolders.filter((folder) => folder !== path && !folder.startsWith(`${path}/`)),
    }
    dispatch({ type: "DELETE_PATH", path })
    pathEventHandlerRef.current?.({ type: "delete", path })
    rebuildFileNodeIndex()
    if (!removedText) dispatch({ type: "CRDT_MAP_STRUCTURE" })
    console.log("[CRDT:Fs] deleted file_id; Monaco binding released", { path, fileID: event.file_id || null })
    return true
  }, [dropContentYText, findPathByFileId, rebuildFileNodeIndex, relocateTrackedPath])

  applyFsEventRef.current = applyFsEvent

  const settlePendingStructureOps = useCallback(() => {
    const syncedOps = yOpsRef.current
    if (!syncedOps) return
    for (let i = 0; i < syncedOps.length; i += 1) {
      const entry = syncedOps.get(i)
      if (!entry || typeof entry !== "object") continue
      const opId = typeof entry.op_id === "string" ? entry.op_id : ""
      if (opId && appliedStructureOpIdsRef.current.has(opId)) continue
      const structureEvent = structureEventFromOp(entry as Record<string, unknown>)
      if (!structureEvent) {
        if (opId) appliedStructureOpIdsRef.current.add(opId)
        continue
      }
      if (applyFsEventRef.current(structureEvent) && opId) {
        appliedStructureOpIdsRef.current.add(opId)
      }
    }
  }, [])
  settlePendingStructureOpsRef.current = settlePendingStructureOps

  const updateFilesFromYjs = useCallback(() => {
    const filesMap = yFilesMapRef.current
    if (!filesMap) return
    if (CRDT_RUNTIME_DEBUG) {
      const m = crdtDebugMetricsRef.current
      logReactFlushFromYjs(m, filesMap.size)
    }
    const fileIDToPath = new Map<string, string>(
      Array.from(folderIdMapRef.current.entries()).map(([p, id]) => [id, p])
    )
    const keysInYjs = new Set<string>()
    filesMap.forEach((ytext, fileID) => {
      if (retiredFileIdsRef.current.has(fileID)) {
        const doc = yDocRef.current
        if (doc && filesMap.has(fileID)) {
          doc.transact(() => {
            filesMap.delete(fileID)
          }, { kind: "seed" } satisfies SeedCrdtOrigin)
        }
        return
      }
      const path = fileIDToPath.get(fileID) ?? fileID
      if (isPathDeleted(path)) {
        console.log("[editor-store] updateFilesFromYjs: ignoring and deleting resurrected file_id:", fileID)
        deleteYFile(path)
        return
      }
      keysInYjs.add(path)
      const content = ytext.toString()
      const existing = latestStateRef.current.files[path]
      // CRDT can return empty stubs on reconnect — do not wipe hydrated project content.
      if (!content.trim() && existing?.content?.trim()) {
        console.warn("[editor-store] skip flushing empty Y.Text over non-empty React file", path)
        return
      }
      dispatch({
        type: "UPSERT_FILE",
        file: {
          path,
          content,
          isAsset: existing?.isAsset ?? isAssetByExtension(path),
          localVersion: (existing?.localVersion ?? 0) + 1,
          pendingSync: false,
        },
      })
    })

    // Never prune React explorer files just because the Y map is partial/empty.
    // Auth open often seeds only project.rudra.config.json first (main.tex content
    // arrives later via file preview). Deleting the stub removes main.tex from the
    // tree until a later heal. Explicit user deletes use deletedPathsRef + deleteYFile.
    if (crdtSyncRef.current?.synced && keysInYjs.size === 0) {
      const reactNonAsset = Object.keys(latestStateRef.current.files).filter(
        (p) => !latestStateRef.current.files[p]?.isAsset,
      )
      if (reactNonAsset.length > 0) {
        console.warn(
          "[editor-store] skip pruning React files — Y map empty after sync (keeping hydrated project files)",
          { reactNonAsset },
        )
      }
      return
    }

    // Only seed the Yjs doc from React state before the first CRDT sync completes.
    // Once synced, the server-side CRDT doc is the source of truth; healing from
    // React here would push stale/deleted paths back into the document.
    if (!crdtSyncRef.current?.synced) {
      const currentFiles = latestStateRef.current.files
      const doc = yDocRef.current
      const missing: string[] = []
      Object.keys(currentFiles).forEach((path) => {
        const file = currentFiles[path]
        const fileID = folderIdMapRef.current.get(path)
        if (!fileID || !file || file.isAsset || filesMap.has(fileID)) return
        if (isPathDeleted(path)) {
          dispatch({ type: "DELETE_PATH", path })
          return
        }
        // Keep empty stubs in the explorer; heal into Y once real content exists.
        if (!file.content?.trim() || !doc) return
        // Never heal stub/placeholder content (e.g. openFile's "% name" comment)
        // into the doc — it would sync to the compiler and resurrect the path in Redis.
        if (isPlaceholderFileContent(path, file.content)) return
        missing.push(path)
        doc.transact(() => {
          const created = new Y.Text()
          created.insert(0, file.content)
          filesMap.set(fileID, created)
        }, { kind: "seed" } satisfies SeedCrdtOrigin)
      })
      if (missing.length > 0) {
        console.warn(
          "[editor-store] Y content map missing hydrated files — seeded by file_id before sync",
          { missing, keysInYjs: Array.from(keysInYjs) },
        )
      }
    }
  }, [deleteYFile, isPathDeleted])

  const flushReactFromCrdtDoc = useCallback(() => {
    if (crdtReactFlushTimerRef.current !== null) {
      window.clearTimeout(crdtReactFlushTimerRef.current)
      crdtReactFlushTimerRef.current = null
    }
    updateFilesFromYjs()
  }, [updateFilesFromYjs])

  /**
   * After auth file-tree / preview hydration, push any React text files that are
   * still missing from Y. Preview intentionally skips writing Y (peer races); without
   * this heal the map can stay config-only and compile/cursors break for joiners.
   * Only seed when alone in the room — if peers exist, request sync instead.
   */
  const healMissingReactFilesIntoYjs = useCallback(() => {
    const filesMap = yFilesMapRef.current
    const doc = yDocRef.current
    const sync = crdtSyncRef.current
    if (!filesMap || !doc || !sync || sync.synced) return

    const peerCount = Math.max(0, sync.awareness.getStates().size - 1)
    if (peerCount > 0) {
      sync.requestSyncFromServer()
      return
    }

    const missing: { path: string; fileID: string; content: string }[] = []
    for (const [path, file] of Object.entries(latestStateRef.current.files)) {
      if (!file || file.isAsset || isPathDeleted(path)) continue
      const fileID = folderIdMapRef.current.get(path)
      if (!fileID || !file.content?.trim() || filesMap.has(fileID)) continue
      // Same guard: openFile stubs must never enter the doc / reach Redis.
      if (isPlaceholderFileContent(path, file.content)) continue
      missing.push({ path, fileID, content: file.content })
    }
    if (missing.length === 0) return

    console.warn("[editor-store] post-hydration seed into Y content map", {
      missing: missing.map((m) => m.path),
      mapSizeBefore: filesMap.size,
    })
    doc.transact(() => {
      for (const { fileID, content } of missing) {
        if (filesMap.has(fileID)) continue
        const ytext = new Y.Text()
        ytext.insert(0, content)
        filesMap.set(fileID, ytext)
      }
    }, { kind: "seed" } satisfies SeedCrdtOrigin)
    flushReactFromCrdtDoc()
  }, [flushReactFromCrdtDoc, isPathDeleted])

  const cleanupYjs = useCallback(() => {
    if (crdtReactFlushTimerRef.current !== null) {
      window.clearTimeout(crdtReactFlushTimerRef.current)
      crdtReactFlushTimerRef.current = null
    }
    const doc = yDocRef.current
    const handler = crdtDocUpdateHandlerRef.current
    if (doc && handler) {
      doc.off("update", handler)
      crdtDocUpdateHandlerRef.current = null
    }
    const dbgHandler = crdtDocDebugUpdateHandlerRef.current
    if (doc && dbgHandler) {
      doc.off("update", dbgHandler)
      crdtDocDebugUpdateHandlerRef.current = null
    }
    if (crdtMetricsIntervalRef.current !== null) {
      window.clearInterval(crdtMetricsIntervalRef.current)
      crdtMetricsIntervalRef.current = null
    }
    clearYTextDebugRegistry(crdtYTextDebugRegistryRef.current)
    for (const unsub of crdtProviderUnsubsRef.current) {
      try {
        unsub()
      } catch {
        /* ignore */
      }
    }
    crdtProviderUnsubsRef.current = []
    if (crdtSyncRef.current) {
      crdtLog("log", "destroying CRDT sync manager")
      crdtSyncRef.current.destroy()
      crdtSyncRef.current = null
    }
    if (yDocRef.current) {
      yDocRef.current.destroy()
      yDocRef.current = null
    }
    yFilesMapRef.current = null
    yOpsRef.current = null
    lastBoundYTextByPathRef.current.clear()
    structureOpsLiveRef.current = false
    appliedStructureOpIdsRef.current.clear()
  }, [])

  const updateYFile = useCallback((path: string, newContent: string): boolean => {
    const filesMap = yFilesMapRef.current
    const doc = yDocRef.current
    if (!filesMap || !doc) return false
    if (isPathDeleted(path)) {
      console.warn("[CRDT] updateYFile refused — path was deleted/renamed", path)
      return false
    }

    const fileID = folderIdMapRef.current.get(path)
    if (!fileID) {
      console.warn("[CRDT] updateYFile refused — no stable file_id for path", path)
      return false
    }
    if (retiredFileIdsRef.current.has(fileID)) {
      console.warn("[CRDT] updateYFile refused — file_id was deleted", fileID)
      return false
    }

    const ytext = filesMap.get(fileID)

    if (!ytext) {
      if (!newContent.trim()) return false
      doc.transact(() => {
        const created = new Y.Text()
        created.insert(0, newContent)
        filesMap.set(fileID, created)
      }, { kind: "local", fileID } satisfies LocalCrdtOrigin)
      return true
    }

    const prev = ytext.toString()
    if (prev === newContent) return true

    if (prev.trim() && !newContent.trim()) {
      console.warn("[CRDT] refusing to wipe non-empty Y.Text with empty content", path)
      return false
    }

    let start = 0
    while (start < prev.length && start < newContent.length && prev[start] === newContent[start]) start++

    let prevEnd = prev.length
    let nextEnd = newContent.length
    while (prevEnd > start && nextEnd > start && prev[prevEnd - 1] === newContent[nextEnd - 1]) {
      prevEnd--
      nextEnd--
    }

    const deleteCount = prevEnd - start
    const insertText = newContent.slice(start, nextEnd)

    if (CRDT_RUNTIME_DEBUG) {
      logReactYFileMutation({
        path,
        deleteCount,
        insertLen: insertText.length,
        prevLen: prev.length,
        metrics: crdtDebugMetricsRef.current,
      })
    }

    doc.transact(() => {
      if (deleteCount > 0) ytext.delete(start, deleteCount)
      if (insertText.length > 0) ytext.insert(start, insertText)
    }, { kind: "local", fileID } satisfies LocalCrdtOrigin)

    return true
  }, [])

  // deleteYFile has been moved to the top of CRDT helpers to resolve declaration order.

  const waitForCrdtSync = useCallback(
    async (_sessionId: string, generation: number, maxWaitMs = 3500): Promise<boolean> => {
      const sync = crdtSyncRef.current
      if (!sync) {
        console.log("[CRDT:Sync] wait skipped — no sync manager")
        return true
      }
      if (sync.synced) {
        console.log("[CRDT:Sync] already synced")
        return true
      }
      console.log("[CRDT:Sync] waiting for sync", { status: sync.status, maxWaitMs })
      const start = Date.now()
      return new Promise<boolean>((resolve) => {
        const poll = () => {
          if (generation !== sessionGenerationRef.current) {
            console.log("[CRDT:Sync] wait aborted — session generation changed")
            resolve(false)
            return
          }
          if (crdtSyncRef.current?.synced) {
            console.log("[CRDT:Sync] sync completed", { elapsedMs: Date.now() - start })
            resolve(true)
            return
          }
          if (Date.now() - start >= maxWaitMs) {
            console.warn("[CRDT:Sync] wait timed out", {
              elapsedMs: Date.now() - start,
              status: crdtSyncRef.current?.status,
              synced: crdtSyncRef.current?.synced,
            })
            crdtLog("warn", "sync wait timed out, proceeding anyway")
            resolve(false)
            return
          }
          setTimeout(poll, 50)
        }
        setTimeout(poll, 50)
      })
    },
    [],
  )

  const connectYjs = useCallback(
    (
      info: CrdtInfo,
      sessionId: string,
      seedFiles: { path: string; content: string }[],
      generation: number,
      options?: { shouldSeed?: boolean },
    ) => {
      cleanupYjs()

      if (CRDT_RUNTIME_DEBUG) {
        crdtDebugMetricsRef.current.reset()
        crdtMetricsIntervalRef.current = window.setInterval(() => {
          crdtDebugMetricsRef.current.maybeLogSummary()
        }, 5000)
      }

      const doc = new Y.Doc()
      const filesMap = doc.getMap<Y.Text>("content")
      console.log("[CRDT:INIT] Yjs doc created", {
        session_id: sessionId,
        mapKey: "content",
        shouldSeed: Boolean(options?.shouldSeed && seedFiles.length > 0),
        seedFileCount: seedFiles.length,
      })
      yDocRef.current = doc
      yFilesMapRef.current = filesMap
      yOpsRef.current = doc.getArray<Record<string, unknown>>("ops")
      structureOpsLiveRef.current = false

      const ops = yOpsRef.current
      const onStructureOps = (event: Y.YArrayEvent<Record<string, unknown>>) => {
        if (generation !== sessionGenerationRef.current) return
        const local = isLocalDocumentEdit(event.transaction.origin)
        for (const step of event.changes.delta) {
          const inserted = step.insert
          const items = Array.isArray(inserted) ? inserted : []
          for (const item of items) {
            if (!item || typeof item !== "object") continue
            const entry = item as Record<string, unknown>
            const opId = typeof entry.op_id === "string" ? entry.op_id : ""
            if (opId && appliedStructureOpIdsRef.current.has(opId)) continue
            // Local handlers already wrote path metadata before appending the op.
            if (local) {
              if (opId) appliedStructureOpIdsRef.current.add(opId)
              continue
            }
            const structureEvent = structureEventFromOp(entry)
            if (!structureEvent) {
              if (opId) appliedStructureOpIdsRef.current.add(opId)
              continue
            }
            // Mark seen only after path metadata is applied or already matches.
            // Initial sync used to mark the whole journal seen and skip apply,
            // leaving folderIdMapRef stale while contentMap was already synced.
            if (applyFsEventRef.current(structureEvent) && opId) {
              appliedStructureOpIdsRef.current.add(opId)
            }
          }
        }
      }
      ops.observe(onStructureOps)
      crdtProviderUnsubsRef.current.push(() => {
        ops.unobserve(onStructureOps)
      })

      filesMap.observe((event: Y.YMapEvent<Y.Text>) => {
        if (CRDT_RUNTIME_DEBUG) {
          logFilesMapStructure(event, crdtDebugMetricsRef.current)
          const reg = crdtYTextDebugRegistryRef.current
          event.keys.forEach((keyChange, key) => {
            if (keyChange.action === "delete") {
              reg.get(key)?.()
              return
            }
            const yt = filesMap.get(key)
            if (!yt || reg.has(key)) return
            const obs = (e: Y.YTextEvent) => {
              logYTextDelta(key, e, crdtDebugMetricsRef.current)
            }
            yt.observe(obs)
            reg.set(key, () => {
              yt.unobserve(obs)
              reg.delete(key)
            })
          })
        }
        if (event.keysChanged.size > 0) {
          dispatch({ type: "CRDT_MAP_STRUCTURE" })
        }
      })

      const scheduleReactFlushFromDoc = (immediate = false) => {
        if (crdtReactFlushTimerRef.current !== null) window.clearTimeout(crdtReactFlushTimerRef.current)
        if (immediate) {
          crdtReactFlushTimerRef.current = null
          updateFilesFromYjs()
          return
        }
        crdtReactFlushTimerRef.current = window.setTimeout(() => {
          crdtReactFlushTimerRef.current = null
          updateFilesFromYjs()
        }, 120)
      }
      const onDocUpdate = (update: Uint8Array, origin: unknown) => {
        const remote = isRemoteDocumentEdit(origin)
        scheduleReactFlushFromDoc(remote)
        if (remote) {
          // Remote CRDT → Monaco/React can echo as "local" typing; block auto-compile + file_change.
          remoteEditGuardUntilRef.current = Date.now() + 2000
          const filesMap = yFilesMapRef.current
          const mainPath = latestStateRef.current.mainFile || latestStateRef.current.activeFile || "main.tex"
          const ytext = filesMap?.get(mainPath)
          const yLen = ytext?.length ?? -1
          const reactLen = latestStateRef.current.files[mainPath]?.content?.length ?? -1
          console.log("[CRDT:Sync] remote document update applied to local Y.Doc", {
            bytes: update.byteLength,
            path: mainPath,
            yLen,
            reactLen,
            autoCompileSuppressedUntil: remoteEditGuardUntilRef.current,
          })
          // Y.Map conflict resolution can replace the Y.Text instance for a path.
          // Force Monaco to rebind to the canonical filesMap entry.
          if (ytext && lastBoundYTextByPathRef.current.get(mainPath) !== ytext) {
            lastBoundYTextByPathRef.current.set(mainPath, ytext)
            dispatch({ type: "CRDT_MAP_STRUCTURE" })
          }
          // Peer is editing — they (or compile leader) will compile. Soft-refresh PDF if
          // compiler WS never fans out pdf_ready to this tab.
          schedulePeerPdfSoftRefreshRef.current()
        }
        if (isLocalDocumentEdit(origin)) {
          if (Date.now() < remoteEditGuardUntilRef.current) {
            console.log("[Compile:PDF] skip auto-compile schedule — inside remote CRDT guard", {
              origin:
                origin && typeof origin === "object"
                  ? ((origin as { kind?: string; constructor?: { name?: string } }).kind
                    ?? (origin as { constructor?: { name?: string } }).constructor?.name)
                  : typeof origin,
            })
            return
          }
          scheduleAutoCompileOnEditRef.current()
        }
      }
      doc.on("update", onDocUpdate)
      crdtDocUpdateHandlerRef.current = onDocUpdate

      if (CRDT_RUNTIME_DEBUG) {
        const onDocDebug = (update: Uint8Array, origin: unknown, _d: Y.Doc, tr: Y.Transaction) => {
          logDocUpdate({
            update,
            origin,
            transaction: tr,
            filesMap: yFilesMapRef.current,
            mainFileHint: latestStateRef.current.mainFile,
            metrics: crdtDebugMetricsRef.current,
          })
        }
        doc.on("update", onDocDebug)
        crdtDocDebugUpdateHandlerRef.current = onDocDebug
      }



      if (CRDT_RUNTIME_DEBUG) {
        primeYTextDebugRegistry(filesMap, crdtDebugMetricsRef.current, crdtYTextDebugRegistryRef.current)
      }

      const syncManager = new CrdtWsSyncManager(doc)
      syncManager.setSessionId(sessionId)
      syncManager.attachDoc(doc)
      crdtSyncRef.current = syncManager

      crdtWsPathRef.current = info.ws_url

      const resolveCrdtWs = () =>
        resolveAuthenticatedCrdtWsUrl(crdtWsPathRef.current)

      void resolveCrdtWs().then((wsUrl) => {
        if (generation !== sessionGenerationRef.current) return

        if (CRDT_DEBUG) {
          crdtLog("log", "[crdt:url]", {
            crdtWsPath: info.ws_url,
            finalWsUrl: redactWsUrl(wsUrl),
          })
        }

        crdtLog("log", "connecting CRDT websocket (demo protocol)", { wsUrl: redactWsUrl(wsUrl) })
        console.log("[CRDT:Room] joining", {
          sessionId,
          wsUrl: redactWsUrl(wsUrl),
          urlCompilerSessionId: getCollaborationSessionIdFromUrl(),
          pageUrl: typeof window !== "undefined" ? window.location.href : null,
          hint: "Both browsers must use the SAME compilerSessionId query param (not just projectId)",
        })

        syncManager.connect(wsUrl, {
          isStale: () => generation !== sessionGenerationRef.current,
          sendFullStateOnOpen: false,
          awarenessWireEnabled: isAuthUserRef.current,
          resolveWsUrl: resolveCrdtWs,
          ...(CRDT_RUNTIME_DEBUG
            ? {
                WebSocketClass: createInstrumentedWebSocketClass(
                  WebSocket,
                  crdtDebugMetricsRef.current,
                ),
              }
            : {}),
        })

        // Solo tab is normal on first open — do not scream SPLIT ROOM.
        // Only warn when we expected a peer (URL already had compilerSessionId) but
        // nobody else appeared (other browser likely opened a different session).
        if (isAuthUserRef.current) {
          window.setTimeout(() => {
            if (generation !== sessionGenerationRef.current) return
            if (crdtSyncRef.current !== syncManager) return
            const peers = Math.max(0, syncManager.awareness.getStates().size - 1)
            console.log("[Collaboration] peer check after connect", {
              sessionId,
              peerCount: peers,
              pageUrl: window.location.href,
            })
            if (peers === 0 && getCollaborationSessionIdFromUrl()) {
              console.warn(
                "[Collaboration] Still alone in this compilerSessionId after 4s — " +
                  "open the other browser with this exact URL (including compilerSessionId).",
                { sessionId, pageUrl: window.location.href },
              )
            } else if (peers === 0) {
              console.info(
                "[Collaboration] Solo in room — share the URL (includes compilerSessionId) for multi-user sync.",
                { sessionId },
              )
            }
          }, 4000)
        }
      })

      const authLabel = readAuthUserLabel()
      const display = authLabel ?? `Guest-${doc.clientID}`
      void (async () => {
        if (isAuthUserRef.current) {
          const profileUser = await fetchAwarenessUserFromProfile()
          if (profileUser) {
            syncManager.setLocalAwarenessUser(profileUser)
            syncManager.broadcastLocalAwareness()
            return
          }
        }
        syncManager.setLocalAwarenessUser(buildAwarenessUserFallback(display, doc.clientID))
        syncManager.broadcastLocalAwareness()
      })()

      if (CRDT_RUNTIME_DEBUG) {
        const onAwarenessUpdate = () => {
          logAwarenessUpdate(crdtDebugMetricsRef.current, syncManager.awareness.getStates().size)
        }
        syncManager.awareness.on("update", onAwarenessUpdate)
        crdtProviderUnsubsRef.current.push(() => {
          syncManager.awareness.off("update", onAwarenessUpdate)
        })
      }

      // Peers publish pdfReady over awareness when their local compile finishes.
      // This is the reliable multi-browser path when compiler WS does not fan out.
      const onPeerPdfReadyAwareness = () => {
        if (generation !== sessionGenerationRef.current) return
        const sessionId = latestStateRef.current.sessionId
        if (!sessionId) return
        const localClientId = syncManager.awareness.clientID
        type PeerPdf = { t: number; compiledFile?: string | null }
        let best: PeerPdf | null = null
        for (const [clientId, state] of syncManager.awareness.getStates()) {
          if (clientId === localClientId) continue
          const raw = (state as { pdfReady?: unknown } | null)?.pdfReady
          if (!raw || typeof raw !== "object") continue
          const pdf = raw as { t?: unknown; sessionId?: unknown; compiledFile?: unknown }
          if (typeof pdf.sessionId === "string" && pdf.sessionId !== sessionId) continue
          const t = typeof pdf.t === "number" && Number.isFinite(pdf.t) ? pdf.t : null
          if (t == null) continue
          if (!best || t > best.t) {
            best = {
              t,
              compiledFile: typeof pdf.compiledFile === "string" ? pdf.compiledFile : null,
            }
          }
        }
        if (!best) return
        const peerPdf: PeerPdf = best
        if (peerPdf.t <= lastAppliedPdfCacheBustRef.current) return
        // Don't clobber an in-flight local compile with a peer's PDF.
        if (localCompilePendingRef.current) {
          pendingAwarenessPdfRef.current = {
            t: peerPdf.t,
            sessionId,
            compiledFile: peerPdf.compiledFile,
          }
          console.log("[Compile:PDF] awareness pdfReady queued — local compile pending", {
            peerT: peerPdf.t,
            lastApplied: lastAppliedPdfCacheBustRef.current,
          })
          return
        }
        pendingAwarenessPdfRef.current = null
        compilePdfTsLog("awareness:pdf_ready_recv", "peer pdfReady via CRDT awareness — applying PDF", {
          sessionId,
          cacheBustMs: peerPdf.t,
          compiledFile: peerPdf.compiledFile ?? null,
          lastAppliedPdfCacheBustMs: lastAppliedPdfCacheBustRef.current,
        })
        applySessionPdfRef.current(sessionId, {
          reason: "crdt awareness pdfReady — peer published PDF generation",
          origin: "remote",
          compiledFile: peerPdf.compiledFile,
          messageData: { t: peerPdf.t, awareness: true },
          backendTimes: { pdf_ready_at: new Date(peerPdf.t).toISOString() },
        })
      }
      syncManager.awareness.on("update", onPeerPdfReadyAwareness)
      crdtProviderUnsubsRef.current.push(() => {
        syncManager.awareness.off("update", onPeerPdfReadyAwareness)
      })

      syncManager.setHandlers({
        onStatus: (status) => {
          if (generation !== sessionGenerationRef.current) return
          crdtLog("log", "sync manager status:", status)
          if (status === "connected") {
            crdtWsConnectedAtRef.current = Date.now()
            logEvent("success", "[crdt] ws_open", syncManager.url)
          } else if (status === "disconnected") {
            if (CRDT_RUNTIME_DEBUG) crdtDebugMetricsRef.current.bumpReconnect()
            logEvent("warn", "[crdt] ws_disconnected", undefined)
          }
        },
        onSynced: () => {
          if (generation !== sessionGenerationRef.current) return
          crdtLog("log", "sync manager synced")
          logEvent("info", "[crdt] synced", `session=${sessionId}`)
          // Retry ops the observer could not settle (a move whose parent was
          // created later in the same journal). Do not mark an op seen unless
          // its path metadata was applied. Ops already applied during the sync
          // update stay marked and are not replayed.
          settlePendingStructureOpsRef.current()
          structureOpsLiveRef.current = true

          // Only seed when the shared map is completely empty. Never seed if another
          // peer is already in the room — both clients creating Y.Text for the same
          // path causes a Y.Map conflict and orphan MonacoBinding (Safari edits no-op on Chrome).
          if (options?.shouldSeed && filesMap.size === 0 && seedFiles.length > 0) {
            const filesWithContent = seedFiles.filter((f) => Boolean(f.content?.trim()))
            const peerCount = Math.max(0, syncManager.awareness.getStates().size - 1)
            if (filesWithContent.length > 0 && peerCount > 0) {
              logEvent(
                "info",
                "[crdt] skip seed — peers already present; requesting another sync",
                `peers=${peerCount}`,
              )
              syncManager.requestSyncFromServer()
            } else if (filesWithContent.length > 0) {
              // Brief delay so a simultaneously opening peer can appear in awareness
              // before we create Y.Text entries (avoids dual-seed map conflicts).
              window.setTimeout(() => {
                if (generation !== sessionGenerationRef.current) return
                const peersNow = Math.max(0, syncManager.awareness.getStates().size - 1)
                if (peersNow > 0) {
                  logEvent("info", "[crdt] deferred seed aborted — peer joined", `peers=${peersNow}`)
                  syncManager.requestSyncFromServer()
                  return
                }
                // Seed the CRDT content map by stable file_id, not by path. We never
                // push React-stale files into the doc — that resurrects deleted/renamed files.
                const toSeed = filesWithContent
                  .map((f) => ({ ...f, fileID: folderIdMapRef.current.get(f.path) }))
                  .filter((f) => Boolean(f.fileID) && !filesMap.has(f.fileID!))
                if (toSeed.length === 0) return
                logEvent(
                  "info",
                  "[crdt] seeding missing files into Y content map after sync",
                  `count=${toSeed.length} mapSizeBefore=${filesMap.size}`,
                )
                const seedOrigin = { kind: "seed" } satisfies SeedCrdtOrigin
                logTransact("seed-initial-files-delayed", seedOrigin, () => {
                  doc.transact(() => {
                    for (const file of toSeed) {
                      if (filesMap.has(file.fileID!)) continue
                      const ytext = new Y.Text()
                      ytext.insert(0, file.content)
                      filesMap.set(file.fileID!, ytext)
                    }
                  }, seedOrigin)
                })
                flushReactFromCrdtDoc()
                dispatch({ type: "CRDT_PROVIDER_SYNCED" })
              }, 400)
            }
          }

          flushReactFromCrdtDoc()
          dispatch({ type: "CRDT_PROVIDER_SYNCED" })
          if (CRDT_RUNTIME_DEBUG) {
            const t0 = crdtWsConnectedAtRef.current
            logYwsSync({
              synced: true,
              roomName: sessionId,
              filesMap: yFilesMapRef.current,
              sinceConnectMs: t0 ? Date.now() - t0 : undefined,
              metrics: crdtDebugMetricsRef.current,
            })
            primeYTextDebugRegistry(filesMap, crdtDebugMetricsRef.current, crdtYTextDebugRegistryRef.current)
          }
        },
        onOrphanYText: (path) => {
          if (generation !== sessionGenerationRef.current) return
          logEvent("warn", "[crdt] orphan Y.Text healed — forcing Monaco rebind", path)
          // Bump map revision so CodeEditorPanel destroys orphan binding and attaches to filesMap entry.
          dispatch({ type: "CRDT_MAP_STRUCTURE" })
        },
        onError: (event) => {
          if (generation !== sessionGenerationRef.current) return
          crdtLog("error", "sync manager connection error:", event)
        },
      })
    },
    [cleanupYjs, logEvent, updateFilesFromYjs, flushReactFromCrdtDoc, dispatch],
  )

  useEffect(() => {
    try {
      const cfg = getCompilerRuntimeConfig()
      configRef.current = cfg
      // Use empty apiBase — all calls go through local BFF (/api/compiler/*)
      apiClientRef.current = new CompilerApiClient()
      dispatch({ type: "CONFIG_READY" })
      logEvent("info", "Compiler config loaded", `template=${cfg.templateId}`)
    } catch (error) {
      const compilerError = toCompilerError(error, "CONFIG_ERROR")
      dispatch({ type: "CONFIG_ERROR", message: compilerError.message })
      logEvent("error", "Compiler config error", compilerError.message)
    }
  }, [logEvent])

  useEffect(() => {
    if (typeof window === "undefined") return
    window.localStorage.setItem(
      STORE_KEY,
      JSON.stringify({
        templateId: state.templateId,
        autoCompile: state.autoCompile,
        mainFile: state.mainFile,
      }),
    )
  }, [state.autoCompile, state.mainFile, state.templateId])

  useEffect(() => {
    return () => {
      sessionInitAbortRef.current?.abort()
      sessionInitAbortRef.current = null
      sessionInitInFlightRef.current = false
      if (sessionInitLockKeyRef.current && sessionInitOwnerRef.current) {
        releaseSessionInitLock(sessionInitLockKeyRef.current, sessionInitOwnerRef.current)
      }
      wsClientRef.current?.disconnect()
      cleanupYjs()
      Object.values(localEditTimersRef.current).forEach((timer) => window.clearTimeout(timer))
      if (autoCompileEditTimerRef.current != null) {
        window.clearTimeout(autoCompileEditTimerRef.current)
        autoCompileEditTimerRef.current = null
      }
      if (localCompileAckTimerRef.current != null) {
        window.clearTimeout(localCompileAckTimerRef.current)
        localCompileAckTimerRef.current = null
      }
      if (localCompileSoftRecoveryTimerRef.current != null) {
        window.clearTimeout(localCompileSoftRecoveryTimerRef.current)
        localCompileSoftRecoveryTimerRef.current = null
      }
      if (peerPdfSoftRefreshTimerRef.current != null) {
        window.clearTimeout(peerPdfSoftRefreshTimerRef.current)
        peerPdfSoftRefreshTimerRef.current = null
      }
    }
  }, [cleanupYjs])

  const clearLocalCompileRecovery = useCallback(() => {
    if (localCompileAckTimerRef.current != null) {
      window.clearTimeout(localCompileAckTimerRef.current)
      localCompileAckTimerRef.current = null
    }
    if (localCompileSoftRecoveryTimerRef.current != null) {
      window.clearTimeout(localCompileSoftRecoveryTimerRef.current)
      localCompileSoftRecoveryTimerRef.current = null
    }
  }, [])

  // Safety valve: if compileStatus is stuck at "compiling" for >60 s (backend never responded),
  // reset it to "error" so the Compile button becomes clickable again.
  useEffect(() => {
    if (state.compileStatus !== "compiling") return
    const timer = window.setTimeout(() => {
      if (latestStateRef.current.compileStatus === "compiling") {
        localCompilePendingRef.current = false
        clearLocalCompileRecovery()
        if (pdfReadyFallbackTimerRef.current != null) {
          clearTimeout(pdfReadyFallbackTimerRef.current)
          pdfReadyFallbackTimerRef.current = null
        }
        dispatch({ type: "COMPILE_ERROR", message: "Compile timed out — no response from backend" })
        logEvent("warn", "[lifecycle] compile timeout — reset compileStatus after 60 s")
      }
    }, 60_000)
    return () => window.clearTimeout(timer)
  }, [state.compileStatus, logEvent, clearLocalCompileRecovery])

  /** Align awareness with `auth_user` after login (same-tab focus) or cross-tab storage updates. */
  useEffect(() => {
    if (typeof window === "undefined") return
    let debounceTimer: number | null = null
    const refreshAwarenessUser = () => {
      const sync = crdtSyncRef.current
      const doc = yDocRef.current
      if (!sync || !doc) return
      void (async () => {
        if (isAuthUserRef.current) {
          clearAwarenessUserProfileCache()
          const profileUser = await fetchAwarenessUserFromProfile()
          if (profileUser) {
            sync.setLocalAwarenessUser(profileUser)
            sync.broadcastLocalAwareness()
            return
          }
        }
        const label = readAuthUserLabel() ?? `Guest-${doc.clientID}`
        sync.setLocalAwarenessUser(buildAwarenessUserFallback(label, doc.clientID))
        sync.broadcastLocalAwareness()
      })()
    }
    const scheduleRefresh = () => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer)
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null
        refreshAwarenessUser()
      }, 400)
    }
    const onAuthChange = () => scheduleRefresh()
    window.addEventListener("auth:session-changed", onAuthChange)
    window.addEventListener("focus", scheduleRefresh)
    return () => {
      window.removeEventListener("auth:session-changed", onAuthChange)
      window.removeEventListener("focus", scheduleRefresh)
      if (debounceTimer !== null) window.clearTimeout(debounceTimer)
    }
  }, [])

  /** Recover stale WebSocket connections when returning to a backgrounded tab (Safari/Chrome). */
  useEffect(() => {
    const wsConnectedAtRef = { compiler: 0, crdt: 0 }

    const recoverConnections = (reason: string) => {
      if (document.visibilityState === "hidden") {
        tabHiddenAtRef.current = Date.now()
        return
      }

      const current = latestStateRef.current
      if (!current.sessionId) return

      const hiddenMs = tabHiddenAtRef.current != null ? Date.now() - tabHiddenAtRef.current : 0
      tabHiddenAtRef.current = null

      const compilerWs = wsClientRef.current
      const crdtSync = crdtSyncRef.current
      const now = Date.now()
      const CONNECT_GRACE_MS = 5000

      console.log("[lifecycle] connection check", {
        reason,
        wsStatus: current.wsStatus,
        crdtStatus: crdtSync?.status,
        crdtSynced: crdtSync?.synced,
        hiddenMs,
      })

      // After backgrounding, CRDT can look "connected" but miss updates — pull from server.
      if (hiddenMs >= 1500) {
        console.log("[CRDT:Sync] tab visible after background — requesting document resync", { hiddenMs })
        if (crdtSync?.status === "connected") {
          crdtSync.requestSyncFromServer()
        } else if (crdtWsPathRef.current) {
          crdtSync?.reconnect()
        }
        flushReactFromCrdtDoc()
        if (hiddenMs >= 5000 && compilerWsUrlRef.current && current.wsStatus === "connected") {
          console.log("[Compiler:WS] reconnect after long background")
          wsConnectedAtRef.compiler = now
          compilerWs?.reconnect()
        }
      }

      // Only recover truly dead sockets — never interrupt an in-flight connect.
      if (
        current.wsStatus === "disconnected" ||
        current.wsStatus === "reconnecting"
      ) {
        if (compilerWsUrlRef.current && now - wsConnectedAtRef.compiler > CONNECT_GRACE_MS) {
          console.log("[lifecycle] recovering compiler WS", current.wsStatus)
          wsConnectedAtRef.compiler = now
          compilerWs?.reconnect()
        }
      }

      if (crdtSync?.status === "disconnected" && crdtWsPathRef.current) {
        if (now - wsConnectedAtRef.crdt > CONNECT_GRACE_MS) {
          console.log("[lifecycle] recovering CRDT WS", crdtSync.status)
          wsConnectedAtRef.crdt = now
          crdtSync.reconnect()
        }
      } else if (crdtSync?.status === "connected") {
        wsConnectedAtRef.crdt = now
        crdtSync.broadcastLocalAwareness()
      }
    }

    const onVisibility = () => recoverConnections("visibilitychange")
    const onPageShow = () => recoverConnections("pageshow")

    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("pageshow", onPageShow)
    return () => {
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("pageshow", onPageShow)
    }
  }, [flushReactFromCrdtDoc])

  const sendWs = useCallback((envelope: OutgoingEnvelope) => {
    const sent = wsClientRef.current?.send(envelope) ?? false
    if (!sent) logEvent("warn", "WebSocket send skipped", envelope.type)
    return sent
  }, [logEvent])

  const clearPdfReadyFallback = useCallback(() => {
    if (pdfReadyFallbackTimerRef.current != null) {
      clearTimeout(pdfReadyFallbackTimerRef.current)
      pdfReadyFallbackTimerRef.current = null
    }
  }, [])

  /**
   * Apply a session PDF for EVERY collaborator on this compiler WS.
   * Uses backend timestamps for ?t= when present so all tabs fetch the same generation.
   */
  const applySessionPdf = useCallback((
    sessionId: string,
    options: {
      reason: string
      origin: "local" | "remote"
      pdfUrlFromBackend?: string | null
      compiledFile?: string | null
      backendTimes?: Record<string, unknown>
      messageData?: Record<string, unknown> | null
    },
  ) => {
    const current = latestStateRef.current
    if (!current.sessionId || current.sessionId !== sessionId) {
      compilePdfTsLog("apply_pdf_skipped", "PDF apply skipped — session mismatch", {
        expectedSessionId: sessionId,
        currentSessionId: current.sessionId ?? null,
        reason: options.reason,
        origin: options.origin,
      })
      return
    }

    clearPdfReadyFallback()
    clearLocalCompileRecovery()
    if (peerPdfSoftRefreshTimerRef.current != null) {
      window.clearTimeout(peerPdfSoftRefreshTimerRef.current)
      peerPdfSoftRefreshTimerRef.current = null
    }
    const wasLocalPending = localCompilePendingRef.current
    if (wasLocalPending) localCompilePendingRef.current = false
    pendingWsPdfRef.current = null

    const { cacheBustMs, source: cacheBustSource } = resolvePdfCacheBustMs({
      backendTimes: options.backendTimes,
      messageData: options.messageData,
      pdfUrlFromBackend: options.pdfUrlFromBackend,
    })

    if (cacheBustMs < lastAppliedPdfCacheBustRef.current) {
      compilePdfTsLog("apply_pdf_skipped", "PDF apply skipped — older than last applied cache-bust", {
        sessionId,
        origin: options.origin,
        reason: options.reason,
        cacheBustMs,
        cacheBustIso: new Date(cacheBustMs).toISOString(),
        cacheBustSource,
        lastAppliedPdfCacheBustMs: lastAppliedPdfCacheBustRef.current,
        lastAppliedPdfCacheBustIso: new Date(lastAppliedPdfCacheBustRef.current).toISOString(),
        pdfUrlFromBackend: options.pdfUrlFromBackend ?? null,
      })
      console.warn("[Compile:PDF] ignoring stale pdf event", {
        origin: options.origin,
        cacheBustMs,
        lastApplied: lastAppliedPdfCacheBustRef.current,
      })
      return
    }
    lastAppliedPdfCacheBustRef.current = cacheBustMs
    lastAppliedPdfAtRef.current = Date.now()

    // Always fetch via our BFF so auth cookies apply; ?t= is the generation key.
    const pdfUrl = `/api/compiler/sessions/${encodeURIComponent(sessionId)}/pdf?t=${cacheBustMs}`
    const isAuth = hasAuthSession()
    const compiledFile =
      options.compiledFile
      ?? (isAuth
        ? (lastTriggeredCompileFileRef.current
           ?? (current.activeFile?.endsWith(".tex") ? current.activeFile : null)
           ?? current.mainFile
           ?? current.activeFile)
        : (current.mainFile || "main.tex"))
    if (wasLocalPending) lastTriggeredCompileFileRef.current = null

    const trigger = lastCompileTriggerTsRef.current
    const fileChange = lastFileChangeTsRef.current
    const cycleStart = compileCycleStartedAtRef.current
    compilePdfTsLog("apply_pdf", options.reason, {
      sessionId,
      origin: options.origin,
      wasLocalPending,
      pdfUrl,
      pdfUrlFromBackend: options.pdfUrlFromBackend ?? null,
      pdfCacheBustMs: cacheBustMs,
      pdfCacheBustIso: new Date(cacheBustMs).toISOString(),
      cacheBustSource,
      compiledFile: compiledFile ?? null,
      previousPdfUrl: current.pdfUrl ?? null,
      previousPdfCacheBustMs: parsePdfCacheBust(current.pdfUrl),
      lastFileChangeIso: fileChange?.iso ?? null,
      lastCompileTriggerIso: trigger?.iso ?? null,
      msSinceFileChange: fileChange ? cacheBustMs - fileChange.epochMs : null,
      msSinceCompileTrigger: trigger ? cacheBustMs - trigger.epochMs : null,
      msSinceCompileCycleStart: cycleStart != null ? cacheBustMs - cycleStart : null,
      backendTimes: options.backendTimes ?? null,
      messageData: options.messageData ?? null,
      note: "All WS peers should apply pdf_ready; ?t= prefers backend time so everyone hits the same PDF generation",
    })
    console.log("[Compile:PDF] applying session PDF", {
      origin: options.origin,
      reason: options.reason,
      pdfUrl,
      cacheBustMs,
      cacheBustSource,
      compiledFile,
      backendPdfUrl: options.pdfUrlFromBackend ?? null,
    })
    logEvent("info", `[lifecycle] ${options.reason}`, pdfUrl)
    logEvent("info", "[lifecycle] pdf_fetch_start", pdfUrl)
    dispatch({ type: "COMPILE_SUCCESS", pdfUrl, compiledFile: compiledFile ?? undefined })

    // Fan-out PDF generation over CRDT awareness so N peers refresh even when
    // compiler WS fails to deliver pdf_ready (common across browsers / tabs).
    if (options.origin === "local") {
      crdtSyncRef.current?.setLocalAwarenessPdfReady({
        t: cacheBustMs,
        sessionId,
        compiledFile: compiledFile ?? null,
      })
      compilePdfTsLog("awareness:pdf_ready", "published pdfReady on CRDT awareness for peers", {
        sessionId,
        cacheBustMs,
        compiledFile: compiledFile ?? null,
      })
    }

    // Apply peer PDF that arrived while we were compiling, if it's newer.
    const queued = pendingAwarenessPdfRef.current
    if (
      queued
      && queued.sessionId === sessionId
      && queued.t > lastAppliedPdfCacheBustRef.current
      && !localCompilePendingRef.current
    ) {
      pendingAwarenessPdfRef.current = null
      queueMicrotask(() => {
        applySessionPdfRef.current(queued.sessionId, {
          reason: "crdt awareness pdfReady — applied after local compile finished",
          origin: "remote",
          compiledFile: queued.compiledFile,
          messageData: { t: queued.t, awareness: true },
          backendTimes: { pdf_ready_at: new Date(queued.t).toISOString() },
        })
      })
    }
  }, [clearPdfReadyFallback, clearLocalCompileRecovery, logEvent])
  applySessionPdfRef.current = applySessionPdf

  const armLocalCompileRecovery = useCallback((sessionId: string) => {
    clearLocalCompileRecovery()
    localCompileAckTimerRef.current = window.setTimeout(() => {
      localCompileAckTimerRef.current = null
      if (!localCompilePendingRef.current || localCompileResponseSeenRef.current) return
      if (latestStateRef.current.sessionId !== sessionId) return
      console.warn("[Compiler:WS] no compile ack after trigger — reconnecting half-open socket", {
        sessionId,
        msSinceTrigger: lastCompileTriggerTsRef.current
          ? Date.now() - lastCompileTriggerTsRef.current.epochMs
          : null,
        lastRecvAgeMs: lastCompilerWsRecvAtRef.current
          ? Date.now() - lastCompilerWsRecvAtRef.current
          : null,
      })
      compilePdfTsLog("ws:local_ack_timeout", "no compile_* recv — reconnecting compiler WS", {
        sessionId,
        lastCompileTriggerIso: lastCompileTriggerTsRef.current?.iso ?? null,
      })
      if (compilerWsUrlRef.current) {
        dispatch({ type: "WS_STATUS", status: "connecting" })
        void wsClientRef.current?.reconnectAndWait(8000)
      }
    }, LOCAL_COMPILE_ACK_TIMEOUT_MS)

    localCompileSoftRecoveryTimerRef.current = window.setTimeout(() => {
      localCompileSoftRecoveryTimerRef.current = null
      if (!localCompilePendingRef.current) return
      const latest = latestStateRef.current
      if (latest.sessionId !== sessionId) return
      const pending = pendingWsPdfRef.current
      const fromPendingUrl = pending?.pdfUrlFromBackend
        ? (() => {
            try {
              const u = new URL(pending.pdfUrlFromBackend, "http://local")
              const t = Number(u.searchParams.get("t"))
              return Number.isFinite(t) ? t : null
            } catch {
              return null
            }
          })()
        : null
      const cacheBustMs = fromPendingUrl ?? lastAppliedPdfCacheBustRef.current
      if (!cacheBustMs || cacheBustMs <= 0) {
        console.warn("[Compile:PDF] local compile soft recovery skipped — no known PDF generation")
        return
      }
      console.warn("[Compile:PDF] local compile soft recovery — PDF fetch without WS pdf_ready", {
        sessionId,
        responseSeen: localCompileResponseSeenRef.current,
        previousPdfCacheBustMs: lastAppliedPdfCacheBustRef.current,
        cacheBustMs,
      })
      compilePdfTsLog("apply_pdf", "local compile soft recovery (no WS pdf_ready on triggerer)", {
        sessionId,
        origin: "local",
        responseSeen: localCompileResponseSeenRef.current,
        cacheBustMs,
      })
      applySessionPdf(sessionId, {
        reason: "local compile soft recovery — triggerer missed WS fan-out; fetching latest PDF",
        origin: "local",
        pdfUrlFromBackend: pending?.pdfUrlFromBackend,
        compiledFile: pending?.compiledFile,
        backendTimes: pending?.backendTimes ?? { recovered_at: new Date(cacheBustMs).toISOString() },
        messageData: { soft_recovery: true, t: cacheBustMs },
      })
    }, LOCAL_COMPILE_SOFT_RECOVERY_MS)
  }, [applySessionPdf, clearLocalCompileRecovery])

  const triggerCompile = useCallback(async (triggerReason = "ws_compile_trigger") => {
    clearPdfReadyFallback()
    clearLocalCompileRecovery()
    if (autoCompileEditTimerRef.current != null) {
      window.clearTimeout(autoCompileEditTimerRef.current)
      autoCompileEditTimerRef.current = null
    }
    const current = latestStateRef.current
    compileCycleStartedAtRef.current = Date.now()
    compilePdfTsLog("trigger", "compile requested", {
      triggerReason,
      sessionId: current.sessionId ?? null,
      wsStatus: current.wsStatus,
      compileStatus: current.compileStatus,
      crdtSync: crdtSyncRef.current?.getDocSyncTimestamps?.() ?? null,
      crdtSynced: crdtSyncRef.current?.synced ?? null,
    })
    if (!current.sessionId) {
      logEvent("warn", "[lifecycle] ws_send compile_trigger blocked", `sessionId=none wsStatus=${current.wsStatus}`)
      return
    }

    const recvAgeMs = lastCompilerWsRecvAtRef.current
      ? Date.now() - lastCompilerWsRecvAtRef.current
      : Number.POSITIVE_INFINITY
    const socketOpen = wsClientRef.current?.isConnected() === true
    const needsReconnect =
      current.wsStatus !== "connected"
      || !socketOpen
      || recvAgeMs > COMPILER_WS_RECV_STALE_MS

    if (needsReconnect) {
      console.warn("[lifecycle] compile_trigger: refreshing compiler WS before send", {
        wsStatus: current.wsStatus,
        triggerReason,
        socketOpen,
        recvAgeMs: Number.isFinite(recvAgeMs) ? recvAgeMs : null,
        reason: !socketOpen || current.wsStatus !== "connected" ? "not_connected" : "recv_stale",
      })
      dispatch({ type: "WS_STATUS", status: "connecting" })
      const connected = compilerWsUrlRef.current
        ? await (wsClientRef.current?.reconnectAndWait(8000) ?? Promise.resolve(false))
        : false
      if (!connected || !wsClientRef.current?.isConnected()) {
        logEvent("warn", "[lifecycle] ws_send compile_trigger blocked", `sessionId=${current.sessionId ?? "none"} wsStatus=${latestStateRef.current.wsStatus}`)
        return
      }
    }

    // Wait until CRDT websocket has applied initial sync (or timeout) so compile uses latest Yjs-backed state.
    let synced = await waitForCrdtSync(current.sessionId, sessionGenerationRef.current)
    if (!synced && crdtSyncRef.current?.status === "disconnected") {
      console.warn("[CRDT:Sync] compile_trigger: sync timeout — attempting CRDT reconnect")
      crdtSyncRef.current?.reconnect()
      synced = await waitForCrdtSync(current.sessionId, sessionGenerationRef.current, 5000)
    }
    if (!synced) {
      logEvent("warn", "[crdt] compile_trigger: Y provider not fully synced within timeout", undefined)
    }
    flushReactFromCrdtDoc()
    const isAuth = hasAuthSession()
    // Never compile a deleted/renamed-away path — a stale activeFile or
    // session main_file pointing at one produces a phantom empty target.
    const pickTex = (p?: string | null): string | null =>
      p && p.endsWith(".tex") && !isPathDeleted(p) ? p : null
    const rawTex =
      (isAuth
        ? (pickTex(current.activeFile) ?? pickTex(current.mainFile) ?? null)
        : (current.mainFile || null))
      || "main.tex"
    const texFile = normalizePath(rawTex) || "main.tex"

    if (!texFile.endsWith(".tex")) {
      const msg = `Cannot compile "${texFile}" — pick a .tex file`
      logEvent("warn", "[compile] blocked — non-tex target", msg)
      if (triggerReason !== "auto_compile_on_edit") {
        dispatch({ type: "COMPILE_ERROR", message: msg })
        toast.error(msg)
      }
      return
    }

    if (!isAuth && current.activeFile && current.activeFile !== texFile) {
      const msg = `Only the main file (${basename(texFile)}) can be compiled directly.`
      dispatch({ type: "COMPILE_ERROR", message: msg })
      logEvent("warn", `[compile] blocked - non-auth active file is not main file`, msg)
      toast.error(msg)
      return
    }

    // Guard: if the target file has no content, reject early so the backend
    // cannot fall back to a different file and produce a misattributed PDF.
    const readTexContent = (): string => {
      const fileID = folderIdMapRef.current.get(texFile) ?? texFile
      const ytextEntry = yFilesMapRef.current?.get(fileID)
      if (ytextEntry?.toString().trim()) return ytextEntry.toString()
      // Fallback: any .tex in the Y content map (resolve fileID back to current path).
      const filesMap = yFilesMapRef.current
      if (filesMap) {
        let found = ""
        folderIdMapRef.current.forEach((id, path) => {
          if (found || !path.endsWith(".tex")) return
          const yt = filesMap.get(id)
          if (!yt) return
          const s = yt.toString()
          if (s.trim()) found = s
        })
        if (found) return found
      }
      return latestStateRef.current.files[texFile]?.content ?? ""
    }

    let fileContent = readTexContent()
    if (!fileContent.trim()) {
      const isInitialAuto = triggerReason === "ws_compile_trigger"
      const waitMs = isInitialAuto ? 5000 : 1500
      const waitStart = Date.now()
      while (Date.now() - waitStart < waitMs) {
        fileContent = readTexContent()
        if (fileContent.trim()) break
        await new Promise((r) => setTimeout(r, 100))
      }
    }

    const name = basename(texFile) || texFile || "main.tex"
    if (!fileContent.trim()) {
      const msg = `${name} is empty — add LaTeX content before compiling`
      // Remote-echo / peer-edit auto-compile should stay quiet — peer owns compile+PDF.
      if (triggerReason === "ws_compile_trigger" || triggerReason === "auto_compile_on_edit") {
        logEvent("warn", `[compile] deferred — ${texFile} still empty`, msg)
        console.warn("[Compile:PDF] skipping empty-file compile", { triggerReason, texFile })
        return
      }
      dispatch({ type: "COMPILE_ERROR", message: msg })
      logEvent("warn", `[compile] blocked — ${texFile} is empty`, msg)
      toast.error(msg)
      return
    }

    // Guard: file must have \documentclass to be compilable as a standalone document.
    // Without it the backend cannot compile it and may silently fall back to main.tex,
    // showing the wrong PDF with no visible error.
    if (!fileContent.includes("\\documentclass")) {
      const msg = `${name} is missing \\documentclass — it cannot be compiled as a standalone document`
      dispatch({ type: "COMPILE_ERROR", message: msg })
      logEvent("warn", `[compile] blocked — ${texFile} missing \\documentclass`, msg)
      toast.error(msg, { duration: 6000 })
      return
    }

    // Record which file we're compiling so compile_success attribution is correct
    // even if the user switches tabs while the backend is compiling.
    lastTriggeredCompileFileRef.current = texFile

    const ensureCompilerWsReady = async (): Promise<boolean> => {
      if (wsClientRef.current?.isConnected()) return true
      console.warn("[lifecycle] compiler WS not open — reconnecting before compile send")
      dispatch({ type: "WS_STATUS", status: "connecting" })
      const ok = await (wsClientRef.current?.reconnectAndWait(8000) ?? Promise.resolve(false))
      return ok && wsClientRef.current?.isConnected() === true
    }

    if (!(await ensureCompilerWsReady())) {
      logEvent("warn", "[lifecycle] ws_send file_change blocked — compiler WS down", `sessionId=${current.sessionId}`)
      lastTriggeredCompileFileRef.current = null
      return
    }

    // Force-push the active file's content (and compilation target if different) via file_change before compile_trigger so the
    // backend has the latest content even if the CRDT debounce hasn't fired yet.
    // This is especially important for newly created files that may not have been
    // sent over the compile WS yet.
    if (current.activeFile && current.activeFile !== texFile) {
      const activeFileID = folderIdMapRef.current.get(current.activeFile) ?? current.activeFile
      const activeEntry = yFilesMapRef.current?.get(activeFileID)
      const activeContent = activeEntry ? activeEntry.toString() : (current.files[current.activeFile]?.content ?? "")
      const activeTs = nowIso()
      sendWs({ type: "file_change", data: { path: current.activeFile, content: activeContent }, timestamp: activeTs })
      compilePdfTsLog("ws:file_change", "sent companion file_change before compile", {
        sessionId: current.sessionId,
        path: current.activeFile,
        envelopeTimestamp: activeTs,
        contentChars: activeContent.length,
      })
    }
    const fileChangeIso = nowIso()
    const fileChangeEpochMs = Date.now()
    lastFileChangeTsRef.current = { iso: fileChangeIso, epochMs: fileChangeEpochMs }

    sendWs({ type: "file_change", data: { path: texFile, content: fileContent }, timestamp: fileChangeIso })
    compilePdfTsLog("ws:file_change", "sent file_change before compile_trigger", {
      sessionId: current.sessionId,
      path: texFile,
      envelopeTimestamp: fileChangeIso,
      envelopeEpochMsApprox: fileChangeEpochMs,
      contentChars: fileContent.length,
      crdtSync: crdtSyncRef.current?.getDocSyncTimestamps?.() ?? null,
      note: "envelope.timestamp is ISO; backend may compare this to CRDT update time",
    })

    // Give the backend a moment to process the file_change before we trigger compile.
    await new Promise<void>((resolve) => setTimeout(resolve, 300))

    // Re-read state in case session was torn down during the delay.
    if (latestStateRef.current.sessionId !== current.sessionId) {
      logEvent("warn", "[lifecycle] session changed during pre-compile delay, aborting", `sessionId=${current.sessionId}`)
      lastTriggeredCompileFileRef.current = null
      localCompilePendingRef.current = false
      return
    }

    // Socket may have been swapped during the delay (async URL resolve / background reconnect).
    const wasOpenBeforeDelayCheck = wsClientRef.current?.isConnected() === true
    if (!(await ensureCompilerWsReady())) {
      logEvent("warn", "[lifecycle] compile_trigger not sent — compiler WS down after delay", `sessionId=${current.sessionId}`)
      lastTriggeredCompileFileRef.current = null
      localCompilePendingRef.current = false
      return
    }
    // If the socket was dead/replaced after file_change, re-push content on the live socket.
    if (!wasOpenBeforeDelayCheck) {
      const retryFcIso = nowIso()
      sendWs({ type: "file_change", data: { path: texFile, content: fileContent }, timestamp: retryFcIso })
      compilePdfTsLog("ws:file_change", "re-sent file_change after WS recover", {
        sessionId: current.sessionId,
        path: texFile,
        envelopeTimestamp: retryFcIso,
        contentChars: fileContent.length,
      })
      await new Promise<void>((resolve) => setTimeout(resolve, 200))
      if (!(await ensureCompilerWsReady())) {
        logEvent("warn", "[lifecycle] compile_trigger not sent — compiler WS down after file_change retry", `sessionId=${current.sessionId}`)
        lastTriggeredCompileFileRef.current = null
        return
      }
    }

    logEvent("info", `[compile] compiling: ${texFile}`, `sessionId=${current.sessionId}`)
    const compileIso = nowIso()
    const compileEpochMs = Date.now()
    lastCompileTriggerTsRef.current = { iso: compileIso, epochMs: compileEpochMs }
    let sent = sendWs({ type: "compile_trigger", data: { main_file: texFile }, timestamp: compileIso })
    if (!sent) {
      // One recovery attempt: reconnect, re-push file, then trigger.
      console.warn("[lifecycle] compile_trigger send failed — reconnect + retry once")
      if (await ensureCompilerWsReady()) {
        const retryFcIso = nowIso()
        sendWs({ type: "file_change", data: { path: texFile, content: fileContent }, timestamp: retryFcIso })
        await new Promise<void>((resolve) => setTimeout(resolve, 200))
        if (await ensureCompilerWsReady()) {
          sent = sendWs({ type: "compile_trigger", data: { main_file: texFile }, timestamp: nowIso() })
        }
      }
    }
    compilePdfTsLog("ws:compile_trigger", sent ? "compile_trigger sent" : "compile_trigger NOT sent", {
      sessionId: current.sessionId,
      texFile,
      triggerReason,
      envelopeTimestamp: compileIso,
      envelopeEpochMsApprox: compileEpochMs,
      msAfterFileChange: fileChangeEpochMs ? compileEpochMs - fileChangeEpochMs : null,
      fileChangeEnvelopeTimestamp: fileChangeIso,
      preCompileDelayMs: 300,
      crdtSync: crdtSyncRef.current?.getDocSyncTimestamps?.() ?? null,
      contentChars: fileContent.length,
      sent,
    })
    if (!sent) {
      logEvent("warn", "[lifecycle] compile_trigger not sent — WS closed during sync wait, please retry", `sessionId=${current.sessionId}`)
      lastTriggeredCompileFileRef.current = null
      localCompilePendingRef.current = false
      return
    }
    localCompilePendingRef.current = true
    localCompileResponseSeenRef.current = false
    dispatch({ type: "COMPILE_START" })
    armLocalCompileRecovery(current.sessionId)
    logEvent("info", "[lifecycle] ws_send compile_trigger", `sessionId=${current.sessionId}`)
  }, [
    logEvent,
    sendWs,
    waitForCrdtSync,
    flushReactFromCrdtDoc,
    clearPdfReadyFallback,
    clearLocalCompileRecovery,
    armLocalCompileRecovery,
    isPathDeleted,
  ])
  triggerCompileRef.current = triggerCompile

  const scheduleAutoCompileOnEdit = useCallback(() => {
    const current = latestStateRef.current
    if (!current.autoCompile || !current.sessionId || current.wsStatus !== "connected") return
    if (Date.now() < remoteEditGuardUntilRef.current) {
      console.log("[Compile:PDF] skip auto-compile — remote CRDT guard active")
      schedulePeerPdfSoftRefreshRef.current()
      return
    }

    if (autoCompileEditTimerRef.current != null) {
      window.clearTimeout(autoCompileEditTimerRef.current)
    }

    autoCompileEditTimerRef.current = window.setTimeout(() => {
      autoCompileEditTimerRef.current = null
      const latest = latestStateRef.current
      if (!latest.autoCompile || !latest.sessionId || latest.wsStatus !== "connected") return
      if (Date.now() < remoteEditGuardUntilRef.current) {
        console.log("[Compile:PDF] skip auto-compile fire — remote CRDT guard active")
        schedulePeerPdfSoftRefreshRef.current()
        return
      }
      if (latest.compileStatus === "compiling" && localCompilePendingRef.current) {
        scheduleAutoCompileOnEditRef.current()
        return
      }
      compilePdfTsLog("auto_fire", "auto-compile debounce fired", {
        sessionId: latest.sessionId,
        debounceMs: AUTO_COMPILE_DEBOUNCE_MS,
        crdtSync: crdtSyncRef.current?.getDocSyncTimestamps?.() ?? null,
      })
      logEvent("info", "[compile] auto-compile after edit (debounced)")
      void triggerCompileRef.current("auto_compile_on_edit")
    }, AUTO_COMPILE_DEBOUNCE_MS)
  }, [logEvent])
  scheduleAutoCompileOnEditRef.current = scheduleAutoCompileOnEdit

  /** When peer edits arrive over CRDT but compiler WS doesn't fan out pdf_ready, refresh PDF after idle. */
  const schedulePeerPdfSoftRefresh = useCallback(() => {
    const current = latestStateRef.current
    if (!current.sessionId || !current.autoCompile) return
    if (peerPdfSoftRefreshTimerRef.current != null) {
      window.clearTimeout(peerPdfSoftRefreshTimerRef.current)
    }
    peerPdfSoftRefreshTimerRef.current = window.setTimeout(() => {
      peerPdfSoftRefreshTimerRef.current = null
      const latest = latestStateRef.current
      if (!latest.sessionId) return
      if (localCompilePendingRef.current) {
        console.log("[Compile:PDF] peer soft PDF refresh skipped — local compile pending")
        return
      }
      // Awareness (or WS pdf_ready) already refreshed recently — avoid mid-race soft fetch.
      if (Date.now() - lastAppliedPdfAtRef.current < 4000) {
        console.log("[Compile:PDF] peer soft PDF refresh skipped — PDF applied recently", {
          msSinceApply: Date.now() - lastAppliedPdfAtRef.current,
        })
        return
      }

      // Never invent Date.now() as ?t= — that caused 404s when a peer compile cleared
      // the PDF mid-flight. Only re-fetch a generation we already know exists.
      let cacheBustMs = lastAppliedPdfCacheBustRef.current
      const sync = crdtSyncRef.current
      if (sync) {
        const localId = sync.awareness.clientID
        for (const [clientId, state] of sync.awareness.getStates()) {
          if (clientId === localId) continue
          const raw = (state as { pdfReady?: unknown } | null)?.pdfReady
          if (!raw || typeof raw !== "object") continue
          const pdf = raw as { t?: unknown; sessionId?: unknown }
          if (typeof pdf.sessionId === "string" && pdf.sessionId !== latest.sessionId) continue
          const t = typeof pdf.t === "number" && Number.isFinite(pdf.t) ? pdf.t : null
          if (t != null && t > cacheBustMs) cacheBustMs = t
        }
      }
      if (cacheBustMs <= 0) {
        console.log("[Compile:PDF] peer soft PDF refresh skipped — no known PDF generation yet")
        return
      }
      if (cacheBustMs <= lastAppliedPdfCacheBustRef.current) {
        console.log("[Compile:PDF] peer soft PDF refresh skipped — already at this generation", {
          cacheBustMs,
        })
        return
      }
      console.log("[Compile:PDF] peer soft PDF refresh — re-fetch known generation", {
        sessionId: latest.sessionId,
        cacheBustMs,
      })
      applySessionPdf(latest.sessionId, {
        reason: "peer CRDT idle — soft PDF refresh (compiler WS may not have fanned out pdf_ready)",
        origin: "remote",
        messageData: { soft_refresh: true, t: cacheBustMs },
        backendTimes: { pdf_ready_at: new Date(cacheBustMs).toISOString() },
      })
    }, PEER_PDF_SOFT_REFRESH_MS)
  }, [applySessionPdf])
  schedulePeerPdfSoftRefreshRef.current = schedulePeerPdfSoftRefresh

  const handleIncomingMessage = useCallback((message: IncomingMessage) => {
    const current = latestStateRef.current
    const backendTimes = extractBackendTimeFields(message.data)
    const trigger = lastCompileTriggerTsRef.current
    const nowMs = Date.now()
    lastCompilerWsRecvAtRef.current = nowMs
    if (
      localCompilePendingRef.current
      && (message.type === "compile_start"
        || message.type === "compile_success"
        || message.type === "compile_error"
        || message.type === "pdf_ready")
    ) {
      localCompileResponseSeenRef.current = true
      if (localCompileAckTimerRef.current != null) {
        window.clearTimeout(localCompileAckTimerRef.current)
        localCompileAckTimerRef.current = null
      }
    }
    const isLocalCompile = localCompilePendingRef.current

    const compiledFileFromData = (data: Record<string, unknown> | undefined): string | undefined => {
      const raw =
        (typeof data?.compiled_file === "string" && data.compiled_file)
        || (typeof data?.main_file === "string" && data.main_file)
        || (typeof data?.path === "string" && String(data.path).endsWith(".tex") ? data.path : null)
      return raw ? normalizePath(raw) : undefined
    }

    switch (message.type) {
      case "compile_start": {
        console.log("[Compile:PDF] ws compile_start", {
          origin: isLocalCompile ? "local" : "remote",
          sessionId: current.sessionId ?? null,
          backendTimes,
          messageData: message.data ?? null,
        })
        compilePdfTsLog(
          isLocalCompile ? "ws:compile_start" : "ws:compile_start_remote",
          isLocalCompile ? "received compile_start (this tab triggered)" : "received compile_start (peer triggered) — showing compiling UI",
          {
            sessionId: current.sessionId ?? null,
            backendTimes,
            messageData: message.data ?? null,
            lastCompileTriggerIso: trigger?.iso ?? null,
            msSinceCompileTrigger: trigger ? nowMs - trigger.epochMs : null,
            isLocalCompile,
          },
        )
        // All collaborators see "compiling" while a session compile runs.
        dispatch({ type: "COMPILE_START" })
        logEvent("info", "[lifecycle] ws_recv compile_start", `sessionId=${current.sessionId ?? "none"} local=${isLocalCompile}`)
        return
      }
      case "compile_success": {
        if (!current.sessionId) return
        const pdfUrlFromBackend = typeof message.data?.pdf_url === "string" ? message.data.pdf_url : undefined
        const compiledFile = compiledFileFromData(message.data as Record<string, unknown> | undefined)
        pendingWsPdfRef.current = {
          pdfUrlFromBackend,
          compiledFile,
          backendTimes,
          messageData: (message.data as Record<string, unknown> | undefined) ?? null,
        }
        console.log("[Compile:PDF] ws compile_success — waiting for pdf_ready", {
          origin: isLocalCompile ? "local" : "remote",
          sessionId: current.sessionId,
          pdfUrlFromBackend: pdfUrlFromBackend ?? null,
          compiledFile: compiledFile ?? null,
          backendTimes,
          messageData: message.data ?? null,
        })
        compilePdfTsLog(
          isLocalCompile ? "ws:compile_success" : "ws:compile_success_remote",
          "received compile_success — waiting pdf_ready (3s fallback) for ALL peers",
          {
            sessionId: current.sessionId,
            origin: isLocalCompile ? "local" : "remote",
            backendTimes,
            messageData: message.data ?? null,
            lastCompileTriggerIso: trigger?.iso ?? null,
            msSinceCompileTrigger: trigger ? nowMs - trigger.epochMs : null,
            pdfUrlFromBackend: pdfUrlFromBackend ?? null,
            compiledFile: compiledFile ?? null,
          },
        )
        logEvent("success", "[lifecycle] ws_recv compile_success", `sessionId=${current.sessionId} local=${isLocalCompile}`)
        // Backend may emit pdf_ready after compile_success — wait, then apply for every peer.
        clearPdfReadyFallback()
        const successSessionId = current.sessionId
        pdfReadyFallbackTimerRef.current = setTimeout(() => {
          const pending = pendingWsPdfRef.current
          compilePdfTsLog("fallback_fire", "pdf_ready missing after 3000ms — applying PDF URL anyway", {
            sessionId: successSessionId,
            origin: localCompilePendingRef.current ? "local" : "remote",
            lastCompileTriggerIso: lastCompileTriggerTsRef.current?.iso ?? null,
            msSinceCompileTrigger: lastCompileTriggerTsRef.current
              ? Date.now() - lastCompileTriggerTsRef.current.epochMs
              : null,
            pending,
          })
          console.warn("[Compile:PDF] pdf_ready timeout — applying PDF from compile_success fallback", {
            sessionId: successSessionId,
            pending,
          })
          applySessionPdf(successSessionId, {
            reason: "pdf fetch fallback after compile_success (no pdf_ready)",
            origin: localCompilePendingRef.current ? "local" : "remote",
            pdfUrlFromBackend: pending?.pdfUrlFromBackend,
            compiledFile: pending?.compiledFile,
            backendTimes: pending?.backendTimes,
            messageData: pending?.messageData,
          })
        }, 3000)
        return
      }
      case "compile_error": {
        // Only the tab that triggered compile should surface the error toast.
        if (!isLocalCompile) {
          console.log("[Compile:PDF] ws compile_error ignored for UI (peer compile)", {
            sessionId: current.sessionId ?? null,
            messageData: message.data ?? null,
          })
          compilePdfTsLog("ws:compile_error_remote", "peer compile_error — not toasting on this tab", {
            sessionId: current.sessionId ?? null,
            backendTimes,
            messageData: message.data ?? null,
          })
          // Clear remote "compiling" spinner if we were mirroring compile_start.
          if (current.compileStatus === "compiling") {
            dispatch({ type: "COMPILE_SUCCESS", pdfUrl: current.pdfUrl, compiledFile: current.compiledFile })
          }
          return
        }
        clearPdfReadyFallback()
        clearLocalCompileRecovery()
        localCompilePendingRef.current = false
        pendingWsPdfRef.current = null
        const d = message.data
        // The full tectonic output lives in d.message or d.error (may be multi-line)
        const fullOutput =
          (typeof d?.message === "string" && d.message) ||
          (typeof d?.error === "string" && d.error) ||
          "Compile failed"

        // Build details: put the full tectonic output first so it is always visible
        const detailParts: string[] = []
        detailParts.push(fullOutput)
        if (typeof d?.details === "string" && d.details && d.details !== fullOutput) detailParts.push(`--- details ---\n${d.details}`)
        if (typeof d?.logs === "string" && d.logs && d.logs !== fullOutput) detailParts.push(`--- logs ---\n${d.logs}`)
        if (typeof d?.stderr === "string" && d.stderr && d.stderr !== fullOutput) detailParts.push(`--- stderr ---\n${d.stderr}`)
        if (typeof d?.stdout === "string" && d.stdout) detailParts.push(`--- stdout ---\n${d.stdout}`)
        if (d?.diagnostics !== undefined) detailParts.push(`--- diagnostics ---\n${JSON.stringify(d.diagnostics, null, 2)}`)

        const details = detailParts.join("\n\n")

        // Short label for the event title row
        const firstLine = fullOutput.split("\n")[0].slice(0, 120)
        compilePdfTsLog("ws:compile_error", firstLine, {
          sessionId: current.sessionId ?? null,
          backendTimes,
          msSinceCompileTrigger: trigger ? nowMs - trigger.epochMs : null,
        })
        console.log("[COMPILER] compile response", {
          endpoint: "ws:compile_error",
          session_id: current.sessionId ?? null,
          responseStatus: "error",
          error: firstLine,
        })
        dispatch({ type: "COMPILE_ERROR", message: firstLine, details })
        console.error("[lifecycle] ws_recv compile_error — full data:", d)
        logEvent("error", firstLine, details)
        toast.error(`Compile error: ${firstLine}`, { duration: 6000 })
        return
      }
      case "file_update": {
        const remotePath = normalizePath(typeof message.data?.path === "string" ? message.data.path : "")
        const updateFileId = typeof message.data?.file_id === "string" ? message.data.file_id : ""

        // Deletion acknowledgement: unlink file_id and dispose the editor binding.
        // The content-map entry is dropped so that Y.Text is not reused for the path.
        if (message.data?.deleted === true) {
          if (!remotePath && !updateFileId) return
          applyFsEvent({ type: "FILE_DELETED", file_id: updateFileId, path: remotePath || undefined })
          return
        }

        // Rename acknowledgement moves the path only. The file_id keeps its Y.Text.
        const renamedFrom = normalizePath(
          typeof message.data?.renamed_from === "string" ? message.data.renamed_from : "",
        )
        if (renamedFrom && renamedFrom !== remotePath) {
          applyFsEvent({
            type: "PATH_RELOCATED",
            source: renamedFrom,
            target: remotePath,
            file_id: updateFileId || undefined,
          })
        }

        const remoteContent = typeof message.data?.content === "string" ? message.data.content : ""
        if (!remotePath) {
          logEvent("warn", "Dropped invalid remote file_update")
          return
        }
        if (isPathDeleted(remotePath)) {
          console.log("[editor-store] handleIncomingMessage: ignoring file_update for deleted path:", remotePath)
          return
        }
        const existing = current.files[remotePath]
        if (existing?.pendingSync && existing.content !== remoteContent) {
          logEvent("warn", "Remote update skipped due to local pending edits", remotePath)
          return
        }
        // file_create is acknowledged without a body. An empty payload is not a
        // request to erase text that was just written locally.
        if (
          !remoteContent.trim()
          && existing?.content?.trim()
          && !isPlaceholderFileContent(remotePath, existing.content)
        ) {
          return
        }
        dispatch({
          type: "UPSERT_FILE",
          file: {
            path: remotePath,
            content: remoteContent,
            isAsset: existing?.isAsset ?? isAssetByExtension(remotePath),
            localVersion: (existing?.localVersion ?? 0) + 1,
            pendingSync: false,
          },
        })
        logEvent("info", "Remote file merged", remotePath)
        return
      }
      case "pong":
        logEvent("info", "WebSocket pong")
        return
      case "pdf_ready": {
        // Fan-out: every collaborator on this session WS must render the new PDF.
        if (!current.sessionId) return
        const pdfUrlFromBackend =
          (typeof message.data?.pdf_url === "string" && message.data.pdf_url)
          || pendingWsPdfRef.current?.pdfUrlFromBackend
          || undefined
        const compiledFile =
          compiledFileFromData(message.data as Record<string, unknown> | undefined)
          ?? pendingWsPdfRef.current?.compiledFile
        const mergedTimes = {
          ...pendingWsPdfRef.current?.backendTimes,
          ...backendTimes,
        }
        const mergedData = {
          ...(pendingWsPdfRef.current?.messageData ?? {}),
          ...((message.data as Record<string, unknown> | undefined) ?? {}),
        }
        console.log("[Compile:PDF] ws pdf_ready — applying on ALL peers", {
          origin: isLocalCompile ? "local" : "remote",
          sessionId: current.sessionId,
          pdfUrlFromBackend: pdfUrlFromBackend ?? null,
          compiledFile: compiledFile ?? null,
          backendTimes: mergedTimes,
          messageData: message.data ?? null,
          isLocalCompile,
        })
        compilePdfTsLog(
          isLocalCompile ? "ws:pdf_ready" : "ws:pdf_ready_remote",
          "received pdf_ready — applying PDF URL for this tab",
          {
            sessionId: current.sessionId,
            origin: isLocalCompile ? "local" : "remote",
            backendTimes: mergedTimes,
            messageData: message.data ?? null,
            lastCompileTriggerIso: trigger?.iso ?? null,
            msSinceCompileTrigger: trigger ? nowMs - trigger.epochMs : null,
            lastFileChangeIso: lastFileChangeTsRef.current?.iso ?? null,
            msSinceFileChange: lastFileChangeTsRef.current
              ? nowMs - lastFileChangeTsRef.current.epochMs
              : null,
            pdfUrlFromBackend: pdfUrlFromBackend ?? null,
            compiledFile: compiledFile ?? null,
          },
        )
        applySessionPdf(current.sessionId, {
          reason: isLocalCompile
            ? "ws_recv pdf_ready — updating preview (local compile)"
            : "ws_recv pdf_ready — updating preview (peer compile)",
          origin: isLocalCompile ? "local" : "remote",
          pdfUrlFromBackend,
          compiledFile,
          backendTimes: mergedTimes,
          messageData: mergedData,
        })
        return
      }
      case "error":
        logEvent("error", "WebSocket error event", String(message.data?.message || "Unknown WS error"))
        return
      case "file_created":
      case "FILE_CREATED":
      case "folder_created":
      case "FOLDER_CREATED":
      case "file_renamed":
      case "FILE_RENAMED":
      case "file_rename":
      case "file_moved":
      case "FILE_MOVED":
      case "file_deleted":
      case "FILE_DELETED": {
        const structureEvent = structureEventFromMessage(message.type, message.data)
        if (!structureEvent) {
          logEvent("warn", "Dropped invalid filesystem event", message.type)
          return
        }
        applyFsEvent(structureEvent)
        return
      }
      default:
        logEvent("warn", "Unknown WebSocket message type", message.type)
    }
  }, [logEvent, isPathDeleted, applySessionPdf, clearPdfReadyFallback, clearLocalCompileRecovery, applyFsEvent])
  const connectWs = useCallback((wsUrl: string) => {
    compilerWsUrlRef.current = wsUrl
    wsClientRef.current?.disconnect()
    const client = new CompilerWsClient({
      onOpen: () => {
        lastCompilerWsRecvAtRef.current = Date.now()
        dispatch({ type: "WS_STATUS", status: "connected", attempt: 0 })
        logEvent("success", "[lifecycle] ws_open", wsUrl)
        console.log("[Compiler:WS] connected")
      },
      onClose: () => {
        dispatch({ type: "WS_STATUS", status: latestStateRef.current.sessionId ? "reconnecting" : "disconnected" })
        logEvent("warn", "[lifecycle] ws_close", wsUrl)
        console.warn("[Compiler:WS] closed")
      },
      onMessage: handleIncomingMessage,
      onError: () => {
        logEvent("error", "[lifecycle] ws_error", wsUrl)
        console.error("[Compiler:WS] error")
      },
      onReconnectAttempt: (attempt, delayMs) => {
        dispatch({ type: "WS_STATUS", status: "reconnecting", attempt })
        logEvent("warn", "[lifecycle] ws_reconnect", `attempt=${attempt} delay=${delayMs}ms url=${wsUrl}`)
        console.warn("[Compiler:WS] reconnect attempt", { attempt, delayMs })
      },
    })
    wsClientRef.current = client
    dispatch({ type: "WS_STATUS", status: "connecting" })
    logEvent("info", "[lifecycle] ws_connecting", wsUrl)
    console.log("[Compiler:WS] connecting", wsUrl)
    const resolveUrl = () => resolveAuthenticatedWsUrl(compilerWsUrlRef.current || wsUrl)
    void resolveUrl().then((authedUrl) => {
      if (wsClientRef.current !== client) return
      client.connect(authedUrl, resolveUrl)
    })
  }, [handleIncomingMessage, logEvent, triggerCompile])

  const getContent = useCallback((fileId: string): string => {
    const path = resolvePathFromFileId(fileId)
    return latestStateRef.current.files[path]?.content ?? ""
  }, [])

  const registerFile = useCallback((fileId: string, name: string, content: string) => {
    const path = normalizePath(fileId) || normalizePath(name)
    if (!path) return
    dispatch({
      type: "UPSERT_FILE",
      file: { path, content, isAsset: false, localVersion: 1, pendingSync: false },
    })
  }, [])

  const sendDebouncedFileChange = useCallback((path: string, content: string) => {
    if (localEditTimersRef.current[path]) window.clearTimeout(localEditTimersRef.current[path])
    localEditTimersRef.current[path] = window.setTimeout(() => {
      delete localEditTimersRef.current[path]
      if (Date.now() < remoteEditGuardUntilRef.current) {
        console.log("[Compile:PDF] skip debounced file_change — remote CRDT guard", { path })
        return
      }
      if (isPathDeleted(path)) {
        console.log("[editor-store] skip debounced file_change — path was deleted/renamed", { path })
        return
      }
      if (latestStateRef.current.wsStatus !== "connected" || !latestStateRef.current.sessionId) return
      sendWs({ type: "file_change", data: { path, content }, timestamp: nowIso() })
      const existing = latestStateRef.current.files[path]
      if (!existing) return
      dispatch({ type: "UPSERT_FILE", file: { ...existing, pendingSync: false } })
    }, EDIT_DEBOUNCE_MS)
  }, [sendWs, isPathDeleted])

  const updateContent = useCallback((fileId: string, content: string) => {
    const path = resolvePathFromFileId(fileId)
    if (!path) return
    const existing = latestStateRef.current.files[path]
    const fileID = folderIdMapRef.current.get(path) ?? path
    const yExisting = yFilesMapRef.current?.get(fileID)?.toString()
    // Remote CRDT flush can drive Monaco fallback onChange with the same text — don't echo.
    if (
      Date.now() < remoteEditGuardUntilRef.current
      || (yExisting != null && yExisting === content)
      || (existing?.content === content && !existing.pendingSync)
    ) {
      if (existing?.content !== content) {
        dispatch({
          type: "UPSERT_FILE",
          file: {
            path,
            content,
            isAsset: existing?.isAsset ?? isAssetByExtension(path),
            localVersion: (existing?.localVersion ?? 0) + 1,
            pendingSync: false,
          },
        })
      }
      return
    }
    dispatch({
      type: "UPSERT_FILE",
      file: {
        path,
        content,
        isAsset: existing?.isAsset ?? isAssetByExtension(path),
        localVersion: (existing?.localVersion ?? 0) + 1,
        pendingSync: true,
      },
    })
    // Skip CRDT/WS for auth users without an active compiler session.
    if (!isAuthUserRef.current || latestStateRef.current.sessionId) {
      updateYFile(path, content)
      sendDebouncedFileChange(path, content)
    }
    if (latestStateRef.current.autoCompile) {
      scheduleAutoCompileOnEditRef.current()
    }
  }, [sendDebouncedFileChange, updateYFile])

  const loadAuthFilePreview = useCallback(async (path: string) => {
    if (!isAuthUserRef.current) return

    const normalized = normalizePath(path)
    if (!normalized) return

    const backendId = folderIdMapRef.current.get(normalized)
    if (!backendId) {
      pendingAuthPreviewPathsRef.current.add(normalized)
      return
    }

    if (authPreviewLoadsRef.current.has(normalized)) return
    authPreviewLoadsRef.current.add(normalized)

    try {
      const res = await api.getFilePreview(backendId)
      const signedUrl = res.data?.preview_url || res.data?.url
      if (!res.success || !signedUrl) return

      const existing = latestStateRef.current.files[normalized]
      const isAsset = Boolean(existing?.isAsset) || isImageFile(normalized, res.data?.mime_type)

      let content: string
      if (isAsset) {
        if (existing?.content?.trim() === signedUrl) return
        content = signedUrl
      } else {
        // Keep CRDT/session content when the user already has real file text.
        if (existing && !isPlaceholderFileContent(normalized, existing.content)) return
        try {
          const contentResp = await fetch(signedUrl)
          if (!contentResp.ok) throw new Error(`status ${contentResp.status}`)
          content = await contentResp.text()
        } catch {
          const proxyResp = await bffFetch(
            `/api/files/preview-proxy?fileId=${encodeURIComponent(backendId)}`,
          )
          if (!proxyResp.ok) return
          content = await proxyResp.text()
        }
      }

      dispatch({
        type: "UPSERT_FILE",
        file: {
          path: normalized,
          content,
          isAsset,
          localVersion: (existing?.localVersion ?? 0) + 1,
          pendingSync: false,
        },
      })
      // Keep latestStateRef in sync before heal (render may not have committed yet).
      latestStateRef.current = {
        ...latestStateRef.current,
        files: {
          ...latestStateRef.current.files,
          [normalized]: {
            path: normalized,
            content,
            isAsset,
            localVersion: (existing?.localVersion ?? 0) + 1,
            pendingSync: false,
          },
        },
      }
      // Preview used to skip Y writes (peer races). When alone, heal so compile/CRDT
      // have main.tex before a second browser joins.
      healMissingReactFilesIntoYjs()
      pendingAuthPreviewPathsRef.current.delete(normalized)
      logEvent("info", "[lifecycle] file preview loaded", normalized)
    } catch (err) {
      logEvent("warn", "[lifecycle] failed to load file preview", `${normalized}: ${String(err)}`)
    } finally {
      authPreviewLoadsRef.current.delete(normalized)
    }
  }, [logEvent, healMissingReactFilesIntoYjs])

  const flushPendingAuthPreviews = useCallback(async () => {
    if (!isAuthUserRef.current) return

    const pathsToLoad = new Set<string>(pendingAuthPreviewPathsRef.current)
    pendingAuthPreviewPathsRef.current.clear()

    for (const candidate of [latestStateRef.current.activeFile, latestStateRef.current.mainFile]) {
      if (!candidate) continue
      const normalized = normalizePath(candidate)
      if (!normalized) continue
      const existing = latestStateRef.current.files[normalized]
      if (!existing || isPlaceholderFileContent(normalized, existing.content)) {
        pathsToLoad.add(normalized)
      }
    }

    await Promise.all([...pathsToLoad].map((path) => loadAuthFilePreview(path)))
  }, [loadAuthFilePreview])

  const openFile = useCallback((fileId: string, fileName: string) => {
    const path = resolvePathFromFileId(fileId) || normalizePath(fileName)
    if (!path) return
    if (isPathDeleted(path)) {
      console.warn("[editor-store] openFile refused — path was deleted/renamed", path)
      return
    }
    if (isAuthUserRef.current && pendingAuthFileCreatesRef.current.has(path)) {
      console.warn("[editor-store] openFile refused — file_id not assigned yet", path)
      return
    }
    if (!latestStateRef.current.files[path]) {
      // For authenticated projects, a path unknown to both local state and the
      // DB tree is a ghost (stale session main_file / active_file). Seeding a
      // comment stub here lets the heal path write it into Yjs → Redis, which
      // permanently resurrects the file. Anonymous sessions keep the stub.
      const treeLoaded = folderIdMapRef.current.size > 0
      if (isAuthUserRef.current && treeLoaded && !folderIdMapRef.current.get(path)) {
        console.warn("[editor-store] openFile refused — path not in file tree/session (stale?)", path)
        return
      }
      dispatch({
        type: "UPSERT_FILE",
        file: {
          path,
          content: `% ${basename(path)}\n`,
          isAsset: isAssetByExtension(path),
          localVersion: 1,
          pendingSync: false,
        },
      })
    }
    dispatch({ type: "SET_ACTIVE_FILE", path })
    // Tell the CRDT sync manager which file is active so MonacoBinding edits
    // (which use the MonacoBinding instance as origin) are attributed to this path.
    crdtSyncRef.current?.setActiveFilePath(path)
    if (isAuthUserRef.current) {
      crdtSyncRef.current?.setLocalAwarenessActiveFile(path)
    }
    if (isAuthUserRef.current) {
      void loadAuthFilePreview(path)
    }
    openFileHandlerRef.current?.(path, basename(path) || fileName)
  }, [loadAuthFilePreview])

  const registerOpenFileHandler = useCallback((handler: OpenFileHandler) => {
    openFileHandlerRef.current = handler
  }, [])

  const registerPathEventHandler = useCallback((handler: PathEventHandler | null) => {
    pathEventHandlerRef.current = handler
  }, [])

  const registerRevealLineHandler = useCallback((handler: (line: number) => void) => {
    revealLineHandlerRef.current = handler
  }, [])

  const synctexPdfHandlersRef = useRef<Set<(box: SynctexForwardBox) => void>>(new Set())
  const framePostHandlersRef = useRef<Set<(msg: object) => void>>(new Set())

  const registerSynctexPdfHandler = useCallback((handler: ((box: SynctexForwardBox) => void) | null) => {
    if (!handler) {
      return
    }
    synctexPdfHandlersRef.current.add(handler)
    return () => {
      synctexPdfHandlersRef.current.delete(handler)
    }
  }, [])

  const applySynctexForwardResult = useCallback((box: SynctexForwardBox) => {
    console.log(
        "[SyncTeX:Debug] applySynctexForwardResult invoked with:",
        box,
        "Registered handlers count:",
        synctexPdfHandlersRef.current.size
    )
    synctexPdfHandlersRef.current.forEach((fn) => fn(box))
    postTabSyncMessage({ type: "SYNCTEX_FORWARD", sourceTabId: "main", box })
  }, [])

  const registerFramePostHandler = useCallback((handler: ((msg: object) => void) | null) => {
    if (!handler) {
      return
    }
    framePostHandlersRef.current.add(handler)
    return () => {
      framePostHandlersRef.current.delete(handler)
    }
  }, [])

  const postToFlutterFrame = useCallback((msg: object) => {
    framePostHandlersRef.current.forEach((fn) => fn(msg))
  }, [])

  const registerInverseSyncHandler = useCallback((handler: ((line: number, column: number, word?: string) => void) | null) => {
    inverseSyncHandlerRef.current = handler
  }, [])

  const applyInverseSyncResult = useCallback((line: number, column: number, word?: string) => {
    inverseSyncHandlerRef.current?.(line, column, word)
    postTabSyncMessage({
      type: "SYNCTEX_INVERSE",
      sourceTabId: "main",
      line,
      column,
      word,
    })
  }, [])

  const setPdfThumbnail = useCallback((pdfUrl: string, dataUrl: string) => {
    dispatch({ type: "SET_PDF_THUMBNAIL", pdfUrl, dataUrl })
  }, [])

  const registerActiveMonacoEditor = useCallback((ed: MonacoEditor.IStandaloneCodeEditor | null) => {
    activeMonacoEditorRef.current = ed
  }, [])

  const getActiveMonacoEditor = useCallback(() => activeMonacoEditorRef.current, [])

  const runOnActiveEditor = useCallback(<T,>(fn: (ed: MonacoEditor.IStandaloneCodeEditor) => T): T | undefined => {
    const ed = activeMonacoEditorRef.current
    if (!ed) return undefined
    return fn(ed)
  }, [])

  const registerEditorHistoryHandlers = useCallback(
    (
      handlers: {
        undo: () => void
        redo: () => void
        canUndo?: () => boolean
        canRedo?: () => boolean
      } | null,
    ) => {
      editorHistoryHandlersRef.current = handlers
    },
    [],
  )

  const undoActiveEditor = useCallback(() => {
    const ed = activeMonacoEditorRef.current
    const history = editorHistoryHandlersRef.current
    ed?.focus()

    // Collab / MonacoBinding: Y.UndoManager owns history
    if (history?.canUndo?.()) {
      history.undo()
      return
    }

    if (!ed) {
      history?.undo()
      return
    }

    const action = ed.getAction("undo")
    if (action) {
      void action.run()
      return
    }
    ed.trigger("keyboard", "undo", null)
  }, [])

  const redoActiveEditor = useCallback(() => {
    const ed = activeMonacoEditorRef.current
    const history = editorHistoryHandlersRef.current
    ed?.focus()

    if (history?.canRedo?.()) {
      history.redo()
      return
    }

    if (!ed) {
      history?.redo()
      return
    }

    const action = ed.getAction("redo")
    if (action) {
      void action.run()
      return
    }
    ed.trigger("keyboard", "redo", null)
  }, [])

  const openFileAtLine = useCallback((fileId: string, fileName: string, line: number) => {
    const files = latestStateRef.current.files

    // 1. Try exact path match first
    let resolvedPath = resolvePathFromFileId(fileId) || normalizePath(fileName)

    // 2. If not found by exact path, search all open files for one whose basename matches
    if (!files[resolvedPath]) {
      const needle = basename(resolvedPath || fileName).toLowerCase()
      const match = Object.keys(files).find((p) => basename(p).toLowerCase() === needle)
      if (match) resolvedPath = match
    }

    if (!resolvedPath) return

    // 3. Only create a new blank file if truly not found anywhere
    if (!files[resolvedPath]) {
      dispatch({
        type: "UPSERT_FILE",
        file: {
          path: resolvedPath,
          content: `% ${basename(resolvedPath)}\n`,
          isAsset: isAssetByExtension(resolvedPath),
          localVersion: 1,
          pendingSync: false,
        },
      })
    }

    if (isAuthUserRef.current) {
      void loadAuthFilePreview(resolvedPath)
    }

    // Open the tab, then reveal the line after a tick so Monaco is mounted
    openFileHandlerRef.current?.(resolvedPath, basename(resolvedPath) || fileName)
    setTimeout(() => revealLineHandlerRef.current?.(line), 80)
    postTabSyncMessage({
      type: "OPEN_FILE_AT_LINE",
      sourceTabId: "main",
      fileId,
      fileName,
      line,
    })
  }, [loadAuthFilePreview])

  const loadTemplate = useCallback(async () => {
    const apiClient = apiClientRef.current
    const cfg = configRef.current
    if (!apiClient || !cfg) {
      logEvent("error", "Cannot load template", "Compiler API config is not ready")
      return
    }
    dispatch({ type: "TEMPLATE_LOADING" })
    logEvent("info", "Loading template", cfg.templateId)
    try {
      const template = await apiClient.getTemplate(cfg.templateId)
      const files: Record<string, EditorFileRecord> = {}
      template.files.forEach((file: CompilerTemplateFile) => {
        const path = normalizePath(file.path)
        if (!path) return
        files[path] = {
          path,
          content: file.content,
          isAsset: Boolean(file.is_asset),
          localVersion: 1,
          pendingSync: false,
        }
      })
      dispatch({
        type: "TEMPLATE_SUCCESS",
        templateId: template.id,
        templateName: template.name,
        templateDescription: template.description,
        mainFile: normalizePath(template.main_file) || "main.tex",
        files,
        virtualFolders: listVirtualFoldersFromFiles(files),
      })
      logEvent("success", "Template loaded", `${template.files.length} files`)
      // Update the ref immediately so openFile sees the new files before React
      // re-renders the provider. Without this, openFile incorrectly treats the
      // main file as missing and overwrites it with an empty placeholder.
      const alreadyHasSession = Boolean(
        latestStateRef.current.sessionId ||
        latestStateRef.current.sessionPhase === "loading" ||
        latestStateRef.current.sessionPhase === "success"
      )
      if (!alreadyHasSession) {
        latestStateRef.current = { ...latestStateRef.current, files }
        const mainPath = normalizePath(template.main_file)
        if (mainPath && files[mainPath]) openFile(mainPath, basename(mainPath))
      }
    } catch (error) {
      const compilerError = toCompilerError(error, "LOAD_TEMPLATE_FAILED")
      dispatch({ type: "TEMPLATE_ERROR" })
      logEvent("error", "Template load failed", compilerError.message)
    }
  }, [logEvent, openFile])

  const resumeSession = useCallback(async (session: CreateSessionResponse) => {
    const cfg = configRef.current
    if (!cfg) return

    const rawWsPath = session.ws_url || `/api/v1/ws/${encodeURIComponent(session.session_id)}`
    const wsUrl = toSameOriginCompilerWsUrl(rawWsPath)

    // Clear old/template files so they don't leak into the resumed session
    dispatch({ type: "CLEAR_FILES" })

    // Immediately populate file explorer from session snapshot so the tree is
    // visible before CRDT sync completes.
    // Backend returns PascalCase field names (Path, Content, IsAsset) — support both.
    if (session.files && Object.keys(session.files).length > 0) {
      console.warn("[lifecycle] session files received from compiler", {
        session_id: session.session_id,
        main_file: session.mainFile,
        paths: Object.keys(session.files || {}),
      })
      for (const fileRecord of Object.values(session.files)) {
        const filePath = normalizePath(fileRecord.Path || fileRecord.path || "")
        if (!filePath) continue
        const content = fileRecord.Content ?? fileRecord.content ?? ""
        const isAsset = fileRecord.IsAsset ?? fileRecord.isAsset ?? isAssetByExtension(filePath)
        dispatch({
          type: "UPSERT_FILE",
          file: {
            path: filePath,
            content,
            isAsset,
            localVersion: 1,
            pendingSync: false,
          },
        })
      }
      logEvent("info", "[lifecycle] session files restored", `count=${Object.keys(session.files).length}`)
    }

    const crdtInfo = session.crdt || {
      room: session.session_id,
      ws_url: `/api/v1/crdt/ws/${encodeURIComponent(session.session_id)}`,
      reconstruct_url: `/api/v1/sessions/${encodeURIComponent(session.session_id)}/crdt-files`,
      history_url: `/api/v1/sessions/${encodeURIComponent(session.session_id)}/crdt-history`,
    }

    const crdtHistoryUrl = crdtHistoryBffUrl(
      crdtInfo.history_url,
      session.session_id,
      projectIdRef.current,
    )

    dispatch({
      type: "SESSION_SUCCESS",
      sessionId: session.session_id,
      wsUrl,
      crdtHistoryUrl,
    })
    if (isAuthUserRef.current) {
      if (projectIdRef.current) saveAuthProjectSessionId(projectIdRef.current, session.session_id)
      syncCollaborationSessionIdToUrl(session.session_id)
      console.log("[Collaboration] resumed compiler session", {
        sessionId: session.session_id,
        projectId: projectIdRef.current,
        pageUrl: typeof window !== "undefined" ? window.location.href : null,
        shareThisUrl: typeof window !== "undefined" ? window.location.href : null,
      })
    } else {
      saveSessionIdToStorage(session.session_id)
    }
    logEvent("success", "[lifecycle] session_resumed", `sessionId=${session.session_id} autoCompile=${session.auto_compile}`)
    connectWs(wsUrl)

    // Restore PDF from the previous compilation if pdfpath indicates one exists.
    const mainFileResolved = normalizePath(session.mainFile || (session as any).main_file || "")
    if (session.pdfpath) {
      sessionHadExistingPdfRef.current = true
      // Prefer lastCompileAt so ?t= matches the real generation. Using Date.now() here
      // made joiners treat the first user's awareness pdfReady.t as "stale" and later
      // soft-refresh with a brand-new t that 404'd while a peer compile was in flight.
      const fromLastCompile = session.lastCompileAt
        ? Date.parse(session.lastCompileAt)
        : Number.NaN
      const cacheBustMs = Number.isFinite(fromLastCompile) ? fromLastCompile : Date.now()
      if (Number.isFinite(fromLastCompile)) {
        lastAppliedPdfCacheBustRef.current = cacheBustMs
        lastAppliedPdfAtRef.current = Date.now()
      }
      const restoredPdfUrl = `/api/compiler/sessions/${encodeURIComponent(session.session_id)}/pdf?t=${cacheBustMs}`
      const compiledFile = mainFileResolved || undefined
      dispatch({ type: "COMPILE_SUCCESS", pdfUrl: restoredPdfUrl, compiledFile })
      logEvent("info", "[lifecycle] restored pdf from session", `compiledFile=${compiledFile ?? "unknown"} t=${cacheBustMs}`)
    } else {
      sessionHadExistingPdfRef.current = false
    }

    // Open the last active file so the editor restores to where the user left off.
    const fileIdResolved = normalizePath(session.fileID || (session as any).file_id || "")
    const lastActiveFile = fileIdResolved || mainFileResolved
    // A stale session main_file/active_file (e.g. a renamed or deleted path)
    // must not be opened — doing so seeds a blank stub that can flow back into
    // Redis/Yjs and resurrect the file.
    const lastActiveKnown = lastActiveFile
      ? Boolean(
          session.files?.[lastActiveFile] ||
          folderIdMapRef.current.get(lastActiveFile) ||
          latestStateRef.current.files[lastActiveFile],
        )
      : false
    if (lastActiveFile && lastActiveKnown && !isPathDeleted(lastActiveFile)) {
      // The dock registers its tab handler after mount. A single 150ms timer
      // can fire first and drop the open, leaving the empty placeholder tab.
      const fileName = basename(lastActiveFile)
      let attempt = 0
      const restoreOpenTab = () => {
        if (openFileHandlerRef.current || attempt >= 20) {
          openFile(lastActiveFile, fileName)
          logEvent("info", "[lifecycle] restored last active file", lastActiveFile)
          return
        }
        attempt += 1
        window.setTimeout(restoreOpenTab, 100)
      }
      restoreOpenTab()
    } else if (lastActiveFile) {
      console.warn("[lifecycle] skip restoring stale active file — not in session/tree state", {
        lastActiveFile,
        inSessionFiles: Boolean(session.files?.[lastActiveFile]),
        inTree: Boolean(folderIdMapRef.current.get(lastActiveFile)),
      })
    }

    if (crdtInfo) {
      sessionGenerationRef.current += 1
      const generation = sessionGenerationRef.current

      function toBffUrl(backendPath: string): string {
        return backendPath.replace(
          `/api/v1/sessions/${session.session_id}/`,
          `/api/compiler/sessions/${encodeURIComponent(session.session_id)}/`,
        )
      }

      const crdtWsUrl = toSameOriginCompilerWsUrl(
        crdtInfo.ws_url && crdtInfo.ws_url.includes("/crdt/ws/")
          ? crdtInfo.ws_url
          : `/api/v1/crdt/ws/${encodeURIComponent(session.session_id)}`,
      )

      const bffCrdtInfo: CrdtInfo = {
        ...crdtInfo,
        ws_url: crdtWsUrl,
        reconstruct_url: toBffUrl(crdtInfo.reconstruct_url),
        history_url: crdtHistoryUrl ?? toBffUrl(crdtInfo.history_url),
      }

      const sessionFiles = Object.values(session.files || {})
        .filter((f: any) => !(f.IsAsset || f.isAsset || f.is_asset))
        .map((f: any) => ({
          path: normalizePath(f.Path || f.path || ""),
          content: f.Content ?? f.content ?? ""
        }))
        .filter((f) => !!f.path)

      let seedFiles = sessionFiles
      const seedHasRealContent = seedFiles.some((f) => Boolean(f.content?.trim()))
      if (!seedHasRealContent) {
        try {
          const reconstructResp = await fetch(bffCrdtInfo.reconstruct_url)
          if (reconstructResp.ok) {
            const reconstructData = (await reconstructResp.json()) as CrdtFilesResponse
            if (reconstructData.files && Object.keys(reconstructData.files).length > 0) {
              seedFiles = Object.entries(reconstructData.files).map(([path, content]) => ({ path, content }))
              logEvent("info", "[crdt] reconstruct success", `files=${seedFiles.length}`)
            }
          }
        } catch (err) {
          logEvent("warn", "[crdt] reconstruct failed during resume", String(err))
        }
      } else {
        logEvent("info", "[crdt] seeding from restored session files", `files=${seedFiles.length}`)
      }

      connectYjs(bffCrdtInfo, session.session_id, seedFiles, generation, { shouldSeed: true })
    }
  }, [connectWs, connectYjs, logEvent, openFile])

  const resumeTriggeredRef = useRef(false)
  const resumeSessionRef = useRef(resumeSession)
  resumeSessionRef.current = resumeSession

  const throwIfSessionProbeFailed = (status: number, label: string) => {
    // 200 with an inactive payload is treated as missing by the caller, not here.
    const kind = classifyHttpStatus(status)
    if (kind === "not_found") return
    throw new SessionInitError(`${label} failed (${status})`, kind, status)
  }

  const tryResumeProbedSession = async (
    pid: string,
    candidateId: string,
    signal: AbortSignal,
    source: "url" | "stored",
  ): Promise<"resumed" | "missing"> => {
    const { status, session } = await probeCompilerSession(candidateId, signal)
    if (session) {
      if (session.projectID && session.projectID !== pid) {
        logEvent(
          "warn",
          `[lifecycle] ${source} session belongs to a different project, ignoring`,
          `expected=${pid} got=${session.projectID}`,
        )
        return "missing"
      }
      logEvent("info", `[lifecycle] ${source} compiler session active, resuming`, session.session_id)
      saveAuthProjectSessionId(pid, session.session_id)
      await resumeSessionRef.current(session)
      await applyFileTreeRef.current(pid)
      return "resumed"
    }
    if (status === 200 || classifyHttpStatus(status) === "not_found") {
      logEvent("info", `[lifecycle] ${source} compiler session missing (${status})`, candidateId)
      return "missing"
    }
    throwIfSessionProbeFailed(status, `GET compiler session (${source})`)
    return "missing"
  }

  const attemptAuthCompilerSession = async (pid: string, signal: AbortSignal) => {
    const pending = consumeAuthCompilerSession()
    if (pending) {
      saveAuthProjectSessionId(pid, pending.session_id)
      await resumeSessionRef.current(pending)
      await applyFileTreeRef.current(pid)
      return
    }

    const urlSessionId = getCollaborationSessionIdFromUrl()
    if (urlSessionId) {
      logEvent("info", "[lifecycle] compiler session from URL", urlSessionId)
      console.log("[Collaboration] joining session from URL", { urlSessionId, projectId: pid })
      const urlResult = await tryResumeProbedSession(pid, urlSessionId, signal, "url")
      if (urlResult === "resumed") return
      logEvent("warn", "[lifecycle] URL session not active, falling back", urlSessionId)
    }

    const storedId = loadAuthProjectSessionId(pid)
    if (storedId) {
      logEvent("info", "[lifecycle] auth project session found, checking status", storedId)
      const storedResult = await tryResumeProbedSession(pid, storedId, signal, "stored")
      if (storedResult === "resumed") return
      clearAuthProjectSessionId(pid)
      logEvent("warn", "[lifecycle] auth project session stale, creating new session")
    } else {
      logEvent("info", "[lifecycle] no auth project session — creating compiler session", `projectId=${pid}`)
      console.warn(
        "[Collaboration] No shared session found. After this tab connects, copy the URL (includes compilerSessionId) to open the same CRDT room in another browser.",
      )
    }

    if (signal.aborted) throw new DOMException("Aborted", "AbortError")

    try {
      const created = await createAuthCompilerSessionForProject(pid, {
        skipErrorToast: true,
        signal,
      })
      saveAuthProjectSessionId(pid, created.session_id)
      const full = await fetchActiveAuthCompilerSession(created.session_id, signal)
      await resumeSessionRef.current(full ?? created)
      await applyFileTreeRef.current(pid)
    } catch (error) {
      if (error instanceof SessionInitError || (error instanceof Error && error.name === "AbortError")) {
        throw error
      }
      if (error instanceof ApiRequestError) {
        throw new SessionInitError(
          error.message,
          classifyHttpStatus(error.status),
          error.status,
        )
      }
      throw new SessionInitError(String(error), "transient", 0)
    }
  }

  const startAuthCompilerSession = (source: "auto" | "manual") => {
    const pid = projectIdRef.current
    if (!pid) return
    if (sessionInitInFlightRef.current) {
      logEvent("info", "[lifecycle] Session init skipped — already in flight", source)
      return
    }
    if (latestStateRef.current.sessionId && latestStateRef.current.sessionPhase === "success") return

    const ownerId = `${source}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    const lockKey = `auth:${pid}`
    if (!acquireSessionInitLock(lockKey, ownerId)) {
      logEvent("info", "[lifecycle] Session init skipped — lock held", lockKey)
      return
    }

    sessionInitAbortRef.current?.abort()
    const abort = new AbortController()
    sessionInitAbortRef.current = abort
    sessionInitInFlightRef.current = true
    sessionInitOwnerRef.current = ownerId
    sessionInitLockKeyRef.current = lockKey
    fileTreeFetchedRef.current = true

    logEvent(
      "info",
      source === "manual" ? "[lifecycle] Manual retry" : "[lifecycle] Session initialization started",
    )

    void (async () => {
      try {
        const outcome = await runBoundedSessionAttempts({
          signal: abort.signal,
          isCurrent: () => sessionInitOwnerRef.current === ownerId && !abort.signal.aborted,
          onAttempt: (attempt, maxAttempts) => {
            dispatch({ type: "SESSION_LOADING" })
            logEvent("info", `[lifecycle] Session attempt ${attempt}/${maxAttempts}`)
          },
          onRetryWait: (failedAttempt, delayMs) => {
            logEvent(
              "info",
              `[lifecycle] Session attempt ${failedAttempt}/${SESSION_MAX_ATTEMPTS} failed — retrying in ${delayMs / 1000}s`,
            )
          },
          attempt: () => attemptAuthCompilerSession(pid, abort.signal),
        })

        if (abort.signal.aborted || sessionInitOwnerRef.current !== ownerId) return

        if (outcome === "connected") {
          logEvent("success", "[lifecycle] Session connected")
        } else if (outcome === "auth") {
          fileTreeFetchedRef.current = false
          dispatch({ type: "SESSION_ERROR", disconnected: false })
          logEvent("error", "[lifecycle] Session init stopped — authentication required")
        } else if (outcome === "disconnected") {
          fileTreeFetchedRef.current = false
          dispatch({ type: "SESSION_ERROR", disconnected: true })
          logEvent("warn", "[lifecycle] Session disconnected")
        }
      } finally {
        if (sessionInitOwnerRef.current === ownerId) {
          sessionInitInFlightRef.current = false
          releaseSessionInitLock(lockKey, ownerId)
        }
      }
    })()
  }

  const startAuthCompilerSessionRef = useRef(startAuthCompilerSession)
  startAuthCompilerSessionRef.current = startAuthCompilerSession

  useEffect(() => {
    if (!isAuthUserRef.current || !state.configReady) return
    if (!projectIdRef.current) return
    if (latestStateRef.current.sessionId) return
    startAuthCompilerSessionRef.current("auto")
  }, [state.configReady])

  const retryCompilerSession = useCallback(() => {
    if (sessionInitInFlightRef.current) {
      logEvent("info", "[lifecycle] Manual retry ignored — session init already in flight")
      return
    }
    if (isAuthUserRef.current) {
      startAuthCompilerSessionRef.current("manual")
      return
    }
    startAnonymousCompilerSessionRef.current("manual")
  }, [logEvent])

  useEffect(() => {
    if (initialSession && !isAuthUserRef.current && !resumeTriggeredRef.current && state.configReady && state.templatePhase === "success") {
      resumeTriggeredRef.current = true
      void resumeSession(initialSession)
    }
  }, [initialSession, state.configReady, state.templatePhase, resumeSession])

  useEffect(() => {
    if (state.configReady && state.templatePhase === "idle" && !isAuthUserRef.current) {
      void loadTemplate()
    }
  }, [state.configReady, state.templatePhase, loadTemplate])

  useEffect(() => {
    if (typeof window !== "undefined") {
      const cookiesList = document.cookie.split(";").map((c) => c.trim())
      const hasSessionCookie = cookiesList.some((c) => c.startsWith("editor_session="))
      if (hasSessionCookie && (!initialSession || isAuthUserRef.current)) {
        // Clear stale cookie, or clear it entirely for auth users (they manage sessions differently).
        console.log("[EditorStore] Clearing session cookie:", isAuthUserRef.current ? "auth user" : "stale cookie")
        void clearSessionAction()
      }
    }
  }, [initialSession])

  /** Live filesystem version. GET /projects can serve a stale filesystem_version. */
  const readFilesystemVersion = useCallback(async (pid: string): Promise<number | undefined> => {
    try {
      const resp = await bffFetch(
        `/api/projects/${encodeURIComponent(pid)}/filesystem/events?since=0&limit=1`,
      )
      if (!resp.ok) {
        logEvent("warn", "[lifecycle] filesystem version fetch failed", `status=${resp.status}`)
        return latestStateRef.current.filesystemVersion
      }
      const result = await resp.json()
      const version = result?.data?.current_version
      if (typeof version === "number") {
        dispatch({ type: "SET_FILESYSTEM_VERSION", version })
        return version
      }
    } catch (err) {
      logEvent("warn", "[lifecycle] filesystem version fetch failed", String(err))
    }
    return latestStateRef.current.filesystemVersion
  }, [logEvent])

  // Shared helper: fetch /api/files/tree, build the nested tree, and update state.
  /** Applies the backend tree to local state. Returns the paths of files that were
   *  newly introduced by this refresh (not already in local state) so callers can
   *  register them with the live compiler session via registerExternalFile. */
  const applyFileTree = useCallback(async (pid: string): Promise<string[]> => {
    try {
      const [treeResp] = await Promise.all([
        bffFetch(`/api/files/tree?project_id=${encodeURIComponent(pid)}`),
        readFilesystemVersion(pid),
      ])
      if (!treeResp.ok) { logEvent("warn", "[lifecycle] file tree fetch failed", `status=${treeResp.status}`); return [] }
      const treeResult = await treeResp.json()
      if (!treeResult.success) { logEvent("warn", "[lifecycle] file tree response not successful", treeResult.message); return [] }

      // Backend returns a pre-nested tree (children embedded in each node).
      const flatItems: ProjectNode[] = Array.isArray(treeResult.data) ? treeResult.data : []
      const tree = buildProjectTree(flatItems, projectNameRef.current || "Project")

      // Update node-id map (files + folders) so create/delete can resolve parent_id / node id.
      // A deleted file_id stays retired. A stale tree must not put that id, or its
      // old Y.Text, back on a path. A different id at the same path is kept.
      const built = buildIdMapFromTree(tree)
      const nextMap = new Map<string, string>()
      built.forEach((id, path) => {
        if (retiredFileIdsRef.current.has(id)) return
        nextMap.set(path, id)
      })
      folderIdMapRef.current = nextMap
      const fileNodes = new Map<string, FsNodeMeta>()
      indexProjectTree(tree, null, fileNodes)
      fileNodes.forEach((_meta, id) => {
        if (retiredFileIdsRef.current.has(id)) fileNodes.delete(id)
      })
      fileNodeByIdRef.current = fileNodes

      const hasActiveSession = isAuthUserRef.current && Boolean(latestStateRef.current.sessionId)
      if (!hasActiveSession) {
        dispatch({ type: "CLEAR_FILES" })
      }

      // Add every folder (including empty ones) to virtualFolders.
      for (const folderPath of extractFolderPathsFromTree(tree)) {
        const normalized = normalizePath(folderPath)
        if (!normalized) continue
        const folderId = built.get(normalized)
        if (folderId && retiredFileIdsRef.current.has(folderId)) continue
        dispatch({ type: "ADD_FOLDER", path: normalized })
      }

      // Add every file at its correct nested path. When a compiler session is active,
      // keep existing CRDT/session file content and only add missing project files.
      const files = extractFilePathsFromTree(tree)
      const addedPaths: string[] = []
      for (const { path, content } of files) {
        const normalized = normalizePath(path)
        if (!normalized) continue
        const treeId = built.get(normalized)
        if (treeId && retiredFileIdsRef.current.has(treeId)) continue
        const existing = latestStateRef.current.files[normalized]
        if (hasActiveSession && existing && !isPlaceholderFileContent(normalized, existing.content)) {
          continue
        }
        if (!existing) addedPaths.push(normalized)
        dispatch({
          type: "UPSERT_FILE",
          file: {
            path: normalized,
            content: existing?.content ?? content,
            isAsset: isAssetByExtension(normalized),
            localVersion: existing?.localVersion ?? 1,
            pendingSync: false,
          },
        })
      }
      logEvent("info", "[lifecycle] file tree applied", `projectId=${pid} files=${files.length} new=${addedPaths.length} folders=${folderIdMapRef.current.size}`)
      // Path metadata for ops that arrived before this tree was loaded.
      settlePendingStructureOpsRef.current()
      await flushPendingAuthPreviews()
      if (isAuthUserRef.current) {
        const mainPath = normalizePath(latestStateRef.current.mainFile || "main.tex")
        const mainFileID = folderIdMapRef.current.get(mainPath) ?? mainPath
        const yMain = yFilesMapRef.current?.get(mainFileID)?.toString() ?? ""
        const reactMain = latestStateRef.current.files[mainPath]?.content ?? ""
        if (!yMain.trim() && !reactMain.trim()) {
          await loadAuthFilePreview(mainPath)
        }
        dispatch({ type: "AUTH_PROJECT_HYDRATED" })
        // File tree / preview may have filled React after the delayed CRDT seed ran
        // (which often only had config). Heal missing text files into Y now.
        healMissingReactFilesIntoYjs()
      }
      return addedPaths
    } catch (err) {
      logEvent("warn", "[lifecycle] failed to apply file tree", String(err))
      return []
    }
  }, [logEvent, flushPendingAuthPreviews, loadAuthFilePreview, healMissingReactFilesIntoYjs, readFilesystemVersion])
  applyFileTreeRef.current = applyFileTree

  // For auth users: fetch the project file tree from /api/files/tree once config is ready.
  useEffect(() => {
    if (!isAuthUserRef.current || fileTreeFetchedRef.current || !state.configReady) return
    if (hasAuthCompilerSessionPending()) return
    const pid = projectIdRef.current
    if (!pid) return
    fileTreeFetchedRef.current = true
    void applyFileTree(pid)
  }, [state.configReady, applyFileTree])

  const autoCompileTriggeredRef = useRef(false)

  // Reset the trigger flag when sessionId changes (i.e. new session created or resumed)
  useEffect(() => {
    autoCompileTriggeredRef.current = false
    if (!state.sessionId) {
      sessionHadExistingPdfRef.current = false
    }
  }, [state.sessionId])

  // Auth preview/tree often finish after the first CRDT seed (config-only). Re-heal
  // once both hydration and CRDT sync are ready so main.tex lands in Y before peers join.
  useEffect(() => {
    if (!isAuthUserRef.current) return
    if (state.authProjectHydrationEpoch <= 0 || state.crdtProviderEpoch <= 0) return
    healMissingReactFilesIntoYjs()
  }, [state.authProjectHydrationEpoch, state.crdtProviderEpoch, healMissingReactFilesIntoYjs])

  useEffect(() => {
    const isAuth = hasAuthSession()
    if (
      state.autoCompile &&
      state.sessionId &&
      state.wsStatus === "connected" &&
      state.crdtProviderEpoch > 0 &&
      (!isAuth || state.authProjectHydrationEpoch > 0) &&
      !autoCompileTriggeredRef.current
    ) {
      if (!isAuth && state.activeFile && state.activeFile !== (state.mainFile || "main.tex")) {
        // Skip automatic initial compile for guest if active file is not main.tex
        return
      }

      // Cross-browser: sessionStorage compile-leader does not work between Safari/Chrome.
      // Wait briefly for awareness peers; joiners must adopt the existing PDF, not recompile.
      const sessionId = state.sessionId
      const timer = window.setTimeout(() => {
        if (autoCompileTriggeredRef.current) return
        if (latestStateRef.current.sessionId !== sessionId) return

        const peerCount = Math.max(
          0,
          (crdtSyncRef.current?.awareness.getStates().size ?? 1) - 1,
        )

        // Prefer any peer-published pdfReady before deciding to compile.
        const sync = crdtSyncRef.current
        if (sync) {
          const localId = sync.awareness.clientID
          let bestT = 0
          let bestCompiled: string | null = null
          for (const [clientId, awState] of sync.awareness.getStates()) {
            if (clientId === localId) continue
            const raw = (awState as { pdfReady?: unknown } | null)?.pdfReady
            if (!raw || typeof raw !== "object") continue
            const pdf = raw as { t?: unknown; sessionId?: unknown; compiledFile?: unknown }
            if (typeof pdf.sessionId === "string" && pdf.sessionId !== sessionId) continue
            const t = typeof pdf.t === "number" && Number.isFinite(pdf.t) ? pdf.t : null
            if (t == null || t <= bestT) continue
            bestT = t
            bestCompiled = typeof pdf.compiledFile === "string" ? pdf.compiledFile : null
          }
          if (bestT > lastAppliedPdfCacheBustRef.current) {
            applySessionPdfRef.current(sessionId, {
              reason: "adopt peer pdfReady on join — skip initial auto-compile",
              origin: "remote",
              compiledFile: bestCompiled,
              messageData: { t: bestT, awareness: true },
              backendTimes: { pdf_ready_at: new Date(bestT).toISOString() },
            })
          }
        }

        if (peerCount > 0) {
          autoCompileTriggeredRef.current = true
          logEvent(
            "info",
            "[compile] auto-compile skipped on join — peers already in room (adopt existing PDF)",
            `peers=${peerCount}`,
          )
          return
        }

        if (sessionHadExistingPdfRef.current) {
          autoCompileTriggeredRef.current = true
          logEvent(
            "info",
            "[compile] auto-compile skipped — session already has a compiled PDF",
          )
          return
        }

        if (!tryClaimAutoCompileLeader(sessionId)) {
          autoCompileTriggeredRef.current = true
          logEvent("info", "[compile] auto-compile skipped — another tab owns compile for this session")
          return
        }
        autoCompileTriggeredRef.current = true
        logEvent("info", "[lifecycle] Auto compile triggered automatically after sync and connection ready")
        void triggerCompile()
      }, 1500)

      return () => window.clearTimeout(timer)
    }
  }, [state.autoCompile, state.sessionId, state.wsStatus, state.crdtProviderEpoch, state.authProjectHydrationEpoch, state.activeFile, state.mainFile, triggerCompile, logEvent])

  const setAutoCompile = useCallback(async (enabled: boolean) => {
    dispatch({ type: "AUTO_COMPILE", enabled })
    logEvent("info", enabled ? "Auto compile enabled" : "Auto compile disabled")
    const current = latestStateRef.current
    if (current.sessionId) {
      try {
        await setAutoCompileAction(current.sessionId, enabled)
      } catch (err) {
        logEvent("error", "Failed to update auto compile on server", String(err))
      }
    }
  }, [logEvent])

  const createSession = useCallback(async () => {
    const apiClient = apiClientRef.current
    const cfg = configRef.current
    const current = latestStateRef.current
    if (!apiClient || !cfg) {
      logEvent("error", "Cannot create session", "Compiler API config is not ready")
      throw new SessionInitError("Compiler API config is not ready", "transient", 0)
    }
    if (current.sessionId || current.sessionPhase === "success") {
      logEvent("info", "createSession bypassed: session already exists", current.sessionId)
      return
    }
    if (!current.templateId) {
      logEvent("warn", "Session creation blocked", "Template is not loaded")
      throw new SessionInitError("Template is not loaded", "permanent", 400)
    }
    dispatch({ type: "SESSION_LOADING" })
    logEvent("info", "Creating session", `templateId=${current.templateId} files=${Object.keys(current.files).length}`)
    try {
      const initialFiles = Object.values(current.files).map((file) => ({
        path: file.path,
        content: file.content,
        isAsset: file.isAsset,
      }))
      const session = await createAndSaveSessionAction({
        templateId: current.templateId,
        autoCompile: current.autoCompile,
        initialFiles: initialFiles,
      })
      // Same-origin socket. next.config rewrites proxy /compiler/api/v1/ws/
      // when the upgrade reaches this process. Route handlers do not.
      const rawWsPath = session.ws_url || `/api/v1/ws/${encodeURIComponent(session.session_id)}`
      const wsUrl = toSameOriginCompilerWsUrl(rawWsPath)
      // NOTE: pdfUrl is intentionally NOT set here.
      // PDF is only available after compile_success — setting it now causes a 404.
      const crdtInfo = session.crdt
      const crdtHistoryUrl = crdtInfo
        ? crdtHistoryBffUrl(crdtInfo.history_url, session.session_id, projectIdRef.current)
        : undefined

      dispatch({
        type: "SESSION_SUCCESS",
        sessionId: session.session_id,
        wsUrl,
        crdtHistoryUrl,
      })
      if (isAuthUserRef.current) {
        if (projectIdRef.current) saveAuthProjectSessionId(projectIdRef.current, session.session_id)
      } else {
        saveSessionIdToStorage(session.session_id)
      }
      logEvent("success", "[lifecycle] session_created", `sessionId=${session.session_id} autoCompile=${current.autoCompile}`)
      connectWs(wsUrl)

      // ── CRDT setup ───────────────────────────────────────────────────────────────
      if (crdtInfo) {
        sessionGenerationRef.current += 1
        const generation = sessionGenerationRef.current

        // Rewrite backend HTTP paths → Next.js BFF paths so requests don't hit
        // /api/v1/... directly (which Next.js has no route for → 404).
        // The CRDT socket stays on this page's origin. next.config rewrites
        // proxy /compiler/api/v1/crdt/ws/ when the upgrade reaches this process.
        crdtLog("log", "raw reconstruct_url:", crdtInfo.reconstruct_url)
        crdtLog("log", "raw history_url:", crdtInfo.history_url)
        const toBffUrl = (backendPath: string) =>
          rewriteSessionApiToBff(backendPath, session.session_id)

        // If the backend doesn't provide a ws_url or provides the legacy one, build the CRDT endpoint URL.
        const crdtWsUrl = toSameOriginCompilerWsUrl(
          crdtInfo.ws_url && crdtInfo.ws_url.includes("/crdt/ws/")
            ? crdtInfo.ws_url
            : `/api/v1/crdt/ws/${encodeURIComponent(session.session_id)}`,
        )

        const bffCrdtInfo: CrdtInfo = {
          ...crdtInfo,
          ws_url: crdtWsUrl,
          reconstruct_url: toBffUrl(crdtInfo.reconstruct_url),
          history_url: crdtHistoryUrl ?? toBffUrl(crdtInfo.history_url),
        }

        const templateFiles = Object.values(current.files)
          .filter((f) => !f.isAsset)
          .map((f) => ({ path: f.path, content: f.content }))

        let seedFiles = templateFiles
        try {
          const reconstructed = await fetchReconstructSeedFiles(bffCrdtInfo.reconstruct_url)
          if (reconstructed.length > 0) {
            seedFiles = reconstructed
            logEvent("info", "[crdt] reconstruct success", `files=${seedFiles.length}`)
          } else {
            logEvent("warn", "[crdt] reconstruct empty, seeding from template", undefined)
          }
        } catch (err) {
          logEvent("warn", "[crdt] reconstruct failed, seeding from template", String(err))
        }

        connectYjs(bffCrdtInfo, session.session_id, seedFiles, generation, { shouldSeed: false })
        dispatch({ type: "CRDT_REPLAY_SEED", seedFiles })
        logEvent("info", "[crdt] connectYjs called", `room=${crdtInfo.room}`)
      }
    } catch (error) {
      const compilerError = toCompilerError(error, "CREATE_SESSION_FAILED")
      logEvent("error", "Session creation failed", compilerError.message)
      throw new SessionInitError(compilerError.message, "transient", 0)
    }
  }, [connectWs, connectYjs, logEvent])

  // Init session when no server-side session was provided.
  // Checks localStorage for a previously stored session ID first;
  // if that session is still active on the backend, resumes it instead of creating a new one.
  // This prevents double-creation when the cookie was cleared but the session is still valid.
  const startAnonymousCompilerSession = (source: "auto" | "manual") => {
    if (isAuthUserRef.current || initialSession) return
    if (sessionInitInFlightRef.current) {
      logEvent("info", "[lifecycle] Session init skipped — already in flight", source)
      return
    }
    if (latestStateRef.current.sessionId && latestStateRef.current.sessionPhase === "success") return
    if (latestStateRef.current.templatePhase !== "success") return

    const ownerId = `${source}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    const lockKey = "anon"
    if (!acquireSessionInitLock(lockKey, ownerId)) {
      logEvent("info", "[lifecycle] Session init skipped — lock held", lockKey)
      return
    }

    sessionInitAbortRef.current?.abort()
    const abort = new AbortController()
    sessionInitAbortRef.current = abort
    sessionInitInFlightRef.current = true
    sessionInitOwnerRef.current = ownerId
    sessionInitLockKeyRef.current = lockKey

    logEvent(
      "info",
      source === "manual" ? "[lifecycle] Manual retry" : "[lifecycle] Session initialization started",
    )

    const attemptAnonymousSession = async () => {
      const storedId = loadSessionIdFromStorage()
      if (storedId) {
        logEvent("info", "[lifecycle] Found localStorage session, checking status", storedId)
        const { status, session } = await probeCompilerSession(storedId, abort.signal)
        if (session) {
          logEvent("info", "[lifecycle] localStorage session is active, resuming", session.session_id)
          await resumeSessionRef.current(session)
          return
        }
        if (status === 200 || classifyHttpStatus(status) === "not_found") {
          clearSessionIdFromStorage()
          logEvent("warn", "[lifecycle] localStorage session stale, creating new session")
        } else {
          throw new SessionInitError(
            `GET compiler session failed (${status})`,
            classifyHttpStatus(status),
            status,
          )
        }
      } else {
        logEvent("info", "[lifecycle] No active session — creating new session automatically")
      }
      await createSession()
    }

    void (async () => {
      try {
        const outcome = await runBoundedSessionAttempts({
          signal: abort.signal,
          isCurrent: () => sessionInitOwnerRef.current === ownerId && !abort.signal.aborted,
          onAttempt: (attempt, maxAttempts) => {
            dispatch({ type: "SESSION_LOADING" })
            logEvent("info", `[lifecycle] Session attempt ${attempt}/${maxAttempts}`)
          },
          onRetryWait: (failedAttempt, delayMs) => {
            logEvent(
              "info",
              `[lifecycle] Session attempt ${failedAttempt}/${SESSION_MAX_ATTEMPTS} failed — retrying in ${delayMs / 1000}s`,
            )
          },
          attempt: attemptAnonymousSession,
        })

        if (abort.signal.aborted || sessionInitOwnerRef.current !== ownerId) return

        if (outcome === "connected") {
          logEvent("success", "[lifecycle] Session connected")
        } else if (outcome === "auth") {
          dispatch({ type: "SESSION_ERROR", disconnected: false })
          logEvent("error", "[lifecycle] Session init stopped — authentication required")
        } else if (outcome === "disconnected") {
          dispatch({ type: "SESSION_ERROR", disconnected: true })
          logEvent("warn", "[lifecycle] Session disconnected")
        }
      } finally {
        if (sessionInitOwnerRef.current === ownerId) {
          sessionInitInFlightRef.current = false
          releaseSessionInitLock(lockKey, ownerId)
        }
      }
    })()
  }
  startAnonymousCompilerSessionRef.current = startAnonymousCompilerSession

  // Init session when no server-side session was provided.
  // Checks localStorage for a previously stored session ID first;
  // if that session is still active on the backend, resumes it instead of creating a new one.
  // This prevents double-creation when the cookie was cleared but the session is still valid.
  useEffect(() => {
    if (initialSession || isAuthUserRef.current) return
    if (!state.configReady || state.templatePhase !== "success") return
    if (latestStateRef.current.sessionId) return
    startAnonymousCompilerSessionRef.current("auto")
  }, [initialSession, state.configReady, state.templatePhase])

  const manualCompile = useCallback(() => {
    const { wsStatus, sessionId } = latestStateRef.current
    console.log("[COMPILER] manual compile requested", {
      session_id: sessionId ?? null,
      wsStatus,
      route: wsStatus === "connected" ? "ws:compile_trigger" : "/api/compiler/compile",
    })
    if (wsStatus === "connected") {
      // triggerCompile: force-pushes active file content via file_change, waits 300ms,
      // then sends compile_trigger with main_file set to the active .tex file.
      // triggerCompile()
      void triggerCompile("manual_compile_button")
    } else {
      logEvent("warn", "[lifecycle] WS not connected — compile blocked", `wsStatus=${wsStatus}`)
      toast.error("Not connected to compiler — please wait and retry")
    }
  }, [triggerCompile, logEvent])

  const createFolder = useCallback((folderPath: string): boolean => {
    const normalized = normalizePath(folderPath)
    if (!normalized || latestStateRef.current.virtualFolders.includes(normalized)) return false

    // Remove folder and its subpaths from deletedPathsRef
    deletedPathsRef.current.delete(normalized)
    Array.from(deletedPathsRef.current).forEach((p) => {
      if (p.startsWith(`${normalized}/`)) deletedPathsRef.current.delete(p)
    })

    dispatch({ type: "ADD_FOLDER", path: normalized })
    logEvent("success", "Folder created", normalized)

    // For auth users: persist via API then refresh the tree
    if (isAuthUserRef.current) {
      const pid = projectIdRef.current
      if (pid) {
        const folderName = basename(normalized)
        const parentNormalized = parentPath(normalized)
        // null when creating at root level (no parent folder)
        const parentId = parentNormalized ? (folderIdMapRef.current.get(parentNormalized) ?? null) : null

        void readFilesystemVersion(pid)
          .then((version) => {
            const expectedVersion = version ?? latestStateRef.current.filesystemVersion ?? 0
            return bffFetch("/api/files/folder", {
              method: "POST",
              body: JSON.stringify({
                // operation_id: newFilesystemOperationId(),
                // expected_version: expectedVersion,
                project_id: pid,
                parent_id: parentId,
                name: folderName,
              }),
            })
          })
          .then((r) => r.json())
          .then((result) => {
            if (result.success) {
              // Capture the backend folder ID immediately so files created inside
              // this folder can resolve parent_id before applyFileTree completes.
              const returnedId =
                typeof result.data === "string"
                  ? result.data
                  : typeof result.data === "object" && result.data !== null
                    ? String((result.data as Record<string, unknown>).id ?? (result.data as Record<string, unknown>).folder_id ?? "")
                    : ""
              if (returnedId) folderIdMapRef.current.set(normalized, returnedId)
              void applyFileTree(pid)
            } else {
              logEvent("warn", "[lifecycle] folder API creation failed", result.message)
            }
          })
          .catch((err) => logEvent("warn", "[lifecycle] folder API creation error", String(err)))
      }
    }

    return true
  }, [logEvent, applyFileTree, readFilesystemVersion])

  const createFileAtPath = useCallback((path: string, content: string, isAsset: boolean, onInserted?: () => void): boolean => {
    const normalized = normalizePath(path)
    if (!normalized || latestStateRef.current.files[normalized] || pendingAuthFileCreatesRef.current.has(normalized)) return false

    // Remove from deleted paths ref since it is explicitly created now
    deletedPathsRef.current.delete(normalized)

    const publishLocalFile = () => {
      const newFile: EditorFileRecord = { path: normalized, content, isAsset, localVersion: 1, pendingSync: false }
      dispatch({
        type: "UPSERT_FILE",
        file: newFile,
      })
      // Update the ref immediately so any subsequent openFile() call sees the new
      // file before React re-renders and can update latestStateRef.current.
      // Without this, openFile() treats the file as missing and overwrites the
      // content with an empty placeholder (e.g. "% filename.tex\n").
      latestStateRef.current = {
        ...latestStateRef.current,
        files: { ...latestStateRef.current.files, [normalized]: newFile },
      }
      // Drop the in-flight guard only after the file is visible and the UUID
      // is mapped, so onInserted (open / inline rename) cannot run early.
      pendingAuthFileCreatesRef.current.delete(normalized)
      logEvent("success", "File created", normalized)
      onInserted?.()
    }

    // Anonymous sessions and assets do not wait on POST /api/files.
    // Keep that path immediate, including the ID-less local file.
    if (!isAuthUserRef.current || isAsset) {
      publishLocalFile()
      // Auth creates get a stable file_id from POST /api/files. updateYFile before
      // that returns cannot resolve folderIdMapRef, so the canonical Y.Text is
      // written in the API callback below. Anonymous sessions have no backend id.
      if (!isAsset && !isAuthUserRef.current) {
        updateYFile(normalized, content)
      }
      if ((!isAuthUserRef.current || latestStateRef.current.sessionId) && latestStateRef.current.wsStatus === "connected") {
        sendWs({ type: "file_create", data: { path: normalized, content, is_asset: isAsset }, timestamp: nowIso() })
      }
      // Anonymous sessions have no backend file_id — append the create op now.
      // Auth sessions append it after the API returns the DB file_id below.
      if (!isAuthUserRef.current) {
        appendStructureOp({ type: isAsset ? "asset" : "create", path: normalized })
      }
      return true
    }

    // Auth text files stay out of state.files until the backend id is mapped.
    // openFile refuses a path that is in neither the files map nor folderIdMapRef,
    // so the editor cannot bind this path while the POST is still in flight.
    const pid = projectIdRef.current
    if (!pid) {
      logEvent("warn", "[lifecycle] file API creation skipped", "missing project id")
      return false
    }

    const fileName = basename(normalized)
    const ext = fileName.includes(".") ? fileName.split(".").pop() ?? "" : ""
    // Send name WITHOUT extension — backend stores them separately and
    // buildProjectTree re-appends the extension when rendering the tree.
    const nameWithoutExt = ext ? fileName.slice(0, -(ext.length + 1)) : fileName
    const parentNormalized = parentPath(normalized)
    const parentId = parentNormalized ? (folderIdMapRef.current.get(parentNormalized) ?? null) : null
    const sizeBytes = new TextEncoder().encode(content).length

    pendingAuthFileCreatesRef.current.add(normalized)

    void bffFetch("/api/files", {
      method: "POST",
      body: JSON.stringify({
        extension: ext,
        mime_type: getMimeType(ext),
        name: nameWithoutExt,
        parent_id: parentId,
        project_id: pid,
        size: sizeBytes,
      }),
    })
      .then((r) => r.json())
      .then((result) => {
        if (result.success) {
          // Capture the backend file ID so deletePath can look it up.
          // Keep the path pending until the file is actually published, so
          // openFile cannot seed an editable stub while this callback runs.
          const returnedId =
            typeof result.data === "string"
              ? result.data
              : typeof result.data === "object" && result.data !== null
                ? String((result.data as Record<string, unknown>).id ?? (result.data as Record<string, unknown>).file_id ?? "")
                : ""
          if (returnedId) {
            folderIdMapRef.current.set(normalized, returnedId)
            if (content.trim() && !isPlaceholderFileContent(normalized, content)) {
              updateYFile(normalized, content)
            }
            if (!yFilesMapRef.current?.has(returnedId)) {
              ensureContentYText(returnedId, "local")
            }
          }
          // Structure op travels inside the CRDT doc: ordered + carries the
          // DB file_id so the compiler can bind the path for S3 pushes.
          // One create op, after the id is known. The file enters the tree
          // only once that id is in folderIdMapRef.
          appendStructureOp({ type: "create", path: normalized, file_id: returnedId || undefined })
          if (returnedId) {
            publishLocalFile()
          } else {
            pendingAuthFileCreatesRef.current.delete(normalized)
            logEvent("warn", "[lifecycle] file API creation returned no id", normalized)
          }
          void applyFileTree(pid)
        } else {
          pendingAuthFileCreatesRef.current.delete(normalized)
          logEvent("warn", "[lifecycle] file API creation failed", result.message)
        }
      })
      .catch((err) => {
        pendingAuthFileCreatesRef.current.delete(normalized)
        logEvent("warn", "[lifecycle] file API creation error", String(err))
      })

    return true
  }, [logEvent, sendWs, updateYFile, appendStructureOp, applyFileTree, ensureContentYText])

  const deletePath = useCallback((path: string) => {
    const normalized = normalizePath(path)
    if (!normalized) return

    // Register in deletedPathsRef so we ignore any resurrected updates for this file/folder
    deletedPathsRef.current.add(normalized)

    const current = latestStateRef.current
    const sessionId = current.sessionId

    // Find all files in the current local files state that match or start with the normalized path
    const childrenKeys = Object.keys(current.files).filter(
      (p) => p === normalized || p.startsWith(`${normalized}/`)
    )

    console.log("[editor-store] deletePath called for:", path, "normalized:", normalized, "childrenKeys:", childrenKeys)

    dispatch({ type: "DELETE_PATH", path: normalized })

    // Update the ref immediately so auto-compile/other callbacks cannot observe
    // the deleted path before React re-renders.
    const nextFiles = { ...current.files }
    childrenKeys.forEach((key) => {
      delete nextFiles[key]
    })
    latestStateRef.current = { ...current, files: nextFiles }

    // Propagate the deletion to the compiler session regardless of auth mode:
    // Redis/CRDT state must reflect the delete even when the DB delete is
    // handled separately via the project API below.
    if (sessionId) {
      childrenKeys.forEach((key) => {
        const id = folderIdMapRef.current.get(key)
        if (id) {
          retiredFileIdsRef.current.add(id)
          fileNodeByIdRef.current.delete(id)
        }
        deleteYFile(key)
        appendStructureOp({ type: "delete", path: key, file_id: id })
        if (latestStateRef.current.wsStatus === "connected") {
          sendWs({ type: "file_delete", data: { path: key }, timestamp: nowIso() })
        }
      })
    }

    if (isAuthUserRef.current) {
      // Auth users: call the backend delete API, then refresh the tree.
      // Remove the deleted path and its children from folderIdMapRef.
      const pid = projectIdRef.current
      const nodeId = folderIdMapRef.current.get(normalized) ?? null
      if (nodeId) {
        retiredFileIdsRef.current.add(nodeId)
        fileNodeByIdRef.current.delete(nodeId)
      }
      folderIdMapRef.current.delete(normalized)
      folderIdMapRef.current.forEach((id, key) => {
        if (key.startsWith(`${normalized}/`)) {
          retiredFileIdsRef.current.add(id)
          fileNodeByIdRef.current.delete(id)
          folderIdMapRef.current.delete(key)
        }
      })

      if (pid && nodeId) {
        void readFilesystemVersion(pid)
          .then((version) => {
            const expectedVersion = version ?? latestStateRef.current.filesystemVersion ?? 0
            const params = new URLSearchParams({
              operation_id: newFilesystemOperationId(),
              expected_version: String(expectedVersion),
            })
            return bffFetch(`/api/files/${encodeURIComponent(nodeId)}?${params.toString()}`, {
              method: "DELETE",
            })
          })
          .then((r) => r.json())
          .then((result) => {
            if (result.success) {
              void applyFileTree(pid)
            } else {
              logEvent("warn", "[lifecycle] file delete API failed", result.message)
            }
          })
          .catch((err) => logEvent("warn", "[lifecycle] file delete API error", String(err)))
      } else {
        // ID unknown — refresh tree anyway so local state stays in sync
        if (pid) void applyFileTree(pid)
      }
    }

    if (pathEventHandlerRef.current) {
      pathEventHandlerRef.current({ type: "delete", path: normalized })
    }

    logEvent("warn", "Deleted path", normalized)

    // Non-auth session sync — call session get API after delete
    if (!isAuthUserRef.current && sessionId) {
      void fetch(`/api/compiler/sessions/${encodeURIComponent(sessionId)}`, {
        headers: { "Cache-Control": "no-cache" },
      }).then(async (resp) => {
        if (resp.ok) {
          const data = await resp.json()

          const dbKeys = new Set(
            Object.keys(data.files || {}).map((p) => normalizePath(p)).filter(Boolean)
          )

          // 1. Cleanup Y.js and local state files that are not in the database response
          const filesMap = yFilesMapRef.current
          if (filesMap) {
            filesMap.forEach((ytext, path) => {
              // Ignore assets since they are not tracked in Yjs map
              const isAsset = latestStateRef.current.files[path]?.isAsset
              if (!isAsset && !dbKeys.has(path)) {
                console.log("[editor-store] cleanup: deleting resurrected file from Yjs/local:", path)
                deleteYFile(path)
                dispatch({ type: "DELETE_PATH", path })
              }
            })
          }

          // 2. Cleanup virtual folders that no longer contain any files in dbKeys
          const currentFolders = latestStateRef.current.virtualFolders
          currentFolders.forEach((folder) => {
            const hasReferencedFiles = Array.from(dbKeys).some(
              (file) => file === folder || file.startsWith(`${folder}/`)
            )
            if (!hasReferencedFiles) {
              console.log("[editor-store] cleanup: deleting empty/stale folder:", folder)
              dispatch({ type: "DELETE_PATH", path: folder })
            }
          })

          logEvent("info", "Session status synced after file deletion", normalized)
        }
      }).catch((err) => {
        logEvent("error", "Failed to call session get API after delete", String(err))
      })
    }
  }, [logEvent, sendWs, deleteYFile, appendStructureOp, applyFileTree, readFilesystemVersion])

  const renamePath = useCallback((sourcePath: string, targetPath: string): boolean => {
    const source = normalizePath(sourcePath)
    const target = normalizePath(targetPath)
    if (!source || !target || source === target) return false
    if (latestStateRef.current.files[target] || latestStateRef.current.virtualFolders.includes(target)) return false

    // Clear target and any target subpaths from deletedPathsRef since they are now active
    deletedPathsRef.current.delete(target)
    Array.from(deletedPathsRef.current).forEach((p) => {
      if (p.startsWith(`${target}/`)) {
        deletedPathsRef.current.delete(p)
      }
    })

    // Tombstone the source and its current descendants. A later file under the
    // old prefix (chapters/new.tex after chapters/ → sections/) is a different path.
    tombstoneMovedAwayPaths(source)

    // Identify all files affected by this rename (individual file or folder contents)
    const filesToRename = Object.entries(latestStateRef.current.files).filter(
      ([path]) => path === source || path.startsWith(`${source}/`)
    )

    dispatch({ type: "RENAME_PATH", source, target })

    if (isAuthUserRef.current) {
      // Auth users: call PATCH /files/{id} for rename or move, then refresh tree.
      const pid = projectIdRef.current
      const nodeId = folderIdMapRef.current.get(source) ?? null

      // Update folderIdMapRef to reflect the new path immediately.
      if (nodeId) {
        folderIdMapRef.current.delete(source)
        folderIdMapRef.current.set(target, nodeId)
        // Also remap any children (folder move).
        folderIdMapRef.current.forEach((id, key) => {
          if (key.startsWith(`${source}/`)) {
            folderIdMapRef.current.delete(key)
            folderIdMapRef.current.set(`${target}${key.slice(source.length)}`, id)
          }
        })
        rebuildFileNodeIndex()
      }

      if (pid && nodeId) {
        const newBase = basename(target)
        const newParentPath = parentPath(target)
        const newParentId = newParentPath
          ? (folderIdMapRef.current.get(newParentPath) ?? null)
          : null

        // A cut/paste or drag-and-drop move (basename unchanged, only the parent
        // differs) goes through the dedicated move endpoint; an actual rename
        // (basename changed, parent may also change) still uses PATCH below.
        const isPureMove = basename(source) === newBase

        if (isPureMove) {
          void readFilesystemVersion(pid)
            .then((version) => {
              const expectedVersion = version ?? latestStateRef.current.filesystemVersion ?? 0
              return bffFetch(`/api/files/${nodeId}/move`, {
                method: "PUT",
                body: JSON.stringify({
                  // operation_id: newFilesystemOperationId(),
                  // expected_version: expectedVersion,
                  target_project_id: pid,
                  parent_id: newParentId,
                }),
              })
            })
            .then((r) => r.json())
            .then((result) => {
              if (result.success) {
                void applyFileTree(pid)
              } else {
                logEvent("warn", "[lifecycle] move API failed", result.message)
              }
            })
            .catch((err) => logEvent("warn", "[lifecycle] move API error", String(err)))
        } else {
          // The backend stores name and extension separately, so a file rename must
          // split "sukesh.txt" into { name: "sukesh", extension: "txt" } — sending the
          // dotted string as `name` gets sanitized to "sukeshtxt". Folders have no
          // extension and send only `name`. Mirrors the split in createFileAtPath.
          const isFileRename = filesToRename.some(([p]) => p === source)
          const dot = isFileRename ? newBase.lastIndexOf(".") : -1
          const newExt = dot > 0 ? newBase.slice(dot + 1) : ""
          const newName = dot > 0 ? newBase.slice(0, dot) : newBase

          void readFilesystemVersion(pid)
            .then((version) => {
              const operationId = newFilesystemOperationId()
              const expectedVersion = version ?? latestStateRef.current.filesystemVersion ?? 0
              return bffFetch(`/api/files/${nodeId}`, {
                method: "PATCH",
                body: JSON.stringify(
                  isFileRename
                    ? {
                        // operation_id: operationId,
                        // expected_version: expectedVersion,
                        name: newName,
                        extension: newExt,
                        mime_type: getMimeType(newExt),
                        parent_id: newParentId,
                      }
                    : {
                        // operation_id: operationId,
                        // expected_version: expectedVersion,
                        name: newName,
                        parent_id: newParentId,
                      },
                ),
              })
            })
            .then((r) => r.json())
            .then((result) => {
              if (result.success) {
                void applyFileTree(pid)
              } else {
                logEvent("warn", "[lifecycle] rename/move API failed", result.message)
              }
            })
            .catch((err) => logEvent("warn", "[lifecycle] rename/move API error", String(err)))
        }
      } else if (pid) {
        // ID unknown — still refresh tree so local state stays in sync.
        void applyFileTree(pid)
      }
    }

    // Propagate the rename to the compiler session in ANY mode that has one:
    // the files-map key move and the ops-journal entry go into a single Yjs
    // transaction so peers + compiler observe one atomic rename, and the
    // file_rename WS message is the immediate fallback channel.
    if (latestStateRef.current.sessionId) {
      const doc = yDocRef.current
      const filesMap = yFilesMapRef.current
      const ops = yOpsRef.current
      const wsConnected = latestStateRef.current.wsStatus === "connected"
      const crdtConnected = crdtSyncRef.current?.status === "connected"
      if (!doc || !filesMap) {
        console.warn("[CRDT:Rename] files-map move SKIPPED — Yjs doc/map missing; compiler will rely on ops + WS only", {
          source,
          target,
          hasDoc: Boolean(doc),
          hasFilesMap: Boolean(filesMap),
        })
      }
      if (!wsConnected) {
        console.warn("[CRDT:Rename] file_rename WS will be SKIPPED — compiler WS not connected; ops journal is the only channel", {
          source,
          target,
          wsStatus: latestStateRef.current.wsStatus,
        })
      }
      if (!crdtConnected) {
        console.warn("[CRDT:Rename] CRDT socket not connected — rename update will queue behind resync", {
          source,
          target,
          crdtStatus: crdtSyncRef.current?.status,
        })
      }
      filesToRename.forEach(([oldPath, record]) => {
        const suffix = oldPath === source ? "" : oldPath.slice(source.length)
        const newPath = `${target}${suffix}`
        const fileID = folderIdMapRef.current.get(newPath) ?? folderIdMapRef.current.get(oldPath)

        let viaYjsTxn = false
        let viaOpOnly = false

        if (doc && filesMap && !record.isAsset) {
          // Rename is metadata-only: the stable file_id keeps the same Y.Text in
          // the content map. Do NOT create a new Y.Text or delete the old one.
          doc.transact(() => {
            ops?.push([{
              op_id:
                typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                  ? crypto.randomUUID()
                  : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
              type: "rename",
              path: oldPath,
              to: newPath,
              file_id: fileID ?? "",
              user_name: readAuthUserLabel() ?? "",
              ts: Date.now(),
            }])
          }, { kind: "local", fileID: fileID ?? "" } satisfies LocalCrdtOrigin)
          viaYjsTxn = true
        } else if (doc && ops) {
          appendStructureOp({ type: "rename", path: oldPath, to: newPath, file_id: fileID })
          viaOpOnly = true
        }

        const renameWsSent = latestStateRef.current.wsStatus === "connected"
          ? sendWs({ type: "file_rename", data: { path: oldPath, to: newPath, file_id: fileID ?? "" }, timestamp: nowIso() })
          : false

        console.log("[CRDT:Rename] propagation channels", {
          oldPath,
          newPath,
          fileID: fileID ?? null,
          viaYjsTxn,
          viaOpOnly,
          fileRenameWsSent: renameWsSent,
          crdtStatus: crdtSyncRef.current?.status,
        })
      })
    } else {
      console.warn("[CRDT:Rename] NO compiler session — rename stays local/DB only; next session create will seed from backend", {
        source,
        target,
      })
    }

    // Keep project.rudra.config.json's mainTexPath in sync — the compiler
    // resolves the main file from this config on EVERY compile, so a stale
    // path points at the deleted file and Tectonic dies on a missing/empty main.
    const configPath = Object.keys(latestStateRef.current.files).find(
      (p) => basename(p) === "project.rudra.config.json",
    )
    if (configPath) {
      const cfgText = latestStateRef.current.files[configPath]?.content ?? ""
      try {
        const cfg = JSON.parse(cfgText)
        const configured = normalizePath(String(cfg?.project?.mainTexPath ?? ""))
        if (configured && (configured === source || configured.startsWith(`${source}/`))) {
          cfg.project.mainTexPath = `${target}${configured.slice(source.length)}`
          updateContent(configPath, JSON.stringify(cfg, null, 2))
          console.log("[CRDT:Rename] updated project config mainTexPath", {
            from: configured,
            to: cfg.project.mainTexPath,
          })
        }
      } catch {
        // Non-JSON or malformed config — leave it untouched.
      }
    }

    if (pathEventHandlerRef.current) {
      pathEventHandlerRef.current({ type: "rename", source, target })
    }

    logEvent("info", "Renamed/moved path", `${source} → ${target}`)
    return true
  }, [logEvent, sendWs, appendStructureOp, applyFileTree, updateContent, rebuildFileNodeIndex, tombstoneMovedAwayPaths, readFilesystemVersion])

  /** Clipboard cut. A file already in this tree uses renamePath (versioned PUT + CRDT rename).
   *  An id that is not in the tree still gets the same PUT body, without a local rename op. */
  const moveClipboardFiles = useCallback(async (fileIds: string[], targetFolderPath: string | null): Promise<boolean> => {
    const pid = projectIdRef.current
    if (!pid || !isAuthUserRef.current) return false
    const parent = targetFolderPath ? normalizePath(targetFolderPath) : ""
    const parentId = parent ? (folderIdMapRef.current.get(parent) ?? null) : null
    if (parent && !parentId) return false

    let allSuccess = true
    for (const id of fileIds) {
      const source = findPathByFileId(id)
      if (source) {
        const target = normalizePath(parent ? `${parent}/${basename(source)}` : basename(source))
        if (source !== target && !renamePath(source, target)) allSuccess = false
        continue
      }
      try {
        const version = await readFilesystemVersion(pid)
        const expectedVersion = version ?? latestStateRef.current.filesystemVersion ?? 0
        const res = await bffFetch(`/api/files/${encodeURIComponent(id)}/move`, {
          method: "PUT",
          body: JSON.stringify({
            // operation_id: newFilesystemOperationId(),
            // expected_version: expectedVersion,
            target_project_id: pid,
            parent_id: parentId,
          }),
        })
        const result = await res.json()
        if (!result?.success) allSuccess = false
        else await applyFileTree(pid)
      } catch {
        allSuccess = false
      }
    }
    return allSuccess
  }, [findPathByFileId, readFilesystemVersion, renamePath, applyFileTree])

  const uploadAsset = useCallback(async (file: File, folderPath?: string) => {
    const folder = normalizePath(folderPath || "")
    const path = normalizePath(folder ? `${folder}/${file.name}` : file.name)
    if (!path) {
      logEvent("error", "Asset upload blocked", "Invalid upload path")
      return
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result || ""))
      reader.onerror = () => reject(new Error("Failed to read asset file"))
      reader.readAsDataURL(file)
    })
    const ok = createFileAtPath(path, dataUrl, true)
    if (!ok) logEvent("warn", "Asset upload skipped", "Path already exists")
  }, [createFileAtPath, logEvent])

  const resolveAssetCandidates = useCallback((assetPath: string) => {
    const cfg = configRef.current
    if (!cfg) return []
    return buildAssetUrlCandidates({
      apiBase: "",
      path: assetPath,
      templateId: latestStateRef.current.templateId,
      sessionId: latestStateRef.current.sessionId,
    })
  }, [])

  /** Backend id for a folder already known to the tree (its store path), or undefined for the
   *  project root / an unknown path. Lets a caller outside this module (e.g. FileExplorerFrame's
   *  OS-file-drop-to-upload handler) resolve a drop target folder to the parent_id an upload
   *  session needs, the same lookup renamePath/deletePath already do internally. */
  const getFolderId = useCallback((path: string): string | undefined => {
    const normalized = normalizePath(path)
    if (!normalized) return undefined
    return folderIdMapRef.current.get(normalized)
  }, [])

  /** Register a file that was created on the backend outside this module's own create flow
   *  (upload-session drop, clipboard copy/move) with the live compiler session.
   *  Text files: FILE_CREATED already bound the id. Load preview into content[file_id]
   *  and append one create op. Do not send path-only file_create.
   *  Assets: a `file_create` WS sets the `file_assets` flag and an `asset` op binds the
   *  file_id — the compiler resolves bytes from the workspace URL at compile time. */
  const registerExternalFile = useCallback(async (path: string) => {
    const normalized = normalizePath(path)
    if (!normalized || !isAuthUserRef.current) return
    const fileID = folderIdMapRef.current.get(normalized)
    const isAsset = latestStateRef.current.files[normalized]?.isAsset ?? isAssetByExtension(normalized)
    if (isAsset) {
      if (latestStateRef.current.wsStatus === "connected") {
        sendWs({ type: "file_create", data: { path: normalized, content: "", is_asset: true }, timestamp: nowIso() })
      }
      appendStructureOp({ type: "asset", path: normalized, file_id: fileID })
      return
    }
    await loadAuthFilePreview(normalized)
    appendStructureOp({ type: "create", path: normalized, file_id: fileID })
  }, [sendWs, appendStructureOp, loadAuthFilePreview])

  /** Re-fetch the file tree from the backend — for changes made outside this module's own
   *  create/rename/delete/move calls (e.g. a real file upload completing via the upload-session
   *  API, which registers the file server-side without ever calling POST /api/files). Any files
   *  newly revealed by the refresh are registered with the compiler session. */
  const refreshFileTree = useCallback(async () => {
    const pid = projectIdRef.current
    if (!pid) return
    const addedPaths = await applyFileTree(pid)
    for (const path of addedPaths) {
      await registerExternalFile(path)
    }
  }, [applyFileTree, registerExternalFile])

  const getYjsBinding = useCallback(() => {
    const doc = yDocRef.current
    const sync = crdtSyncRef.current
    const filesMap = yFilesMapRef.current
    if (!doc || !sync || !filesMap) return null
    return {
      doc,
      awareness: sync.awareness,
      filesMap,
      getFileId: (path: string) => folderIdMapRef.current.get(path) ?? path,
      getYText: (path: string) => {
        const normalized = normalizePath(path)
        if (!normalized || isPathDeleted(normalized)) return null
        const fileID = folderIdMapRef.current.get(normalized) ?? normalized
        if (retiredFileIdsRef.current.has(fileID)) return null
        return filesMap.get(fileID) ?? null
      },
    }
  }, [isPathDeleted])

  const setActiveFile = useCallback((fileIdOrPath: string) => {
    const path = resolvePathFromFileId(fileIdOrPath) || normalizePath(fileIdOrPath)
    if (!path) return
    dispatch({ type: "SET_ACTIVE_FILE", path })
    crdtSyncRef.current?.setActiveFilePath(path)
    if (isAuthUserRef.current) {
      crdtSyncRef.current?.setLocalAwarenessActiveFile(path)
    }
  }, [])

  const contextValue = useMemo<EditorStoreContextValue>(() => ({
    state,
    projectName: projectNameRef.current,
    projectId: projectIdRef.current,
    getContent,
    registerFile,
    updateContent,
    openFile,
    openFileAtLine,
    registerOpenFileHandler,
    registerRevealLineHandler,
    registerSynctexPdfHandler,
    applySynctexForwardResult,
    registerFramePostHandler,
    postToFlutterFrame,
    registerInverseSyncHandler,
    applyInverseSyncResult,
    setPdfThumbnail,
    registerActiveMonacoEditor,
    getActiveMonacoEditor,
    runOnActiveEditor,
    registerEditorHistoryHandlers,
    undoActiveEditor,
    redoActiveEditor,
    loadTemplate,
    createSession,
    retryCompilerSession,
    manualCompile,
    setAutoCompile,
    createFolder,
    createFileAtPath,
    deletePath,
    renamePath,
    moveClipboardFiles,
    uploadAsset,
    resolveAssetCandidates,
    getFolderId,
    refreshFileTree,
    getYjsBinding,
    setActiveFile,
    registerPathEventHandler,
  }), [
    state,
    getContent,
    registerFile,
    updateContent,
    openFile,
    openFileAtLine,
    registerOpenFileHandler,
    registerRevealLineHandler,
    registerSynctexPdfHandler,
    applySynctexForwardResult,
    registerFramePostHandler,
    postToFlutterFrame,
    registerInverseSyncHandler,
    applyInverseSyncResult,
    setPdfThumbnail,
    registerActiveMonacoEditor,
    getActiveMonacoEditor,
    runOnActiveEditor,
    registerEditorHistoryHandlers,
    undoActiveEditor,
    redoActiveEditor,
    loadTemplate,
    createSession,
    retryCompilerSession,
    manualCompile,
    setAutoCompile,
    createFolder,
    createFileAtPath,
    deletePath,
    renamePath,
    moveClipboardFiles,
    uploadAsset,
    registerPathEventHandler,
    resolveAssetCandidates,
    getFolderId,
    refreshFileTree,
    getYjsBinding,
    setActiveFile,
  ])

  return <EditorStoreContext.Provider value={contextValue}>{children}</EditorStoreContext.Provider>
}

export function useEditorStore() {
  const ctx = useContext(EditorStoreContext)
  if (!ctx) {
    throw new Error("useEditorStore must be used inside <EditorStoreProvider>")
  }
  return ctx
}

export function pathParent(path: string): string {
  return parentPath(path)
}

/** Resolve flexlayout fileId / legacy numeric id to a normalized project path. */
export function resolveEditorFilePath(fileId: string): string {
  const normalized = normalizePath(fileId)
  if (normalized) return normalized
  return normalizePath(LEGACY_FILE_ID_MAP[fileId] || fileId)
}
